
import React, { useState, useEffect, useMemo, useCallback, lazy, Suspense, useRef } from 'react';
import { 
  Package, Truck, LayoutDashboard, History, Store, Plus, Minus, Search, AlertTriangle, 
  Calendar as CalendarIcon, ChevronRight, Trash2, Edit2, CheckCircle, XCircle, ArrowUpRight, 
  ArrowDownLeft, LogOut, Database, Briefcase, Folder, ArrowLeft, MoreVertical, Settings, MapPin, 
  Save, Users, Shield, Clock, FileText, Upload, Image as ImageIcon, Loader2, Info, Flame, X, 
  Filter, LayoutGrid, List, Ban, Tag, User, Lock, Layers, MoreHorizontal, CheckSquare, Square, 
  Sparkles, Lightbulb, ShoppingBag, DollarSign, Factory, ExternalLink, Printer, FileSpreadsheet, 
  Menu, Wrench, Coffee, Utensils, Hammer, Globe, Anchor, Zap, Crown, Camera, ClipboardCheck, 
  Mail, Eye, EyeOff, GripVertical, Home, Download, List as ListIcon, Calendar, ChevronLeft, AlertCircle,
  PackageCheck, TrendingDown, TrendingUp, Equal, Play, QrCode, Barcode
} from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import BarcodeComponent from 'react-barcode';
import { auth, db, setAuthToken } from './api';
import api from './api';
import UserManagementModal from './UserManagementModal';
import LoginScreen from './components/auth/LoginScreen';
import ProfileModal from './components/auth/ProfileModal';
import ProfileDropdown from './components/shared/ProfileDropdown';
import SettingsModule from './components/settings/SettingsModule';
import SearchableDropdown from './components/shared/SearchableDropdown';
import AIInsightsModal from './components/shared/AIInsightsModal';
import { useLanguage } from './LanguageContext';
import { getTranslation } from './translations';
import { formatNumber, formatCurrency, formatNumberWithUnit } from './utils/formatNumber';
import { useDebounce } from './hooks/useDebounce';
import * as XLSX from 'xlsx';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';

const INITIAL_CATEGORIES = ["Cleaning Tools", "Chemicals", "Disposables", "Equipment", "PPE", "Ingredients", "Packaging", "Crockery", "Others"];

const Footer = () => (
    <footer className="text-center py-6 text-sm text-slate-400">
        © POMEN {new Date().getFullYear()}
    </footer>
);

// --- Components ---

const Card = ({ children, className = "", noPadding = false }) => (
  <div className={`bg-white rounded-2xl sm:rounded-3xl border border-slate-100 shadow-[0_2px_20px_-4px_rgba(0,0,0,0.05)] hover:shadow-[0_8px_30px_-4px_rgba(0,0,0,0.08)] transition-all duration-300 ${className}`}>
    {noPadding ? children : <div className="p-4 sm:p-6 md:p-8">{children}</div>}
  </div>
);

const Badge = ({ children, color = "slate", className = "" }) => {
  const styles = {
    slate: "bg-slate-100 text-slate-600 border-slate-200",
    green: "bg-emerald-50 text-emerald-700 border-emerald-100 ring-1 ring-emerald-500/20",
    red: "bg-rose-50 text-rose-700 border-rose-100 ring-1 ring-rose-500/20",
    blue: "bg-blue-50 text-blue-700 border-blue-100 ring-1 ring-blue-500/20",
    amber: "bg-amber-50 text-amber-700 border-amber-100 ring-1 ring-amber-500/20",
    purple: "bg-purple-50 text-purple-700 border-purple-100 ring-1 ring-purple-500/20",
    orange: "bg-orange-50 text-orange-700 border-orange-100 ring-1 ring-orange-500/20",
  };
  return (
    <span className={`px-3 py-1 rounded-full text-[11px] font-semibold border ${styles[color] || styles.slate} ${className}`}>
      {children}
    </span>
  );
};

const Button = ({ children, onClick, variant = "primary", className = "", disabled = false, icon: Icon, fullWidth = false, ...props }) => {
  const variants = {
    primary: "bg-[#10056E] hover:bg-[#0d0459] text-white shadow-lg shadow-[#10056E]/30",
    secondary: "bg-white hover:bg-slate-50 text-slate-700 border border-slate-200 shadow-sm",
    danger: "bg-rose-500 hover:bg-rose-600 text-white shadow-lg shadow-rose-500/30",
    ghost: "bg-transparent hover:bg-slate-100 text-slate-600",
  };
  
  return (
    <button 
      onClick={onClick} 
      disabled={disabled}
      className={`
        inline-flex items-center justify-center gap-2 px-5 py-3 rounded-2xl text-sm font-semibold transition-all duration-200 active:scale-[0.98] touch-manipulation
        disabled:opacity-60 disabled:cursor-not-allowed disabled:active:scale-100
        ${variants[variant]} 
        ${fullWidth ? 'w-full' : ''}
        ${className}
      `}
      {...props}
    >
      {Icon && <Icon size={18} className={disabled ? 'animate-spin' : ''} />}
      {children}
    </button>
  );
};

const Input = ({ label, className, ...props }) => (
  <div className="flex flex-col gap-2 w-full">
    {label && (
      <label className="text-xs font-bold uppercase tracking-wider text-slate-500 ml-1">
        {label} {props.required && <span className="text-rose-500">*</span>}
      </label>
    )}
    <div className="relative">
      <input 
        className={`
          flex h-12 w-full rounded-2xl border border-transparent bg-slate-100 px-4 py-3 text-sm text-slate-800 placeholder:text-slate-400
          focus:outline-none focus:bg-white focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/10 transition-all duration-200
          disabled:cursor-not-allowed disabled:opacity-50
          ${className}
        `}
        {...props}
      />
    </div>
  </div>
);

const Select = ({ label, children, ...props }) => (
  <div className="flex flex-col gap-2 w-full">
    {label && (
      <label className="text-xs font-bold uppercase tracking-wider text-slate-500 ml-1">
        {label} {props.required && <span className="text-rose-500">*</span>}
      </label>
    )}
    <div className="relative">
      <select 
        className="flex h-12 w-full appearance-none rounded-2xl border border-transparent bg-slate-100 px-4 py-3 text-sm text-slate-800 focus:outline-none focus:bg-white focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/10 transition-all duration-200 pr-10"
        {...props}
      >
        {children}
      </select>
      <ChevronRight className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 rotate-90 pointer-events-none" size={16} />
    </div>
  </div>
);

const Modal = ({ isOpen, onClose, title, children, maxWidth = "sm:max-w-lg" }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center sm:p-4">
      <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm transition-opacity animate-in fade-in duration-300" onClick={onClose} />
      <div className={`bg-white w-full ${maxWidth} max-h-[92vh] sm:max-h-[90vh] rounded-t-[1.5rem] sm:rounded-[2rem] shadow-2xl overflow-hidden flex flex-col relative z-10 animate-in slide-in-from-bottom-4 sm:zoom-in-95 duration-300`}>
        <div className="flex items-center justify-between p-4 sm:p-6 border-b border-slate-100 bg-white/80 backdrop-blur-md z-20 shrink-0">
          <h3 className="text-lg sm:text-xl font-bold text-slate-800 tracking-tight">{title}</h3>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full bg-slate-100 text-slate-500 hover:bg-slate-200 hover:text-slate-800 transition-colors"><X size={18} /></button>
        </div>
        <div className="p-4 sm:p-6 overflow-y-auto custom-scrollbar">{children}</div>
      </div>
    </div>
  );
};

const Toast = ({ message, type, onClose }) => (
    <div className={`fixed bottom-20 md:bottom-6 left-4 right-4 md:left-auto md:right-6 md:w-auto px-4 sm:px-6 py-3 sm:py-4 rounded-xl shadow-2xl flex items-center gap-3 sm:gap-4 z-[110] ${type === 'error' ? 'bg-white text-rose-600 border border-rose-100' : 'bg-[#006838] text-white'}`}>
        <span className="text-sm sm:text-base">{message}</span>
        <button onClick={onClose}><X size={16}/></button>
    </div>
);

const ConfirmationModal = ({ isOpen, onClose, title, message, onConfirm }) => {
    const [isLoading, setIsLoading] = React.useState(false);
    const lang = localStorage.getItem('enhive-language') || 'en';
    
    const handleConfirm = async () => {
        setIsLoading(true);
        try {
            await onConfirm();
        } finally {
            setIsLoading(false);
        }
    };
    
    return (
        <Modal isOpen={isOpen} onClose={isLoading ? undefined : onClose} title={title}>
            <div className="space-y-4">
                <p>{message}</p>
                <div className="flex justify-end gap-2">
                    <Button variant="secondary" onClick={onClose} disabled={isLoading}>
                        {lang === 'en' ? 'Cancel' : 'Batal'}
                    </Button>
                    <Button onClick={handleConfirm} disabled={isLoading} icon={isLoading ? Loader2 : undefined}>
                        {isLoading ? (lang === 'en' ? 'Processing...' : 'Memproses...') : (lang === 'en' ? 'Confirm' : 'Sahkan')}
                    </Button>
                </div>
            </div>
        </Modal>
    );
};

// --- MAIN APP ---

// Helper function to get icon component by name
const getIconComponent = (iconName) => {
    const iconMap = {
        'Package': Package,
        'Store': Store,
        'ShoppingBag': ShoppingBag,
        'Briefcase': Briefcase,
        'Factory': Factory,
        'Coffee': Coffee,
        'Utensils': Utensils,
        'Wrench': Wrench,
        'Hammer': Hammer,
        'Globe': Globe,
        'Truck': Truck,
        'Home': Home,
        'Anchor': Anchor,
        'Zap': Zap,
        'Crown': Crown,
        'Camera': Camera
    };
    return iconMap[iconName] || Package; // Default to Package if not found
};

// Export PO to Excel
const exportPOToExcel = async (po, project, suppliers = [], t = (k) => k, language = 'en') => {
    const poNumber = `PO-${po.id.slice(-6).toUpperCase()}`;
    const poDate = new Date(po.date).toLocaleDateString('en-MY', { day: '2-digit', month: 'short', year: 'numeric' });
    const generatedAt = new Date().toLocaleString('en-MY', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });

    // Supplier details
    const sup = suppliers.find(s => s.name === po.supplier) || {};
    const supAddress = [sup.addressLine1, sup.addressLine2, [sup.postcode, sup.state].filter(Boolean).join(' ')].filter(Boolean).join(', ');
    const companyAddress = [project.companyAddressLine1, project.companyAddressLine2, [project.companyPostcode, project.companyState].filter(Boolean).join(' ')].filter(Boolean).join(', ') || project.companyAddress || '';
    const shipAddress = [project.warehouseAddressLine1, project.warehouseAddressLine2, [project.warehousePostcode, project.warehouseState].filter(Boolean).join(' ')].filter(Boolean).join(', ') || project.warehouseAddress || '';

    const wb = new ExcelJS.Workbook();
    wb.creator = 'ENHIVE by POMEN';
    const ws = wb.addWorksheet('Purchase Order', { properties: { defaultColWidth: 14 } });

    // -- Colors --
    const navy = '0F172A';
    const brandBlue = '10056E';
    const brandBlueDark = '0D0459';
    const slate50 = 'F8FAFC';
    const slate100 = 'F1F5F9';
    const slate200 = 'E2E8F0';
    const slate500 = '64748B';
    const white = 'FFFFFF';

    // -- Column widths (A-G) --
    ws.columns = [
        { width: 6 },   // A - #
        { width: 36 },  // B - Item
        { width: 14 },  // C - SKU
        { width: 10 },  // D - Qty
        { width: 9 },   // E - Unit
        { width: 22 },  // F - Unit Price / Grand Total label
        { width: 18 },  // G - Amount
    ];

    let r = 1;

    // === COMPANY HEADER BAR ===
    ws.mergeCells(`A${r}:G${r}`);
    const titleCell = ws.getCell(`A${r}`);
    titleCell.value = (project.companyName || project.name || '').toUpperCase();
    titleCell.font = { name: 'Calibri', size: 16, bold: true, color: { argb: white } };
    titleCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: navy } };
    titleCell.alignment = { vertical: 'middle', horizontal: 'left', indent: 1 };
    ws.getRow(r).height = 38;
    r++;

    // SSM + Contact line
    ws.mergeCells(`A${r}:G${r}`);
    const contactCell = ws.getCell(`A${r}`);
    const ssmVal = project.companySsm || project.companySSM || '';
    contactCell.value = [ssmVal ? `SSM: ${ssmVal}` : '', companyAddress, project.companyPhone ? `Tel: ${project.companyPhone}` : '', project.companyEmail ? `Email: ${project.companyEmail}` : ''].filter(Boolean).join('  |  ');
    contactCell.font = { name: 'Calibri', size: 9, color: { argb: slate500 } };
    contactCell.alignment = { vertical: 'middle', horizontal: 'left', indent: 1 };
    contactCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: slate50 } };
    ws.getRow(r).height = 22;
    r++;

    // Brand accent line
    ws.mergeCells(`A${r}:G${r}`);
    ws.getCell(`A${r}`).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: brandBlue } };
    ws.getRow(r).height = 4;
    r++;

    // Spacer
    ws.getRow(r).height = 8;
    r++;

    // === PURCHASE ORDER TITLE + PO META ===
    ws.mergeCells(`A${r}:C${r}`);
    const poTitleCell = ws.getCell(`A${r}`);
    poTitleCell.value = 'PURCHASE ORDER';
    poTitleCell.font = { name: 'Calibri', size: 20, bold: true, color: { argb: navy } };
    poTitleCell.alignment = { vertical: 'middle', horizontal: 'left', indent: 1 };

    // PO meta on the right
    const metaLabels = [t('po.poNoLabel'), t('po.dateLabel'), t('po.paymentTermLabel'), t('po.statusLabel')];
    const metaValues = [poNumber, poDate, po.paymentTerm || '30 Days', po.status === 'Selesai' ? 'Completed' : po.status === 'Batal' ? 'Cancelled' : 'Pending'];
    ws.getCell(`F${r}`).value = metaLabels[0];
    ws.getCell(`F${r}`).font = { name: 'Calibri', size: 9, bold: true, color: { argb: slate500 } };
    ws.getCell(`F${r}`).alignment = { horizontal: 'right' };
    ws.getCell(`G${r}`).value = metaValues[0];
    ws.getCell(`G${r}`).font = { name: 'Calibri', size: 10, bold: true, color: { argb: navy } };
    ws.getRow(r).height = 30;
    r++;

    for (let i = 1; i < metaLabels.length; i++) {
        ws.getCell(`F${r}`).value = metaLabels[i];
        ws.getCell(`F${r}`).font = { name: 'Calibri', size: 9, bold: true, color: { argb: slate500 } };
        ws.getCell(`F${r}`).alignment = { horizontal: 'right' };
        ws.getCell(`G${r}`).value = metaValues[i];
        ws.getCell(`G${r}`).font = { name: 'Calibri', size: 10, color: { argb: navy } };
        if (metaLabels[i] === 'Status:') {
            ws.getCell(`G${r}`).font = { name: 'Calibri', size: 10, bold: true, color: { argb: po.status === 'Diterima' || po.status === 'Selesai' ? brandBlueDark : 'D97706' } };
        }
        ws.getRow(r).height = 18;
        r++;
    }

    // Spacer
    ws.getRow(r).height = 8;
    r++;

    // === VENDOR + SHIP TO SECTION ===
    // Vendor header
    ws.mergeCells(`A${r}:C${r}`);
    ws.getCell(`A${r}`).value = 'VENDOR (SUPPLIER)';
    ws.getCell(`A${r}`).font = { name: 'Calibri', size: 9, bold: true, color: { argb: white } };
    ws.getCell(`A${r}`).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: slate500 } };
    ws.getCell(`A${r}`).alignment = { indent: 1, vertical: 'middle' };
    // Ship-to header
    ws.mergeCells(`E${r}:G${r}`);
    ws.getCell(`E${r}`).value = 'SHIP TO';
    ws.getCell(`E${r}`).font = { name: 'Calibri', size: 9, bold: true, color: { argb: white } };
    ws.getCell(`E${r}`).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: slate500 } };
    ws.getCell(`E${r}`).alignment = { indent: 1, vertical: 'middle' };
    ws.getCell(`D${r}`).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: white } };
    ws.getRow(r).height = 22;
    r++;

    const vendorLines = [
        { label: po.supplier, bold: true },
        { label: supAddress || '-' },
        { label: `Attn: ${sup.picName || '-'}` },
        { label: `Tel: ${sup.picPhone || '-'}  |  Email: ${sup.email || '-'}` },
    ];
    const shipLines = [
        { label: project.warehouseName || project.name, bold: true },
        { label: shipAddress || '-' },
        { label: `Attn: ${project.warehousePicName || project.warehousePICName || '-'}` },
        { label: `Tel: ${project.warehousePicPhone || project.warehousePICPhone || '-'}` },
    ];

    for (let i = 0; i < Math.max(vendorLines.length, shipLines.length); i++) {
        ws.mergeCells(`A${r}:C${r}`);
        ws.mergeCells(`E${r}:G${r}`);
        if (vendorLines[i]) {
            ws.getCell(`A${r}`).value = vendorLines[i].label;
            ws.getCell(`A${r}`).font = { name: 'Calibri', size: vendorLines[i].bold ? 11 : 9.5, bold: !!vendorLines[i].bold, color: { argb: vendorLines[i].bold ? navy : slate500 } };
            ws.getCell(`A${r}`).alignment = { indent: 1, vertical: 'middle' };
        }
        if (shipLines[i]) {
            ws.getCell(`E${r}`).value = shipLines[i].label;
            ws.getCell(`E${r}`).font = { name: 'Calibri', size: shipLines[i].bold ? 11 : 9.5, bold: !!shipLines[i].bold, color: { argb: shipLines[i].bold ? navy : slate500 } };
            ws.getCell(`E${r}`).alignment = { indent: 1, vertical: 'middle' };
        }
        // Light background for vendor/ship section
        for (const col of ['A','B','C']) { ws.getCell(`${col}${r}`).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: slate50 } }; }
        for (const col of ['E','F','G']) { ws.getCell(`${col}${r}`).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: slate50 } }; }
        ws.getRow(r).height = 18;
        r++;
    }

    // Spacer
    ws.getRow(r).height = 12;
    r++;

    // === ITEMS TABLE HEADER ===
    const headerLabels = ['#', 'ITEM DESCRIPTION', 'SKU', 'QTY', 'UNIT', 'UNIT PRICE (RM)', 'AMOUNT (RM)'];
    const headerRow = ws.getRow(r);
    headerLabels.forEach((label, i) => {
        const cell = headerRow.getCell(i + 1);
        cell.value = label;
        cell.font = { name: 'Calibri', size: 9, bold: true, color: { argb: white } };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: brandBlue } };
        cell.alignment = { horizontal: i >= 3 ? 'right' : (i === 0 ? 'center' : 'left'), vertical: 'middle' };
        cell.border = { bottom: { style: 'thin', color: { argb: brandBlueDark } } };
    });
    headerRow.height = 28;
    r++;

    // === ITEMS ROWS ===
    let subtotal = 0;
    po.items.forEach((item, idx) => {
        const lineTotal = item.quantity * (item.cost || 0);
        subtotal += lineTotal;
        const row = ws.getRow(r);
        const isEven = idx % 2 === 0;
        const bgColor = isEven ? white : slate100;

        const vals = [idx + 1, item.name, item.sku || '-', item.quantity, item.unit || 'pcs', item.cost || 0, lineTotal];
        vals.forEach((val, i) => {
            const cell = row.getCell(i + 1);
            cell.value = val;
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: bgColor } };
            cell.border = { bottom: { style: 'hair', color: { argb: slate200 } } };

            if (i === 0) {
                cell.font = { name: 'Calibri', size: 10, bold: true, color: { argb: slate500 } };
                cell.alignment = { horizontal: 'center', vertical: 'middle' };
            } else if (i === 1) {
                cell.font = { name: 'Calibri', size: 10, bold: true, color: { argb: navy } };
                cell.alignment = { vertical: 'middle', wrapText: true };
            } else if (i >= 5) {
                cell.numFmt = '#,##0.00';
                cell.font = { name: 'Calibri', size: 10, color: { argb: navy } };
                cell.alignment = { horizontal: 'right', vertical: 'middle' };
            } else if (i === 3) {
                cell.font = { name: 'Calibri', size: 10, color: { argb: navy } };
                cell.alignment = { horizontal: 'right', vertical: 'middle' };
            } else {
                cell.font = { name: 'Calibri', size: 10, color: { argb: slate500 } };
                cell.alignment = { horizontal: i >= 3 ? 'right' : 'left', vertical: 'middle' };
            }
        });
        row.height = 24;
        r++;
    });

    // === TOTALS SECTION ===
    // Thin green line separator
    for (let c = 1; c <= 7; c++) {
        ws.getCell(r, c).border = { top: { style: 'medium', color: { argb: brandBlue } } };
    }
    ws.getRow(r).height = 6;
    r++;

    // Subtotal
    ws.mergeCells(`A${r}:E${r}`);
    ws.getCell(`F${r}`).value = 'SUBTOTAL';
    ws.getCell(`F${r}`).font = { name: 'Calibri', size: 10, bold: true, color: { argb: slate500 } };
    ws.getCell(`F${r}`).alignment = { horizontal: 'right', vertical: 'middle' };
    ws.getCell(`G${r}`).value = subtotal;
    ws.getCell(`G${r}`).numFmt = '#,##0.00';
    ws.getCell(`G${r}`).font = { name: 'Calibri', size: 10, color: { argb: navy } };
    ws.getCell(`G${r}`).alignment = { horizontal: 'right', vertical: 'middle' };
    ws.getRow(r).height = 22;
    r++;

    // Grand Total
    ws.mergeCells(`A${r}:E${r}`);
    ws.getCell(`F${r}`).value = 'GRAND TOTAL (RM)';
    ws.getCell(`F${r}`).font = { name: 'Calibri', size: 12, bold: true, color: { argb: white } };
    ws.getCell(`F${r}`).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: navy } };
    ws.getCell(`F${r}`).alignment = { horizontal: 'right', vertical: 'middle' };
    ws.getCell(`F${r}`).border = { top: { style: 'thin', color: { argb: slate200 } }, bottom: { style: 'thin', color: { argb: slate200 } } };
    ws.getCell(`G${r}`).value = po.totalCost || subtotal;
    ws.getCell(`G${r}`).numFmt = '#,##0.00';
    ws.getCell(`G${r}`).font = { name: 'Calibri', size: 14, bold: true, color: { argb: white } };
    ws.getCell(`G${r}`).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: brandBlue } };
    ws.getCell(`G${r}`).alignment = { horizontal: 'right', vertical: 'middle' };
    ws.getRow(r).height = 32;
    r++;

    // Spacer
    ws.getRow(r).height = 12;
    r++;

    // === NOTES ===
    if (po.notes) {
        ws.mergeCells(`A${r}:G${r}`);
        ws.getCell(`A${r}`).value = 'NOTES / INSTRUCTIONS';
        ws.getCell(`A${r}`).font = { name: 'Calibri', size: 9, bold: true, color: { argb: slate500 } };
        ws.getCell(`A${r}`).alignment = { indent: 1 };
        ws.getRow(r).height = 18;
        r++;

        ws.mergeCells(`A${r}:G${r}`);
        ws.getCell(`A${r}`).value = po.notes;
        ws.getCell(`A${r}`).font = { name: 'Calibri', size: 10, color: { argb: navy } };
        ws.getCell(`A${r}`).alignment = { indent: 1, wrapText: true, vertical: 'top' };
        ws.getRow(r).height = 30;
        r++;

        ws.getRow(r).height = 8;
        r++;
    }

    // === TERMS & CONDITIONS ===
    ws.mergeCells(`A${r}:G${r}`);
    ws.getCell(`A${r}`).value = 'TERMS & CONDITIONS';
    ws.getCell(`A${r}`).font = { name: 'Calibri', size: 8, bold: true, color: { argb: slate500 } };
    ws.getCell(`A${r}`).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: slate50 } };
    ws.getCell(`A${r}`).alignment = { indent: 1, vertical: 'middle' };
    ws.getRow(r).height = 18;
    r++;

    const terms = [
        'Goods once sold will not be taken back or exchanged.',
        'Goods remain the property of the vendor till fully paid.',
        'Delivery must be made within the time specified.',
        `Payment terms: ${po.paymentTerm || '30 days from invoice date'}.`,
    ];
    terms.forEach(t => {
        ws.mergeCells(`A${r}:G${r}`);
        ws.getCell(`A${r}`).value = `    ${t}`;
        ws.getCell(`A${r}`).font = { name: 'Calibri', size: 8, color: { argb: slate500 } };
        ws.getCell(`A${r}`).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: slate50 } };
        ws.getRow(r).height = 15;
        r++;
    });

    // Spacer
    ws.getRow(r).height = 8;
    r++;

    // === FOOTER ===
    ws.mergeCells(`A${r}:G${r}`);
    ws.getCell(`A${r}`).value = 'This is a computer-generated document. No signature is required.';
    ws.getCell(`A${r}`).font = { name: 'Calibri', size: 8, italic: true, color: { argb: slate500 } };
    ws.getCell(`A${r}`).alignment = { horizontal: 'center', vertical: 'middle' };
    r++;
    ws.mergeCells(`A${r}:G${r}`);
    ws.getCell(`A${r}`).value = `Generated by ENHIVE on ${generatedAt}`;
    ws.getCell(`A${r}`).font = { name: 'Calibri', size: 8, color: { argb: slate500 } };
    ws.getCell(`A${r}`).alignment = { horizontal: 'center', vertical: 'middle' };

    // === PRINT SETUP ===
    ws.pageSetup = { paperSize: 9, orientation: 'portrait', fitToPage: true, fitToWidth: 1, fitToHeight: 0, margins: { left: 0.4, right: 0.4, top: 0.5, bottom: 0.5, header: 0.3, footer: 0.3 } };

    // Write file
    const buffer = await wb.xlsx.writeBuffer();
    saveAs(new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), `${poNumber}_${poDate.replace(/\s/g, '-')}.xlsx`);
};

// Export PO to PDF - Opens HTML version in new tab for printing
const exportPOToPDF = (po, project, suppliers, t = (k) => k, language = 'en') => {
    const poNumber = `PO-${po.id.slice(-6).toUpperCase()}`;
    const formattedDate = new Date().toLocaleDateString('en-MY', { day: '2-digit', month: 'short', year: 'numeric' });
    const currentDateTime = new Date().toLocaleString('en-MY', { 
        day: '2-digit', 
        month: '2-digit', 
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
    });
    
    // Find supplier details
    const supplierDetails = suppliers.find(s => s.name === po.supplier) || {};
    
    // Construct company mailing address
    const companyMailingAddress = [
        project.companyAddressLine1,
        project.companyAddressLine2,
        [project.companyPostcode, project.companyState].filter(Boolean).join(' ')
    ].filter(Boolean).join(', ');
    
    // Construct warehouse shipping address
    const warehouseShippingAddress = [
        project.warehouseAddressLine1,
        project.warehouseAddressLine2,
        [project.warehousePostcode, project.warehouseState].filter(Boolean).join(' ')
    ].filter(Boolean).join(', ');
    
    // Generate HTML content
    const htmlContent = `
<!DOCTYPE html>
<html lang="ms">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Purchase Order #${poNumber}</title>
    <link rel="preconnect" href="https://fonts.googleapis.com">
    <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
    <link href="https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700&display=swap" rel="stylesheet">
    <style>
        * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
        }
        
        body {
            font-family: 'Poppins', Arial, Helvetica, sans-serif;
            font-size: 11pt;
            line-height: 1.4;
            color: #1e293b;
            padding: 10mm;
            background: white;
        }
        
        @media print {
            body {
                padding: 5mm;
            }
            .no-print {
                display: none !important;
            }
        }
        
        .po-container {
            max-width: 800px;
            margin: 0 auto;
            background: white;
            border: 1px solid #e2e8f0;
        }
        
        /* Header Section */
        .po-header {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 20px;
            padding: 20px;
            border-bottom: 2px solid #e2e8f0;
        }
        
        .company-info h1 {
            font-size: 16pt;
            font-weight: bold;
            color: #0f172a;
            margin-bottom: 5px;
        }
        
        .company-info p {
            font-size: 9pt;
            color: #64748b;
            margin: 2px 0;
        }
        
        .po-details {
            text-align: right;
        }
        
        .po-title {
            font-size: 20pt;
            font-weight: bold;
            color: #0f172a;
            margin-bottom: 10px;
        }
        
        .po-meta {
            text-align: right;
            font-size: 10pt;
        }
        
        .po-meta div {
            margin: 5px 0;
        }
        
        .po-meta strong {
            color: #0f172a;
        }
        
        .status-badge {
            display: inline-block;
            padding: 3px 10px;
            border-radius: 12px;
            font-size: 9pt;
            font-weight: bold;
            margin-left: 5px;
        }
        
        .status-selesai {
            background-color: #d1fae5;
            color: #065f46;
        }
        
        .status-belum {
            background-color: #fef3c7;
            color: #92400e;
        }
        
        /* Vendor and Ship To Section */
        .info-section {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 20px;
            padding: 15px 20px;
            background: #f8fafc;
            border-bottom: 1px solid #e2e8f0;
        }
        
        .info-box h3 {
            font-size: 10pt;
            font-weight: bold;
            color: #64748b;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            margin-bottom: 10px;
        }
        
        .info-box h4 {
            font-size: 11pt;
            font-weight: bold;
            color: #0f172a;
            margin-bottom: 5px;
        }
        
        .info-box p {
            font-size: 9.5pt;
            color: #475569;
            margin: 2px 0;
        }
        
        /* Items Table */
        .items-table {
            width: 100%;
            border-collapse: collapse;
            margin: 0;
        }
        
        .items-table thead {
            background-color: #10056e;
            color: white;
        }
        
        .items-table th {
            padding: 12px 10px;
            text-align: left;
            font-size: 9pt;
            font-weight: bold;
            text-transform: uppercase;
            letter-spacing: 0.5px;
        }
        
        .items-table th:first-child {
            text-align: center;
            width: 40px;
        }
        
        .items-table th:nth-child(3),
        .items-table th:nth-child(4) {
            text-align: right;
        }
        
        .items-table tbody tr {
            border-bottom: 1px solid #e2e8f0;
        }
        
        .items-table tbody tr:hover {
            background-color: #f8fafc;
        }
        
        .items-table td {
            padding: 12px 10px;
            font-size: 10pt;
            color: #1e293b;
        }
        
        .items-table td:first-child {
            text-align: center;
            color: #64748b;
            font-weight: bold;
        }
        
        .items-table td:nth-child(3),
        .items-table td:nth-child(4) {
            text-align: right;
        }
        
        .item-name {
            font-weight: 600;
            color: #0f172a;
        }
        
        /* Summary Section */
        .summary-section {
            display: grid;
            grid-template-columns: 1fr 300px;
            gap: 20px;
            padding: 15px 20px;
            border-top: 2px solid #e2e8f0;
        }
        
        .notes-box h4 {
            font-size: 10pt;
            font-weight: bold;
            color: #0f172a;
            margin-bottom: 8px;
        }
        
        .notes-box p {
            font-size: 9.5pt;
            color: #64748b;
            line-height: 1.5;
        }
        
        .total-box {
            text-align: right;
        }
        
        .total-label {
            font-size: 12pt;
            font-weight: bold;
            color: #0f172a;
            margin-bottom: 5px;
        }
        
        .total-amount {
            font-size: 24pt;
            font-weight: bold;
            color: #10056e;
        }
        
        /* Footer Section */
        .po-footer {
            padding: 15px 20px;
            background: #f8fafc;
            border-top: 1px solid #e2e8f0;
        }
        
        .footer-title {
            font-size: 10pt;
            font-weight: bold;
            color: #0f172a;
            margin-bottom: 8px;
            text-transform: uppercase;
        }
        
        .footer-terms {
            list-style: none;
            padding: 0;
            line-height: 1.3;
        }
        
        .footer-terms li {
            font-size: 9pt;
            color: #475569;
            margin: 3px 0;
            padding-left: 15px;
            position: relative;
        }
        
        .footer-terms li:before {
            content: "•";
            position: absolute;
            left: 0;
            color: #10056e;
            font-weight: bold;
        }
        
        .generated-info {
            text-align: center;
            margin-top: 20px;
            padding-top: 15px;
            border-top: 1px solid #e2e8f0;
        }
        
        .generated-info p {
            font-size: 8pt;
            color: #94a3b8;
            margin: 3px 0;
        }
    </style>
</head>
<body>
    <div class="po-container">
        <!-- Header -->
        <div class="po-header">
            <div class="company-info">
                ${project.companyLogo ? `<img src="${project.companyLogo}" alt="Company Logo" style="width: 80px; height: 80px; object-fit: contain;">` : ''}
                <h1>${project.companyName || project.name}</h1>
                <p><strong>SSM:</strong> ${project.companySsm || project.companySSM || '-'}</p>
                <p>${companyMailingAddress || project.companyAddress || '-'}</p>
                <p><strong>Tel:</strong> ${project.companyPhone || '-'} | <strong>Email:</strong> ${project.companyEmail || '-'}</p>
            </div>
            <div class="po-details">
                <div class="po-title">PURCHASE ORDER</div>
                <div class="po-meta">
                    <div><strong>PO No:</strong> ${poNumber}</div>
                    <div><strong>Date:</strong> ${formattedDate}</div>
                    <div><strong>Pay Term:</strong> ${po.paymentTerm || '30 Days'}</div>
                    <div><strong>Status:</strong> <span class="status-badge ${po.status === 'Selesai' ? 'status-selesai' : 'status-belum'}">${po.status === 'Selesai' ? 'Completed' : po.status === 'Batal' ? 'Cancelled' : 'Pending'}</span></div>
                </div>
            </div>
        </div>
        
        <!-- Vendor and Ship To -->
        <div class="info-section">
            <div class="info-box">
                <h3>VENDOR (SUPPLIER)</h3>
                <h4>${po.supplier}</h4>
                <p>${supplierDetails.addressLine1 || ''}</p>
                <p>${supplierDetails.addressLine2 || ''}</p>
                <p>${supplierDetails.postcode || ''} ${supplierDetails.state || ''}</p>
                <p><strong>Attn:</strong> ${supplierDetails.picName || '-'}</p>
                <p><strong>Tel:</strong> ${supplierDetails.picPhone || supplierDetails.phone || '-'}</p>
                <p><strong>Email:</strong> ${supplierDetails.email || '-'}</p>
            </div>
            <div class="info-box">
                <h3>SHIP TO</h3>
                <h4>${project.warehouseName || project.name}</h4>
                <p>${warehouseShippingAddress || project.warehouseAddress || '-'}</p>
                <p><strong>Attn:</strong> ${project.warehousePicName || project.warehousePICName || '-'}</p>
                <p><strong>Tel:</strong> ${project.warehousePicPhone || project.warehousePICPhone || '-'}</p>
                <p><strong>Email:</strong> ${project.warehousePicEmail || project.warehousePICEmail || '-'}</p>
            </div>
        </div>
        
        <!-- Items Table -->
        <table class="items-table">
            <thead>
                <tr>
                    <th>#</th>
                    <th>ITEM DESCRIPTION</th>
                    <th>SKU</th>
                    <th>QTY</th>
                </tr>
            </thead>
            <tbody>
                ${po.items.map((item, index) => `
                    <tr>
                        <td>${index + 1}</td>
                        <td>
                            <div class="item-name">${item.name}</div>
                        </td>
                        <td>${item.sku || '-'}</td>
                        <td>${formatNumber(item.quantity)} ${item.unit || 'pcs'}</td>
                    </tr>
                `).join('')}
            </tbody>
        </table>
        
        <!-- Summary -->
        <div class="summary-section" style="grid-template-columns: 1fr;">
            <div class="notes-box">
                <h4>NOTES/INSTRUCTIONS:</h4>
                <p>${po.notes || t('reports.noAdditionalNotes')}</p>
            </div>
        </div>
        
        <!-- Footer -->
        <div class="po-footer">
            <div class="footer-title">Terms & Conditions</div>
            <ul class="footer-terms">
                <li>Goods once sold will not be taken back or exchanged</li>
                <li>Goods remain the property of the vendor till fully paid</li>
                <li>Delivery must be made within the time specified</li>
                <li>All disputes are subject to ${project.state || 'Kuala Lumpur'} jurisdiction only</li>
                <li>Payment terms: ${po.paymentTerm || '30 days from invoice date'}</li>
            </ul>
            
            <div class="generated-info">
                <p><strong>This is a computer-generated document. No signature is required.</strong></p>
                <p>Generated by ENHIVE on ${currentDateTime}</p>
            </div>
        </div>
    </div>
    
    <!-- Floating Print Button -->
    <div class="print-button-container">
        <button onclick="window.print()" class="print-btn">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="6 9 6 2 18 2 18 9"></polyline>
                <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2 2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"></path>
                <rect x="6" y="14" width="12" height="8"></rect>
            </svg>
            ${t("common.print")}
        </button>
    </div>
    
    <style>
        .print-button-container {
            position: fixed;
            top: 20px;
            right: 20px;
            z-index: 1000;
        }
        
        .print-btn {
            display: flex;
            align-items: center;
            gap: 8px;
            padding: 12px 24px;
            background: white;
            border: 2px solid #64748b;
            color: #64748b;
            font-size: 14px;
            font-weight: 600;
            border-radius: 8px;
            cursor: pointer;
            box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
            transition: all 0.2s;
        }
        
        .print-btn:hover {
            background: #f8fafc;
            border-color: #475569;
            color: #475569;
            transform: translateY(-1px);
            box-shadow: 0 6px 12px -2px rgba(0, 0, 0, 0.15);
        }
        
        .print-btn:active {
            transform: translateY(0);
        }
        
        @media print {
            .print-button-container {
                display: none;
            }
        }
    </style>
    
    <script>
        // Auto-print dialog on load (optional)
        // window.onload = function() {
        //     window.print();
        // };
    </script>
</body>
</html>
    `;
    
    // Open in new tab and trigger print dialog
    const printWindow = window.open('', '_blank');
    printWindow.document.write(htmlContent);
    printWindow.document.close();
    
    // Wait for content to load, then trigger print dialog
    printWindow.onload = function() {
        printWindow.focus();
        printWindow.print();
    };
};

// Export Audit Report to Excel
const exportAuditToExcel = (audit, project, t = (k) => k, language = 'en') => {
    const auditNumber = `AUDIT-${audit.id.slice(-6).toUpperCase()}`;
    const formattedDate = new Date(audit.startDate).toLocaleDateString('en-MY', { day: '2-digit', month: 'short', year: 'numeric' });
    
    // Create workbook
    const wb = XLSX.utils.book_new();
    
    // Header data
    const headerData = [
        [t('reports.auditReportTitle')],
        [''],
        [project.companyName || project.name],
        [project.companyAddress || ''],
        [`Tel: ${project.companyPhone || '-'} | Email: ${project.companyEmail || '-'}`],
        [''],
        [t('reports.auditNo') + ':', auditNumber],
        [t('reports.dateLabel') + ':', new Date(audit.startDate).toLocaleDateString('en-MY')],
        [t('reports.auditor') + ':', audit.auditorName],
        [t('reports.statusLabel') + ':', audit.status === 'Completed' ? t('reports.completedStatus') : t('reports.inProgressStatus')],
        [language === 'en' ? 'Progress:' : 'Kemajuan:', `${audit.progress || 0}%`],
        [language === 'en' ? 'Notes:' : 'Nota:', audit.notes || '-'],
        [''],
        [t('reports.itemsList')],
        [t('reports.noLabel'), t('reports.productName'), t('reports.skuLabel'), t('reports.systemStockLabel'), t('reports.physicalStockLabel'), t('reports.unitLabel'), t('reports.differenceLabel'), t('reports.statusColLabel')]
    ];
    
    // Items data
    const itemsData = audit.items.map((item, index) => {
        const diff = (item.actualQty || 0) - item.systemQty;
        const status = diff === 0 ? t('stockCheck.match') : diff > 0 ? t('stockCheck.excess') : t('stockCheck.shortage');
        return [
            index + 1,
            item.name,
            item.sku || '-',
            item.systemQty,
            item.actualQty || 0,
            item.unit || 'pcs',
            diff,
            status
        ];
    });
    
    // Summary data
    const totalItems = audit.items.length;
    const matchedItems = audit.items.filter(i => (i.actualQty || 0) === i.systemQty).length;
    const discrepancies = audit.items.filter(i => i.confirmed && (i.actualQty || 0) !== i.systemQty).length;
    
    const summaryData = [
        [''],
        ['RINGKASAN'],
        [t('reports.totalItems') + ':', totalItems],
        [t('reports.matchedLabel') + ':', matchedItems],
        ['Item Berbeza:', discrepancies]
    ];
    
    // Combine all data
    const wsData = [...headerData, ...itemsData, ...summaryData];
    
    // Create worksheet
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    
    // Set column widths
    ws['!cols'] = [
        { wch: 5 },  // Bil
        { wch: 30 }, // Nama Produk
        { wch: 12 }, // SKU
        { wch: 12 }, // Stok Sistem
        { wch: 12 }, // Stok Fizikal
        { wch: 8 },  // Unit
        { wch: 12 }, // Perbezaan
        { wch: 10 }  // Status
    ];
    
    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, 'Laporan Audit');
    
    // Generate file
    XLSX.writeFile(wb, `${auditNumber}_${formattedDate}.xlsx`);
};

// Export Audit Report to PDF - Opens HTML version in new tab for printing
const exportAuditToPDF = (audit, project, t = (k) => k, language = 'en') => {
    const auditNumber = `AUDIT-${audit.id.slice(-6).toUpperCase()}`;
    const formattedDate = new Date(audit.startDate).toLocaleDateString('en-MY', { day: '2-digit', month: 'short', year: 'numeric' });
    const currentDateTime = new Date().toLocaleString('en-MY', { 
        day: '2-digit', 
        month: '2-digit', 
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
    });
    
    const totalItems = audit.items.length;
    const matchedItems = audit.items.filter(i => (i.actualQty || 0) === i.systemQty).length;
    const discrepancies = audit.items.filter(i => i.confirmed && (i.actualQty || 0) !== i.systemQty).length;
    
    // Generate HTML content
    const htmlContent = `
<!DOCTYPE html>
<html lang="ms">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>${t("reports.auditReportTitle")} #${auditNumber}</title>
    <link rel="preconnect" href="https://fonts.googleapis.com">
    <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
    <link href="https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700&display=swap" rel="stylesheet">
    <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { font-family: 'Poppins', Arial, sans-serif; font-size: 11pt; line-height: 1.4; color: #1e293b; padding: 10mm; background: white; }
        @media print { body { padding: 5mm; } .no-print { display: none !important; } }
        .audit-container { max-width: 800px; margin: 0 auto; background: white; border: 1px solid #e2e8f0; }
        .audit-header { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; padding: 20px; border-bottom: 2px solid #e2e8f0; }
        .company-info h1 { font-size: 16pt; font-weight: bold; color: #0f172a; margin-bottom: 5px; }
        .company-info p { font-size: 9pt; color: #64748b; margin: 2px 0; }
        .audit-details { text-align: right; }
        .audit-title { font-size: 20pt; font-weight: bold; color: #0f172a; margin-bottom: 10px; }
        .audit-meta { font-size: 9pt; color: #64748b; line-height: 1.6; }
        .audit-meta strong { color: #0f172a; font-weight: 600; }
        .audit-summary { display: grid; grid-template-columns: repeat(3, 1fr); gap: 15px; padding: 20px; background: #f8fafc; border-bottom: 2px solid #e2e8f0; }
        .summary-box { text-align: center; padding: 10px; background: white; border-radius: 8px; border: 1px solid #e2e8f0; }
        .summary-label { font-size: 8pt; color: #64748b; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 5px; font-weight: 600; }
        .summary-value { font-size: 18pt; font-weight: bold; color: #0f172a; }
        .items-section { padding: 20px; }
        .section-title { font-size: 12pt; font-weight: bold; color: #0f172a; margin-bottom: 15px; padding-bottom: 8px; border-bottom: 2px solid #e2e8f0; }
        table { width: 100%; border-collapse: collapse; font-size: 9pt; }
        th { background: #f1f5f9; color: #475569; font-weight: 600; text-align: left; padding: 10px 8px; border: 1px solid #e2e8f0; font-size: 8pt; text-transform: uppercase; letter-spacing: 0.3px; }
        td { padding: 8px; border: 1px solid #e2e8f0; color: #334155; }
        tr:nth-child(even) { background: #f8fafc; }
        .status-match { color: #059669; font-weight: 600; }
        .status-diff-lebih { color: #2563eb; font-weight: 600; }
        .status-diff-kurang { color: #f59e0b; font-weight: 600; }
        .diff-positive { color: #2563eb; font-weight: 600; }
        .diff-negative { color: #f59e0b; font-weight: 600; }
        .audit-footer { padding: 20px; background: #f8fafc; border-top: 2px solid #e2e8f0; font-size: 8pt; color: #64748b; text-align: center; }
        .audit-footer p { margin: 3px 0; }
        .audit-footer strong { color: #0f172a; }
    </style>
</head>
<body>
    <div class="audit-container">
        <div class="audit-header">
            <div class="company-info">
                ${project.companyLogo ? `<img src="${project.companyLogo}" alt="Company Logo" style="width: 80px; height: 80px; object-fit: contain;">` : ''}
                <h1>${project.companyName || project.name}</h1>
                <p>${project.companyAddressLine1 || ''}</p>
                <p>${project.companyAddressLine2 || ''}</p>
                <p>${[project.companyPostcode, project.companyState].filter(Boolean).join(' ')}</p>
                <p>Tel: ${project.companyPhone || '-'} | Email: ${project.companyEmail || '-'}</p>
            </div>
            <div class="audit-details">
                <div class="audit-title">${t("reports.auditReportTitle")}</div>
                <div class="audit-meta">
                    <p><strong>${t("reports.auditNo")}:</strong> ${auditNumber}</p>
                    <p><strong>${t('reports.dateLabel')}:</strong> ${formattedDate}</p>
                    <p><strong>${t("reports.auditor")}:</strong> ${audit.auditorName}</p>
                    <p><strong>${t('reports.statusLabel')}:</strong> ${audit.status === 'Completed' ? t('reports.completedStatus') : t('reports.inProgressStatus')}</p>
                </div>
            </div>
        </div>
        
        <div class="audit-summary">
            <div class="summary-box">
                <div class="summary-label">${t("reports.totalItems")}</div>
                <div class="summary-value">${formatNumber(totalItems)}</div>
            </div>
            <div class="summary-box">
                <div class="summary-label">${t("reports.matchedLabel")}</div>
                <div class="summary-value" style="color: #059669;">${formatNumber(matchedItems)}</div>
            </div>
            <div class="summary-box">
                <div class="summary-label">${t("reports.discrepanciesLabel")}</div>
                <div class="summary-value" style="color: #dc2626;">${formatNumber(discrepancies)}</div>
            </div>
        </div>
        
        <div class="items-section">
            <div class="section-title">${t("reports.itemsList")}</div>
            <table>
                <thead>
                    <tr>
                        <th style="width: 5%;">${t("reports.noLabel")}</th>
                        <th style="width: 30%;">${t("reports.productName")}</th>
                        <th style="width: 12%;">SKU</th>
                        <th style="width: 10%;">${t("reports.systemQty")}</th>
                        <th style="width: 10%;">${t("reports.physicalQty")}</th>
                        <th style="width: 8%;">${t("reports.unitLabel")}</th>
                        <th style="width: 10%;">${t("reports.difference")}</th>
                        <th style="width: 10%;">${t("reports.statusLabel")}</th>
                    </tr>
                </thead>
                <tbody>
                    ${audit.items.map((item, index) => {
                        const diff = (item.actualQty || 0) - item.systemQty;
                        const isMatch = diff === 0;
                        const diffClass = diff > 0 ? 'diff-positive' : diff < 0 ? 'diff-negative' : '';
                        const status = isMatch ? t('stockCheck.match') : diff > 0 ? t('stockCheck.excess') : t('stockCheck.shortage');
                        const statusClass = isMatch ? 'status-match' : diff > 0 ? 'status-diff-lebih' : 'status-diff-kurang';
                        return `
                        <tr>
                            <td style="text-align: center;">${index + 1}</td>
                            <td><strong>${item.name}</strong></td>
                            <td>${item.sku || '-'}</td>
                            <td style="text-align: right;">${formatNumber(item.systemQty)}</td>
                            <td style="text-align: right;">${formatNumber(item.actualQty || 0)}</td>
                            <td>${item.unit || 'pcs'}</td>
                            <td style="text-align: right;" class="${diffClass}">${diff > 0 ? '+' : ''}${formatNumber(Math.abs(diff))}</td>
                            <td class="${statusClass}">${status}</td>
                        </tr>
                        `;
                    }).join('')}
                </tbody>
            </table>
        </div>
        
        ${audit.notes ? `
        <div class="items-section" style="padding-top: 0;">
            <div class="section-title">${t("reports.auditNotes")}</div>
            <p style="padding: 10px; background: #f8fafc; border-radius: 8px; color: #475569;">${audit.notes}</p>
        </div>
        ` : ''}
        
        <div class="audit-footer">
            <p><strong>${t("reports.autoGenerated")}</strong></p>
            <p>${t("reports.generatedBy")} ${currentDateTime}</p>
        </div>
    </div>
    
    <!-- Floating Print Button -->
    <div class="print-button-container">
        <button onclick="window.print()" class="print-btn">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="6 9 6 2 18 2 18 9"></polyline>
                <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2 2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"></path>
                <rect x="6" y="14" width="12" height="8"></rect>
            </svg>
            ${t("common.print")}
        </button>
    </div>
    
    <style>
        .print-button-container {
            position: fixed;
            top: 20px;
            right: 20px;
            z-index: 1000;
        }
        
        .print-btn {
            display: flex;
            align-items: center;
            gap: 8px;
            padding: 12px 24px;
            background: white;
            border: 2px solid #64748b;
            color: #64748b;
            font-size: 14px;
            font-weight: 600;
            border-radius: 8px;
            cursor: pointer;
            box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
            transition: all 0.2s;
        }
        
        .print-btn:hover {
            background: #f8fafc;
            border-color: #475569;
            color: #475569;
            transform: translateY(-1px);
            box-shadow: 0 6px 12px -2px rgba(0, 0, 0, 0.15);
        }
        
        .print-btn:active {
            transform: translateY(0);
        }
        
        @media print {
            .print-button-container {
                display: none;
            }
        }
    </style>
</body>
</html>
    `;
    
    // Open in new tab and trigger print dialog
    const printWindow = window.open('', '_blank');
    printWindow.document.write(htmlContent);
    printWindow.document.close();
    
    // Wait for content to load, then trigger print dialog
    printWindow.onload = function() {
        printWindow.focus();
        printWindow.print();
    };
};

// Public Product Page Component - accessible via QR code
function PublicProductPage({ productId }) {
    const [product, setProduct] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const API_URL = process.env.REACT_APP_BACKEND_URL;

    useEffect(() => {
        const fetchProduct = async () => {
            try {
                const res = await fetch(`${API_URL}/api/public/product/${productId}`);
                if (!res.ok) throw new Error('Product not found');
                const data = await res.json();
                setProduct(data);
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };
        fetchProduct();
    }, [productId, API_URL]);

    if (loading) return <div className="flex h-screen items-center justify-center bg-slate-50"><Loader2 className="animate-spin text-[#10056E]" size={48}/></div>;
    if (error || !product) return (
        <div className="flex h-screen items-center justify-center bg-slate-50">
            <div className="text-center p-8">
                <Package size={64} className="mx-auto mb-4 text-slate-300"/>
                <h1 className="text-xl font-bold text-slate-700">Product Not Found</h1>
                <p className="text-sm text-slate-400 mt-2">The requested product could not be found.</p>
            </div>
        </div>
    );

    const isLowStock = product.quantity <= product.minLevel;

    return (
        <div className="min-h-screen bg-gradient-to-b from-[#10056E]/5 via-white to-slate-50" data-testid="public-product-page">
            <div className="max-w-md mx-auto px-4 py-6">
                {/* Header */}
                <div className="text-center mb-5">
                    <div className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[#10056E] text-white text-xs font-bold tracking-wide shadow-lg shadow-[#10056E]/20">
                        <Package size={14}/> ENHIVE Inventory
                    </div>
                    {product.workspace && <p className="text-[11px] text-slate-400 mt-2 font-medium">{product.workspace}</p>}
                </div>

                {/* Product Card */}
                <div className="bg-white rounded-3xl border border-slate-200/80 shadow-xl shadow-slate-200/40 overflow-hidden">
                    {/* Image */}
                    {product.image ? (
                        <div className="w-full aspect-square max-h-[360px] bg-slate-100">
                            <img src={product.image} className="w-full h-full object-cover" alt={product.name}/>
                        </div>
                    ) : (
                        <div className="w-full aspect-[4/3] bg-gradient-to-br from-[#10056E]/5 via-slate-50 to-white flex flex-col items-center justify-center gap-3">
                            <div className="w-20 h-20 rounded-2xl bg-white shadow-sm border border-slate-100 flex items-center justify-center">
                                <Package size={36} className="text-[#10056E]/30"/>
                            </div>
                            <span className="text-[10px] text-slate-300 font-medium uppercase tracking-widest">No Image</span>
                        </div>
                    )}

                    <div className="p-5 space-y-4">
                        {/* Product Name & SKU */}
                        <div>
                            <h1 className="text-lg font-bold text-slate-900 leading-snug">{product.name}</h1>
                            <p className="text-xs text-slate-400 font-mono mt-1">{product.sku}</p>
                        </div>

                        {/* Badges */}
                        <div className="flex flex-wrap gap-1.5">
                            <span className="px-2.5 py-1 rounded-full bg-[#10056E]/10 text-[#10056E] text-[11px] font-bold">{product.category}</span>
                            {product.binNo && <span className="px-2.5 py-1 rounded-full bg-slate-100 text-slate-600 text-[11px] font-semibold">BIN {product.binNo}</span>}
                            <span className="px-2.5 py-1 rounded-full bg-slate-100 text-slate-600 text-[11px] font-semibold">{product.unit}</span>
                        </div>

                        {/* Divider */}
                        <div className="border-t border-slate-100"></div>

                        {/* Stock Status */}
                        <div>
                            <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Stock Status</div>
                            <div className="flex items-center justify-between">
                                <span className={`px-4 py-2.5 rounded-2xl text-base font-bold ${isLowStock ? 'bg-rose-50 text-rose-600 ring-1 ring-rose-200' : 'bg-emerald-50 text-emerald-600 ring-1 ring-emerald-200'}`}>
                                    {product.quantity} {product.unit}
                                </span>
                                {isLowStock && (
                                    <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-rose-50 text-rose-600 text-[11px] font-bold">
                                        <AlertCircle size={13}/> Low Stock
                                    </div>
                                )}
                                {!isLowStock && (
                                    <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-50 text-emerald-600 text-[11px] font-bold">
                                        <CheckCircle size={13}/> In Stock
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                <p className="text-center text-[10px] text-slate-300 mt-6 font-medium">Powered by ENHIVE by POMEN</p>
            </div>
        </div>
    );
}

export default function InventorySystem() {
    const { language } = useLanguage();
    const t = getTranslation(language);
    
    const [user, setUser] = useState(null);
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [loading, setLoading] = useState(true);
    const [dataLoading, setDataLoading] = useState(false);
    
    // Data State
    const [projects, setProjects] = useState([]);
    const [activeProject, setActiveProject] = useState(null);
    const [inventory, setInventory] = useState([]);
    const [transactions, setTransactions] = useState([]);
    const [purchaseOrders, setPurchaseOrders] = useState([]);
    const [stockChecks, setStockChecks] = useState([]);
    const [projectUsers, setProjectUsers] = useState([]);
    const [suppliers, setSuppliers] = useState([]);
    const [priceLogs, setPriceLogs] = useState([]);

    // UI State
    const [activeTab, setActiveTab] = useState('dashboard');
    const [categoryFilter, setCategoryFilter] = useState('ALL');
    const [showPriceLogModal, setShowPriceLogModal] = useState(false);
    const [selectedItemForPriceLog, setSelectedItemForPriceLog] = useState(null);
    
    // Delete project modal states
    const [isDeleteProjectModalOpen, setIsDeleteProjectModalOpen] = useState(false);
    const [deleteProjectPassword, setDeleteProjectPassword] = useState('');
    const [deleteProjectError, setDeleteProjectError] = useState('');
    const [isDeletingProject, setIsDeletingProject] = useState(false);
    const [showLowStockOnly, setShowLowStockOnly] = useState(false);
    const [activeSettingsTab, setActiveSettingsTab] = useState('sku');
    const [toast, setToast] = useState(null);
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [aiInsights, setAiInsights] = useState(null);
    const [loadingInsights, setLoadingInsights] = useState(false);
    const [showAIInsightsModal, setShowAIInsightsModal] = useState(false);
    const [aiInsightsContent, setAiInsightsContent] = useState('');
    const [aiInsightsTitle, setAiInsightsTitle] = useState('');
    const [loadingForecast, setLoadingForecast] = useState(false);
    
    // Modals
    const [isProjectModalOpen, setIsProjectModalOpen] = useState(false);
    const [isItemModalOpen, setIsItemModalOpen] = useState(false);
    const [isStockModalOpen, setIsStockModalOpen] = useState(false);
    const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
    const [isStockCheckModalOpen, setIsStockCheckModalOpen] = useState(false);
    const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false); // NEW STATE
    const [isSupplierModalOpen, setIsSupplierModalOpen] = useState(false);
    const [editingSupplier, setEditingSupplier] = useState(null);
    const [isUserModalOpen, setIsUserModalOpen] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [allUsers, setAllUsers] = useState([]);
    
    const [confirmModal, setConfirmModal] = useState({ isOpen: false });
    const [receivePOModal, setReceivePOModal] = useState({ isOpen: false, po: null });

    // Selections & Filters
    const [selectedItem, setSelectedItem] = useState(null);
    const [selectedPOId, setSelectedPOId] = useState(null);
    const [activeStockCheck, setActiveStockCheck] = useState(null);
    
    const [searchTerm, setSearchTerm] = useState('');
    const [poViewMode, setPoViewMode] = useState('list');
    const [poListViewType, setPoListViewType] = useState('table'); // 'table' or 'calendar'
    const [poItems, setPoItems] = useState([]);
    const [currentPOItem, setCurrentPOItem] = useState({ id: '', qty: 1, cost: 0 });
    const [poActuals, setPoActuals] = useState({});
    const [poCalendarDate, setPoCalendarDate] = useState(new Date());
    const [editingPO, setEditingPO] = useState(null);
    
    const [stockAction, setStockAction] = useState('IN');
    const [previewImage, setPreviewImage] = useState('');
    const [stockProofImage, setStockProofImage] = useState('');
    const [viewingImage, setViewingImage] = useState(null);
    const [viewingProductId, setViewingProductId] = useState(null);
    const [productLogs, setProductLogs] = useState(null);
    const [loadingProductLogs, setLoadingProductLogs] = useState(false);
    const [productDetailTab, setProductDetailTab] = useState('overview');
    const [stockCheckSearch, setStockCheckSearch] = useState('');
    
    // Bulk Stock Out
    const [bulkStockOutMode, setBulkStockOutMode] = useState(false);
    const [bulkStockOutItems, setBulkStockOutItems] = useState([]);
    const [bulkProofImage, setBulkProofImage] = useState('');
    const [isDraggingProof, setIsDraggingProof] = useState(false);
    const [showBulkConfirm, setShowBulkConfirm] = useState(false);
    const [bulkConfirmData, setBulkConfirmData] = useState(null);
    const [bulkSelectedProduct, setBulkSelectedProduct] = useState('');
    const [bulkProofError, setBulkProofError] = useState(false);
    const [bulkAddQty, setBulkAddQty] = useState(1);    
    // Audit list filters
    const [auditListSearch, setAuditListSearch] = useState('');
    const [auditStatusFilter, setAuditStatusFilter] = useState('ALL'); // ALL, ONGOING, COMPLETED
    const [auditDateFilter, setAuditDateFilter] = useState('ALL_TIME');
    const [auditDateFrom, setAuditDateFrom] = useState('');
    const [auditDateTo, setAuditDateTo] = useState('');
    const [showDiscrepancyOnly, setShowDiscrepancyOnly] = useState(false);

    const [historyViewMode, setHistoryViewMode] = useState('log');
    const [historyFilterType, setHistoryFilterType] = useState('All');
    const [historySearchTerm, setHistorySearchTerm] = useState('');
    const [poFilterStatus, setPoFilterStatus] = useState('All');
    const [poFilterSupplier, setPoFilterSupplier] = useState('All');
    
    // Sejarah (History) state variables
    const [sejarahTab, setSejarahTab] = useState('log'); // 'log' or 'reports'
    const [transactionLogs, setTransactionLogs] = useState([]);
    const [transactionLogsMeta, setTransactionLogsMeta] = useState({ total: 0, page: 1, totalPages: 1 });
    const [sejarahSearch, setSejarahSearch] = useState('');
    const [sejarahTypeFilter, setSejarahTypeFilter] = useState('ALL');
    const [sejarahTimeFilter, setSejarahTimeFilter] = useState('ALL_TIME');
    const [sejarahDateFrom, setSejarahDateFrom] = useState('');
    const [sejarahDateTo, setSejarahDateTo] = useState('');
    const [sejarahCurrentPage, setSejarahCurrentPage] = useState(1);
    const [burstReportMonth, setBurstReportMonth] = useState(new Date().toISOString().slice(0, 7)); // YYYY-MM format
    const [selectedTransaction, setSelectedTransaction] = useState(null);
    const [isTransactionDetailOpen, setIsTransactionDetailOpen] = useState(false);
    const [loadingTransactions, setLoadingTransactions] = useState(false);
    
    // Quick Action item selector
    const [showQuickActionSelector, setShowQuickActionSelector] = useState(false);
    const [quickActionType, setQuickActionType] = useState(''); // 'IN' or 'OUT'
    const [quickActionSearch, setQuickActionSearch] = useState('');
    
    // Loading states for all forms
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [poDateFilter, setPoDateFilter] = useState('ALL_TIME');
    const [customDateFrom, setCustomDateFrom] = useState('');
    const [customDateTo, setCustomDateTo] = useState('');
    const [poFilterDiscrepancy, setPoFilterDiscrepancy] = useState(false);
    const [receiveDate, setReceiveDate] = useState('');
    const [selectedProjectIcon, setSelectedProjectIcon] = useState('Package');
    const [auditReportModal, setAuditReportModal] = useState({ isOpen: false, audit: null });
    const [auditConfirmModal, setAuditConfirmModal] = useState({ isOpen: false, check: null, summary: null });
    const [isFinalizingAudit, setIsFinalizingAudit] = useState(false);

    // Initial Load
    useEffect(() => {
        const init = async () => {
            try {
                const currentUser = await auth.me();
                setUser(currentUser);
                setIsLoggedIn(true);
                await fetchData();
            } catch (e) { setIsLoggedIn(false); } finally { setLoading(false); }
        };
        init();
    }, []);

    const fetchData = async () => {
        try {
            const [p, i, t, u, s, po, sc] = await Promise.all([
                db.getProjects(), db.getInventory(), db.getTransactions(), db.getUsers(), db.getSuppliers(), db.getPOs(), db.getStockChecks()
            ]);
            setProjects(p); setInventory(i); setTransactions(t); setProjectUsers(u); setSuppliers(s); setPurchaseOrders(po); setStockChecks(sc);
            
            // Extract price logs from transactions
            const logs = t
                .filter(tx => tx.type === 'PRICE_CHANGE')
                .map(tx => {
                    try {
                        return {
                            ...tx,
                            metadata: tx.metadata ? JSON.parse(tx.metadata) : null
                        };
                    } catch {
                        return tx;
                    }
                })
                .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
            setPriceLogs(logs);
            
            if (activeProject) {
                const updated = p.find(proj => proj.id === activeProject.id);
                if (updated && JSON.stringify(updated) !== JSON.stringify(activeProject)) {
                    setActiveProject(updated);
                }
            }
        } catch (e) { showToast(t('common.error'), "error"); }
    };

    useEffect(() => {
        if (!isLoggedIn) return;
        const interval = setInterval(fetchData, 30000); // Reduced from 10s to 30s for better performance
        return () => clearInterval(interval);
    }, [isLoggedIn, activeProject]);

    // Derived Data
    const projectInventory = useMemo(() => activeProject ? inventory.filter(i => i.projectId === activeProject.id) : [], [inventory, activeProject]);
    const projectTransactions = useMemo(() => activeProject ? transactions.filter(t => t.projectId === activeProject.id).sort((a,b) => new Date(b.timestamp) - new Date(a.timestamp)) : [], [transactions, activeProject]);
    const projectPOs = useMemo(() => activeProject ? purchaseOrders.filter(p => p.projectId === activeProject.id).sort((a,b) => new Date(b.createdAt) - new Date(a.createdAt)) : [], [purchaseOrders, activeProject]);
    const projectStockChecks = useMemo(() => activeProject ? stockChecks.filter(s => s.projectId === activeProject.id).sort((a,b) => new Date(b.startDate) - new Date(a.startDate)) : [], [stockChecks, activeProject]);
    const projectSuppliers = useMemo(() => activeProject ? suppliers.filter(s => s.projectId === activeProject.id) : [], [suppliers, activeProject]);
    const projectPriceLogs = useMemo(() => activeProject ? priceLogs.filter(pl => pl.projectId === activeProject.id).slice(0, 10) : [], [priceLogs, activeProject]);
    const currentAppUser = useMemo(() => projectUsers.find(u => u.email === user?.email) || user, [projectUsers, user]);
    const projectCategories = useMemo(() => {
        const cats = activeProject?.categories || INITIAL_CATEGORIES;
        return [...cats].sort((a, b) => a.localeCompare(b, 'en-MY'));
    }, [activeProject]);
    
    // Get current user with project role
    const currentProjectRole = useMemo(() => {
        if (!user || !activeProject) return null;
        if (user.isSuperAdmin) return 'Super Admin';
        const access = user.access?.find(a => a.projectId === activeProject.id);
        return access?.role || null;
    }, [user, activeProject]);
    
    // Permission helpers
    const can = useMemo(() => {
        const role = currentProjectRole;
        const hideFinancials = user?.hideFinancials || false;
        
        return {
            // Role-based permissions
            viewFinancials: !hideFinancials,
            accessSettings: role === 'Super Admin' || role === 'Admin',
            manageUsers: role === 'Super Admin',
            createProject: user?.isSuperAdmin,
            deleteProject: user?.isSuperAdmin,
            accessDashboard: role !== 'Staff',
            accessPO: role !== 'Staff',
            accessStockCheck: role !== 'Staff',
            accessHistory: role !== 'Staff',
            doStockIn: role !== 'Staff',
            doStockOut: true, // All roles can do stock out
            
            // Derived permissions
            viewPrices: !hideFinancials,
            viewCosts: !hideFinancials,
            viewTotals: !hideFinancials,
            editPrices: !hideFinancials && (role === 'Super Admin' || role === 'Admin'),
        };
    }, [currentProjectRole, user]);
    
    // Filter available tabs based on role
    const availableTabs = useMemo(() => {
        const role = currentProjectRole;
        if (role === 'Staff') {
            return ['inventory']; // Staff only sees Operasi Stok
        }
        const tabs = ['dashboard', 'inventory', 'purchase_orders', 'stock_checks', 'transactions'];
        if (can.accessSettings) {
            tabs.push('settings');
        }
        return tabs;
    }, [currentProjectRole, can.accessSettings]);
    
    // Auto-redirect if on restricted tab
    useEffect(() => {
        if (activeProject && !availableTabs.includes(activeTab)) {
            setActiveTab(availableTabs[0]);
        }
    }, [activeProject, activeTab, availableTabs]);
    
    // Filter projects based on user access
    const accessibleProjects = useMemo(() => {
        if (!currentAppUser) return [];
        // Super Admin sees all projects
        if (currentAppUser.isSuperAdmin) return projects;
        // Regular users only see projects they have access to
        if (!currentAppUser.access || currentAppUser.access.length === 0) return [];
        return projects.filter(p => currentAppUser.access.some(a => a.projectId === p.id));
    }, [projects, currentAppUser]);
    
    // Auto-navigate to project if user has access to only one project
    useEffect(() => {
        if (!isLoggedIn || !currentAppUser || activeProject) return;
        if (currentAppUser.isSuperAdmin) return; // Super Admin chooses manually
        if (accessibleProjects.length === 1) {
            // User has access to only one project, auto-navigate
            setActiveProject(accessibleProjects[0]);
        }
    }, [isLoggedIn, currentAppUser, accessibleProjects, activeProject]);

    const canSwitchProject = accessibleProjects.length > 1 || currentAppUser?.isSuperAdmin;

    // Sync selected icon with active project
    useEffect(() => {
        if (activeProject && activeProject.icon) {
            setSelectedProjectIcon(activeProject.icon);
        }
    }, [activeProject]);

    // Debounce search term for better performance
    const debouncedSearchTerm = useDebounce(searchTerm, 300);

    const filteredInventory = useMemo(() => 
        projectInventory.filter(item => 
            item.name.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) || 
            item.sku.toLowerCase().includes(debouncedSearchTerm.toLowerCase())
        ), 
    [projectInventory, debouncedSearchTerm]);
    
    const lowStockItems = useMemo(() => projectInventory.filter(item => parseInt(item.quantity) <= parseInt(item.minLevel)), [projectInventory]);
    
    // Calculate monthly burst violations (items with stock OUT exceeding monthly limit)
    const burstViolations = useMemo(() => {
        const currentMonth = new Date().getMonth();
        const currentYear = new Date().getFullYear();
        
        // Build a map of itemId -> monthlyOut for performance
        const monthlyOutMap = new Map();
        projectTransactions.forEach(tx => {
            if (tx.type === 'OUT') {
                const txDate = new Date(tx.timestamp);
                if (txDate.getMonth() === currentMonth && txDate.getFullYear() === currentYear) {
                    const current = monthlyOutMap.get(tx.itemId) || 0;
                    monthlyOutMap.set(tx.itemId, current + tx.quantity);
                }
            }
        });
        
        // Filter and map in one pass
        return projectInventory
            .filter(item => item.burstLimit && item.burstLimit > 0)
            .map(item => {
                const monthlyOut = monthlyOutMap.get(item.id) || 0;
                return { item, monthlyOut, exceeded: monthlyOut - item.burstLimit };
            })
            .filter(({ exceeded }) => exceeded > 0)
            .map(({ item, monthlyOut, exceeded }) => ({ ...item, monthlyOut, exceeded }));
    }, [projectInventory, projectTransactions]);
    
    const totalStockValue = useMemo(() => projectInventory.reduce((acc, item) => acc + (parseInt(item.quantity) * (parseFloat(item.price) || 0)), 0), [projectInventory]);
    const selectedPO = useMemo(() => projectPOs.find(p => p.id === selectedPOId), [projectPOs, selectedPOId]);

    const filteredPOs = useMemo(() => {
        let filtered = projectPOs;
        
        // Filter by status
        if (poFilterStatus !== 'All') filtered = filtered.filter(d => d.status === poFilterStatus);
        
        // Filter by supplier
        if (poFilterSupplier !== 'All') filtered = filtered.filter(d => d.supplier === poFilterSupplier);
        
        // Filter by discrepancy
        if (poFilterDiscrepancy) {
            filtered = filtered.filter(po => {
                return po.hasDiscrepancy || po.items?.some(i => i.receivedQty !== undefined && i.receivedQty !== i.quantity);
            });
        }
        
        // Filter by date range
        if (poDateFilter !== 'ALL_TIME') {
            const now = new Date();
            const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
            
            filtered = filtered.filter(po => {
                const poDate = new Date(po.date);
                const poDateOnly = new Date(poDate.getFullYear(), poDate.getMonth(), poDate.getDate());
                
                switch (poDateFilter) {
                    case 'TODAY':
                        return poDateOnly.getTime() === today.getTime();
                    case 'YESTERDAY':
                        const yesterday = new Date(today);
                        yesterday.setDate(yesterday.getDate() - 1);
                        return poDateOnly.getTime() === yesterday.getTime();
                    case 'THIS_WEEK':
                        const startOfWeek = new Date(today);
                        startOfWeek.setDate(today.getDate() - today.getDay());
                        return poDateOnly >= startOfWeek && poDateOnly <= today;
                    case 'LAST_WEEK':
                        const lastWeekStart = new Date(today);
                        lastWeekStart.setDate(today.getDate() - today.getDay() - 7);
                        const lastWeekEnd = new Date(lastWeekStart);
                        lastWeekEnd.setDate(lastWeekStart.getDate() + 6);
                        return poDateOnly >= lastWeekStart && poDateOnly <= lastWeekEnd;
                    case 'LAST_7_DAYS':
                        const last7Days = new Date(today);
                        last7Days.setDate(today.getDate() - 7);
                        return poDateOnly >= last7Days && poDateOnly <= today;
                    case 'THIS_MONTH':
                        return poDate.getMonth() === now.getMonth() && poDate.getFullYear() === now.getFullYear();
                    case 'LAST_MONTH':
                        const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
                        return poDate.getMonth() === lastMonth.getMonth() && poDate.getFullYear() === lastMonth.getFullYear();
                    case 'LAST_30_DAYS':
                        const last30Days = new Date(today);
                        last30Days.setDate(today.getDate() - 30);
                        return poDateOnly >= last30Days && poDateOnly <= today;
                    case 'CUSTOM_RANGE':
                        if (customDateFrom && customDateTo) {
                            const fromDate = new Date(customDateFrom);
                            const toDate = new Date(customDateTo);
                            return poDateOnly >= fromDate && poDateOnly <= toDate;
                        }
                        return true;
                    default:
                        return true;
                }
            });
        }
        
        return filtered;
    }, [projectPOs, poFilterStatus, poFilterSupplier, poFilterDiscrepancy, poDateFilter, customDateFrom, customDateTo]);

    const filteredTransactions = useMemo(() => {
        return projectTransactions.filter(tx => {
            if (historyFilterType !== 'All' && tx.type !== historyFilterType) return false;
            if (historySearchTerm) {
                const term = historySearchTerm.toLowerCase();
                const match = tx.itemName?.toLowerCase().includes(term) || tx.reason?.toLowerCase().includes(term);
                if (!match) return false;
            }
            return true;
        });
    }, [projectTransactions, historyFilterType, historySearchTerm]);

    // Handlers
    const showToast = useCallback((msg, type = 'success') => { 
        setToast({ message: msg, type }); 
        setTimeout(() => setToast(null), 3000); 
    }, []);
    
    const requestConfirm = useCallback((title, message, onConfirm) => { 
        setConfirmModal({ 
            isOpen: true, 
            title, 
            message, 
            onConfirm: async () => { 
                await onConfirm(); 
                // Modal will close itself after confirming
                setConfirmModal({ isOpen: false }); 
            }
        }); 
    }, []);
    
    const handleLogin = useCallback(async (u) => { 
        setUser(u); 
        setIsLoggedIn(true); 
        setDataLoading(true);
        try {
            await fetchData();
        } finally {
            setDataLoading(false);
        }
    }, []);
    
    const handleLogout = () => { 
        requestConfirm(
            t('auth.logout'), 
            t('auth.logoutConfirm'), 
            () => {
                setAuthToken(null); 
                setUser(null); 
                setIsLoggedIn(false); 
                setActiveProject(null);
            }
        ); 
    };

    const handleCreateProject = async (e) => { 
        e.preventDefault(); 
        setIsSubmitting(true);
        try { 
            await db.createProject({ 
                name: e.target.name.value, 
                description: e.target.description.value, 
                createdBy: user.email, 
                categories: INITIAL_CATEGORIES 
            }); 
            fetchData(); 
            setIsProjectModalOpen(false); 
            showToast(t('common.projectCreated')); 
        } catch(e) { 
            showToast(t("common.error"), "error"); 
        } finally {
            setIsSubmitting(false);
        }
    };
    const handleDeleteProject = async () => {
        setDeleteProjectError('');
        setIsDeletingProject(true);
        
        try {
            // Verify password
            const response = await api.post('/auth/verify-password', { password: deleteProjectPassword });
            
            if (response.status !== 200) {
                setDeleteProjectError('Kata laluan tidak sah');
                setIsDeletingProject(false);
                return;
            }
            
            // Show final confirmation
            requestConfirm(
                t("settings.deleteConfirmTitle"),
                `${language === "en" ? `Are you sure you want to delete project "${activeProject.name}"? All data will be permanently lost.` : `Adakah anda benar-benar pasti mahu memadam projek "${activeProject.name}"? Semua data akan hilang selama-lamanya.`}`,
                async () => {
                    try {
                        await db.deleteProject(activeProject.id);
                        setProjects(projects.filter(pr => pr.id !== activeProject.id));
                        setActiveProject(null);
                        setIsDeleteProjectModalOpen(false);
                        setDeleteProjectPassword('');
                        showToast(t('common.projectDeleted'));
                    } catch (err) {
                        showToast(t('common.projectDeleteError'), "error");
                        setIsDeletingProject(false);
                    }
                }
            );
        } catch (err) {
            setDeleteProjectError(t('common.errorVerifyingPassword'));
            setIsDeletingProject(false);
        }
    };

    const handleAddItem = async (e) => { 
        e.preventDefault(); 
        setIsSubmitting(true);
        const data = { 
            projectId: activeProject.id, 
            name: e.target.name.value, 
            sku: e.target.sku.value, 
            price: parseFloat(e.target.price.value), 
            minLevel: parseInt(e.target.minLevel.value), 
            burstLimit: parseInt(e.target.burstLimit.value) || 0,
            unit: e.target.unit.value, 
            category: e.target.category.value,
            binNo: e.target.binNo.value || ''
        };
        
        // Only update image if a new one was uploaded
        if (previewImage) {
            data.image = previewImage;
        } else if (selectedItem && selectedItem.image) {
            // Maintain existing image when editing
            data.image = selectedItem.image;
        } else {
            data.image = '';
        }
        
        try { 
            if (selectedItem) {
                // Check if price changed - ensure both are numbers for accurate comparison
                const oldPrice = parseFloat(selectedItem.price);
                const newPrice = parseFloat(data.price);
                
                // Update the item first
                await db.updateItem(selectedItem.id, data);
                
                // Then log price change if price actually changed (with small tolerance for floating point)
                if (Math.abs(oldPrice - newPrice) > 0.001) {
                    try {
                        const priceLog = {
                            projectId: activeProject.id,
                            itemId: selectedItem.id,
                            itemName: data.name,
                            sku: data.sku,
                            oldPrice: oldPrice,
                            newPrice: newPrice,
                            changeType: 'SKU_EDIT',
                            changedBy: user.id,
                            changedByName: user.name,
                            timestamp: new Date().toISOString()
                        };
                        
                        // Create transaction for price log
                        await db.createTransaction({
                            projectId: activeProject.id,
                            itemId: selectedItem.id,
                            itemName: data.name,
                            type: 'PRICE_CHANGE',
                            transactionType: 'PRICE_CHANGE',
                            quantity: 0,
                            reason: `${t('inventory.priceChangedFrom')}${oldPrice.toFixed(2)} ${t('inventory.priceChangedTo')}${newPrice.toFixed(2)}`,
                            user: user.email,
                            userName: user.name,
                            referenceId: selectedItem.id,
                            referenceName: 'SKU_EDIT',
                            metadata: priceLog
                        });
                        
                        console.log("✅ Price change logged:", { oldPrice, newPrice, item: data.name });
                    } catch (priceLogError) {
                        console.error("❌ Error logging price change:", priceLogError);
                        // Continue even if price log fails
                    }
                }
            } else { 
                data.quantity = 0; 
                await db.createItem(data); 
            }
            
            // Refresh data
            await fetchData();
            
            // Close modal and reset states
            setIsItemModalOpen(false);
            setSelectedItem(null);
            setPreviewImage('');
            
            showToast(t('common.productSaved'));
        } catch(e) { 
            console.error("Error saving item:", e);
            showToast(t('common.productSaveError'), "error"); 
        } finally {
            setIsSubmitting(false);
        }
    };
    const handleStockAdjustment = async (e) => { 
        e.preventDefault(); 
        
        // Validate image upload
        if (!stockProofImage) {
            showToast(t('inventory.pleaseUploadProof'), "error");
            return;
        }
        
        setIsSubmitting(true);
        const qty = parseInt(e.target.quantity.value); 
        
        if (stockAction === 'OUT' && qty > selectedItem.quantity) { 
            showToast(t('inventory.insufficientStock'), "error"); 
            setIsSubmitting(false);
            return; 
        } 
        
        const newQty = stockAction === 'IN' ? selectedItem.quantity + qty : selectedItem.quantity - qty; 
        
        try { 
            await db.updateItem(selectedItem.id, { quantity: newQty }); 
            await db.createTransaction({ 
                projectId: activeProject.id, 
                itemId: selectedItem.id, 
                itemName: selectedItem.name, 
                type: stockAction, 
                transactionType: stockAction === 'IN' ? 'STOK_MASUK' : 'STOK_KELUAR',
                quantity: qty, 
                reason: e.target.reason.value, 
                user: user.email, 
                userName: user.name,
                picName: e.target.picName.value, 
                picContact: e.target.picContact.value, 
                proofImage: stockProofImage 
            }); 
            fetchData(); 
            setIsStockModalOpen(false); 
            setStockProofImage(''); 
            showToast(t('inventory.stockUpdated')); 
        } catch(e) { 
            showToast(t("common.error"), "error"); 
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleBulkStockOut = async (e) => {
        e.preventDefault();
        if (bulkStockOutItems.length === 0) {
            showToast(t('inventory.noItemsAdded'), "error");
            return;
        }
        if (!bulkProofImage) {
            setBulkProofError(true);
            return;
        }
        // Validate stock before showing confirmation
        for (const item of bulkStockOutItems) {
            const invItem = inventory.find(i => i.id === item.id);
            if (!invItem) continue;
            if (item.qty > invItem.quantity) {
                showToast(`${t('common.insufficientStockFor')} ${item.name}`, "error");
                return;
            }
        }
        const reason = e.target.reason.value;
        const picName = e.target.picName?.value || '';
        const picContact = e.target.picContact?.value || '';
        setBulkConfirmData({ reason, picName, picContact });
        setShowBulkConfirm(true);
    };

    const executeBulkStockOut = async () => {
        if (!bulkConfirmData) return;
        setShowBulkConfirm(false);
        setIsSubmitting(true);
        const { reason, picName, picContact } = bulkConfirmData;
        try {
            for (const item of bulkStockOutItems) {
                const invItem = inventory.find(i => i.id === item.id);
                const newQty = invItem.quantity - item.qty;
                await db.updateItem(item.id, { quantity: newQty });
                await db.createTransaction({
                    projectId: activeProject.id,
                    itemId: item.id,
                    itemName: item.name,
                    type: 'OUT',
                    transactionType: 'STOK_KELUAR',
                    quantity: item.qty,
                    reason: reason,
                    user: user.email,
                    userName: user.name,
                    picName: picName,
                    picContact: picContact,
                    proofImage: bulkProofImage
                });
            }
            fetchData();
            setBulkStockOutMode(false);
            setBulkStockOutItems([]);
            setBulkProofImage('');
            setBulkConfirmData(null);
            setBulkSelectedProduct('');
            setBulkProofError(false);
            showToast(`${bulkStockOutItems.length} ${language === 'en' ? 'items stock out recorded' : 'item stok keluar berjaya direkod'}`);
        } catch (err) {
            console.error("Bulk stock out error:", err);
            showToast(t('inventory.bulkOutError'), "error");
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCreatePO = async (e) => { 
        e.preventDefault(); 
        if (poItems.length === 0) { 
            showToast(t("inventory.noItemsAdded"), "error"); 
            return; 
        } 
        
        setIsSubmitting(true);
        const poData = { 
            projectId: activeProject.id, 
            supplier: e.target.supplier.value, 
            date: e.target.date.value, 
            notes: e.target.notes.value, 
            items: poItems, 
            totalCost: poItems.reduce((acc, i) => acc + (i.quantity * i.cost), 0), 
            status: 'Belum Selesai', 
            history: [] 
        }; 
        
        try {
            // Check for price changes and update master data
            for (const poItem of poItems) {
                const masterItem = projectInventory.find(i => i.id === poItem.id);
                if (masterItem && masterItem.price !== poItem.cost) {
                    const oldPrice = masterItem.price;
                    const newPrice = poItem.cost;
                    
                    try {
                        // Update master data price
                        await db.updateItem(masterItem.id, { price: newPrice });
                        
                        // Log price change
                        const priceLog = {
                            projectId: activeProject.id,
                            itemId: masterItem.id,
                            itemName: masterItem.name,
                            sku: masterItem.sku,
                            oldPrice: oldPrice,
                            newPrice: newPrice,
                            changeType: 'PO_CREATE',
                            changedBy: user.id,
                            changedByName: user.name,
                            timestamp: new Date().toISOString(),
                            poSupplier: poData.supplier
                        };
                        
                        await db.createTransaction({
                            projectId: activeProject.id,
                            itemId: masterItem.id,
                            itemName: masterItem.name,
                            type: 'PRICE_CHANGE',
                            transactionType: 'PRICE_CHANGE',
                            quantity: 0,
                            reason: `${t('inventory.priceChangedFrom')}${oldPrice.toFixed(2)} ${t('inventory.priceChangedTo')}${newPrice.toFixed(2)} (PO: ${poData.supplier})`,
                            user: user.email,
                            userName: user.name,
                            referenceId: null,
                            referenceName: 'PO_CREATE',
                            metadata: priceLog
                        });
                    } catch (priceError) {
                        console.error("Error updating price for item:", masterItem.name, priceError);
                        // Continue with PO creation even if price update fails
                    }
                }
            }
            
            if (editingPO) {
                await db.updatePO(editingPO.id, poData);
                fetchData();
                setPoViewMode('list');
                setPoItems([]);
                setEditingPO(null);
                showToast(t('po.poUpdated'));
            } else {
                await db.createPO(poData); 
                fetchData(); 
                setPoViewMode('list'); 
                setPoItems([]); 
                showToast(t('po.poCreated')); 
            }
        } catch(e) {
            console.error("Error saving PO:", e);
            showToast(editingPO ? t('po.poUpdateError') : t('po.poCreateError'), "error"); 
        } finally {
            setIsSubmitting(false);
        }
    };
    
    const handleCancelPO = async (poId) => {
        try {
            await db.updatePO(poId, { status: 'Batal' });
            fetchData();
            showToast(language === 'en' ? "PO cancelled" : "PO berjaya dibatalkan");
        } catch(e) {
            showToast(t("po.poCancelError"), "error");
        }
    };

    const handleDeletePO = async (poId) => {
        try {
            await db.deletePO(poId);
            fetchData();
            setSelectedPOId(null);
            showToast(language === 'en' ? "PO deleted" : "PO berjaya dipadam");
        } catch(e) {
            showToast(t("po.poDeleteError"), "error");
        }
    };
    
    const handleEditPO = (po) => {
        setEditingPO(po);
        setPoItems(po.items.map(item => ({
            ...item,
            quantity: item.quantity,
            cost: item.cost
        })));
        setPoViewMode('create');
        setSelectedPOId(null);
    };
    
    const handleConfirmReceivePO = async (e) => { 
        e.preventDefault(); 
        setIsSubmitting(true);
        const { po } = receivePOModal; 
        const updatedItems = po.items.map(item => ({ 
            ...item, 
            receivedQty: parseInt(poActuals[item.id]) || 0 
        })); 
        let hasDiscrepancy = updatedItems.some(i => i.receivedQty !== i.quantity); 
        
        try { 
            for (const item of updatedItems) { 
                if (item.receivedQty > 0) { 
                    const invItem = inventory.find(i => i.id === item.id); 
                    if (invItem) await db.updateItem(item.id, { 
                        quantity: invItem.quantity + item.receivedQty 
                    }); 
                    await db.createTransaction({ 
                        projectId: activeProject.id, 
                        itemId: item.id, 
                        itemName: item.name, 
                        type: 'IN', 
                        transactionType: 'PO_DITERIMA',
                        quantity: item.receivedQty, 
                        reason: `PO: ${po.supplier}`, 
                        user: user.email,
                        userName: user.name,
                        referenceId: po.id,
                        referenceName: po.supplier
                    }); 
                } 
            } 
            await db.updatePO(po.id, { 
                status: 'Selesai', 
                items: updatedItems, 
                hasDiscrepancy 
            }); 
            fetchData(); 
            setReceivePOModal({ isOpen: false, po: null }); 
            setSelectedPOId(null); 
            showToast(t('po.poCompleted')); 
        } catch(e) { 
            showToast(t("common.error"), "error"); 
        } finally {
            setIsSubmitting(false);
        }
    };
    const handleStartStockCheck = async (e) => { 
        e.preventDefault(); 
        setIsSubmitting(true);
        const check = { 
            projectId: activeProject.id, 
            status: 'In Progress', 
            auditorName: e.target.auditorName.value, 
            notes: e.target.notes.value, 
            startDate: e.target.auditDate.value, 
            items: projectInventory.map(i => ({ 
                itemId: i.id, 
                name: i.name, 
                sku: i.sku, 
                category: i.category,
                systemQty: i.quantity, 
                actualQty: i.quantity, // Pre-fill with system quantity
                confirmed: false, // Not yet verified by user
                unit: i.unit, 
                image: i.image 
            })), 
            progress: 0, // 0% because nothing confirmed yet
            createdBy: user.id 
        }; 
        try { 
            const newCheck = await db.createStockCheck(check); 
            fetchData(); 
            setIsStockCheckModalOpen(false); 
            setActiveStockCheck(newCheck); 
            showToast(t('stockCheck.auditStarted')); 
        } catch(e) { 
            showToast(t("common.error"), "error"); 
        } finally {
            setIsSubmitting(false);
        }
    };
    const handleFinalizeStockCheck = async (check) => {
        // Calculate summary for confirmation
        const finalizedItems = check.items.map(i => {
            const actual = i.actualQty !== null ? i.actualQty : i.systemQty;
            return { ...i, actualQty: actual, status: actual === i.systemQty ? 'Matched' : 'Adjusted' };
        });
        
        const itemsToAdjust = finalizedItems.filter(item => item.status === 'Adjusted');
        const matchedItems = finalizedItems.filter(item => item.status === 'Matched');
        
        const summary = {
            totalItems: finalizedItems.length,
            matched: matchedItems.length,
            toAdjust: itemsToAdjust.length,
            adjustments: itemsToAdjust.map(item => ({
                name: item.name,
                sku: item.sku,
                systemQty: item.systemQty,
                actualQty: item.actualQty,
                diff: item.actualQty - item.systemQty,
                unit: item.unit
            }))
        };
        
        // Show confirmation modal
        setAuditConfirmModal({ isOpen: true, check, summary });
    };
    
    const confirmFinalizeAudit = async () => {
        const { check, summary } = auditConfirmModal;
        
        setIsFinalizingAudit(true);
        
        try {
            const finalizedItems = check.items.map(i => {
                const actual = i.actualQty !== null ? i.actualQty : i.systemQty;
                return { ...i, actualQty: actual, status: actual === i.systemQty ? 'Matched' : 'Adjusted' };
            });
            
            // Perform adjustments
            for (const item of finalizedItems) {
                if (item.status === 'Adjusted') {
                    const diff = item.actualQty - item.systemQty;
                    await db.updateItem(item.itemId, { quantity: item.actualQty });
                    await db.createTransaction({
                        projectId: activeProject.id,
                        itemId: item.itemId,
                        itemName: item.name,
                        type: diff > 0 ? 'IN' : 'OUT',
                        transactionType: 'PELARASAN_AUDIT',
                        quantity: Math.abs(diff),
                        reason: `Pelarasan Audit: ${check.auditorName}`,
                        user: user.email,
                        userName: user.name,
                        referenceId: check.id,
                        referenceName: check.auditorName
                    });
                }
            }
            
            await db.updateStockCheck(check.id, {
                status: 'Completed',
                items: finalizedItems,
                completedDate: new Date(),
                totalAdjustments: summary.toAdjust,
                progress: 100
            });
            
            fetchData();
            setActiveStockCheck(null);
            setAuditConfirmModal({ isOpen: false, check: null, summary: null });
            showToast(t('stockCheck.auditCompleted'));
        } catch (e) {
            console.error('Finalize error:', e);
            showToast(t('stockCheck.completeAuditError'), "error");
        } finally {
            setIsFinalizingAudit(false);
        }
    };
    const handleSaveProfile = async (formData) => { 
        const data = { 
            name: formData.name, 
            avatar: formData.avatar || '' 
        }; 
        if (formData.newPassword) {
            if (formData.newPassword !== formData.confirmPassword) {
                showToast(language === 'en' ? 'Passwords do not match' : 'Kata laluan tidak sepadan', "error");
                return;
            }
            data.password = formData.newPassword;
        }
        try { 
            await api.put(`/users/${currentAppUser.id}`, data); 
            setIsProfileModalOpen(false); 
            showToast(t('auth.profileUpdated')); 
            window.location.reload(); 
        } catch(err) { 
            showToast(t("common.error") + ": " + (err.response?.data?.detail || err.message), "error"); 
        } 
    };
    
    const handleSaveUser = async (userData) => {
        try {
            if (editingUser) {
                await db.updateUser(editingUser.id, userData);
                showToast(t("common.userUpdated"));
            } else {
                await db.createUser(userData);
                showToast(t("common.userCreated"));
            }
            fetchData();
            setIsUserModalOpen(false);
            setEditingUser(null);
        } catch(err) {
            showToast(t("common.error") + ": " + (err.response?.data?.detail || err.message), "error");
        }
    };
    
    const handleDeleteUser = async (userId) => {
        requestConfirm(t("common.deleteUser"), t("common.deleteUserConfirm"), async () => {
            try {
                await db.deleteUser(userId);
                fetchData();
                showToast(t("common.userDeleted"));
            } catch(err) {
                showToast(t("common.error"), "error");
            }
        });
    };
    const handleAddCategory = async (e) => { 
        e.preventDefault(); 
        const newCat = e.target.newCategory.value.trim(); 
        if (newCat && !projectCategories.includes(newCat)) { 
            const updatedCategories = [...projectCategories, newCat].sort((a, b) => a.localeCompare(b, 'en-MY')); 
                console.log("App Version: 1.0.5 (GET Login)");
            await db.updateProject(activeProject.id, { categories: updatedCategories }); 
            setActiveProject(prev => ({ ...prev, categories: updatedCategories })); // Optimistic update
            fetchData(); 
            showToast(t("settings.categoryAdded")); 
            e.target.reset(); 
        } 
    };
    const handleDeleteCategory = async (cat) => { 
        requestConfirm(t("settings.deleteCategory"), `${t("settings.deleteCategoryConfirm")} "${cat}"?`, async () => { 
            const updatedCategories = projectCategories.filter(c => c !== cat).sort((a, b) => a.localeCompare(b, 'en-MY')); 
            await db.updateProject(activeProject.id, { categories: updatedCategories }); 
            setActiveProject(prev => ({ ...prev, categories: updatedCategories })); // Optimistic update
            fetchData(); 
            showToast(t("settings.categoryDeleted")); 
        }); 
    };
    const handleAddSupplier = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        const data = {
            projectId: activeProject.id,
            name: e.target.name.value,
            picName: e.target.picName.value,
            picPhone: e.target.picPhone.value,
            email: e.target.email.value,
            paymentTerms: e.target.paymentTerms.value,
            addressLine1: e.target.addressLine1.value,
            addressLine2: e.target.addressLine2.value,
            postcode: e.target.postcode.value,
            state: e.target.state.value,
            // Combine address fields for backward compatibility
            address: `${e.target.addressLine1.value}${e.target.addressLine2.value ? ', ' + e.target.addressLine2.value : ''}, ${e.target.postcode.value || ''} ${e.target.state.value || ''}`
        };
        try {
            if (editingSupplier) {
                await db.updateSupplier(editingSupplier.id, data);
                showToast(t("settings.supplierUpdated"));
                fetchData();
            } else {
                await db.createSupplier(data);
                showToast(t("settings.supplierAdded"));
                // Fetch data first to update the suppliers list
                await fetchData();
                // Auto-select the new supplier in PO form after data is fetched
                if (poViewMode === 'create' || poViewMode === 'edit') {
                    setTimeout(() => {
                        const supplierSelect = document.querySelector('select[name="supplier"]');
                        if (supplierSelect) {
                            supplierSelect.value = data.name;
                        }
                    }, 300);
                }
            }
            setIsSupplierModalOpen(false);
            setEditingSupplier(null);
        } catch(e) { 
            showToast(t("settings.supplierError"), "error"); 
        } finally {
            setIsSubmitting(false);
        }
    };

    // --- Fetch Product Logs ---
    const fetchProductLogs = async (itemId) => {
        setLoadingProductLogs(true);
        try {
            const response = await api.get(`/product/${itemId}/logs`);
            setProductLogs(response.data);
        } catch (err) {
            console.error('Error fetching product logs:', err);
            setProductLogs({ stockTransactions: [], priceChanges: [], poLogs: [], burstReport: [] });
        } finally {
            setLoadingProductLogs(false);
        }
    };

    const openProductDetail = (item) => {
        setViewingProductId(item.id);
        setProductDetailTab('overview');
        fetchProductLogs(item.id);
    };

    // --- Product Detail View ---
    const renderProductDetail = () => {
        const item = projectInventory.find(i => i.id === viewingProductId);
        if (!item) return null;

        const publicUrl = `${window.location.origin}?product=${item.id}`;
        const isLowStock = item.quantity <= item.minLevel;

        // Usage since last PO
        const completedPOsWithItem = projectPOs
            .filter(po => po.status === 'Selesai' && po.items?.some(i => i.id === item.id))
            .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        let usageSinceLastPO = null;
        if (completedPOsWithItem.length > 0) {
            const lastPO = completedPOsWithItem[0];
            const poItem = lastPO.items.find(i => i.id === item.id);
            const poDate = new Date(lastPO.createdAt);
            const outTxs = projectTransactions.filter(tx => tx.itemId === item.id && tx.type === 'OUT' && new Date(tx.timestamp) >= poDate);
            const totalUsed = outTxs.reduce((sum, tx) => sum + tx.quantity, 0);
            usageSinceLastPO = { totalUsed, supplier: lastPO.supplier, receivedQty: poItem?.receivedQty || poItem?.quantity || 0, poDate };
        }

        const handleDownloadQR = () => {
            const svg = document.getElementById('product-qr-code');
            if (!svg) return;
            const svgData = new XMLSerializer().serializeToString(svg);
            const canvas = document.createElement('canvas');
            canvas.width = 400; canvas.height = 400;
            const ctx = canvas.getContext('2d');
            const img = new Image();
            img.onload = () => { ctx.drawImage(img, 0, 0, 400, 400); const a = document.createElement('a'); a.download = `QR-${item.sku}.png`; a.href = canvas.toDataURL('image/png'); a.click(); };
            img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
        };

        const handleDownloadBarcode = () => {
            const svg = document.querySelector('#product-barcode svg');
            if (!svg) return;
            const svgData = new XMLSerializer().serializeToString(svg);
            const canvas = document.createElement('canvas');
            canvas.width = 500; canvas.height = 200;
            const ctx = canvas.getContext('2d');
            const img = new Image();
            img.onload = () => { ctx.drawImage(img, 0, 0, 500, 200); const a = document.createElement('a'); a.download = `Barcode-${item.sku}.png`; a.href = canvas.toDataURL('image/png'); a.click(); };
            img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
        };

        const handlePrintAssetTag = () => {
            const printContent = document.getElementById('asset-tag-sticker');
            if (!printContent) return;
            const w = window.open('', '_blank');
            w.document.write(`<html><head><title>Asset Tag - ${item.sku}</title><style>
                @page { size: 80mm 50mm; margin: 0; }
                body { margin: 0; padding: 4mm; font-family: Arial, sans-serif; }
                .tag { border: 2px solid #333; border-radius: 4px; padding: 3mm; display: flex; gap: 3mm; align-items: center; }
                .qr-side { flex-shrink: 0; }
                .info-side { flex: 1; overflow: hidden; }
                .info-side .name { font-size: 11pt; font-weight: bold; margin-bottom: 1mm; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
                .info-side .sku { font-size: 9pt; color: #555; margin-bottom: 1mm; font-family: monospace; }
                .info-side .cat { font-size: 8pt; color: #777; }
            </style></head><body>`);
            w.document.write(printContent.innerHTML);
            w.document.write('</body></html>');
            w.document.close();
            w.print();
        };

        const logs = productLogs || { stockTransactions: [], priceChanges: [], poLogs: [], burstReport: [] };
        const detailTab = productDetailTab;
        const setDetailTab = setProductDetailTab;

        return (
            <div className="space-y-6" data-testid="product-detail-view">
                {/* Back Button */}
                <button onClick={() => { setViewingProductId(null); setProductLogs(null); }} className="flex items-center gap-2 text-sm font-semibold text-slate-500 hover:text-slate-800 transition-colors" data-testid="product-detail-back-btn">
                    <ArrowLeft size={16} /> {language === 'en' ? 'Back to Stock Operations' : 'Kembali ke Operasi Stok'}
                </button>

                {/* Product Header */}
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                    <div className="p-6">
                        <div className="flex flex-col md:flex-row gap-6">
                            {/* Product Image */}
                            <div className="w-32 h-32 rounded-2xl bg-gradient-to-br from-slate-100 to-slate-50 flex items-center justify-center overflow-hidden border-2 border-slate-200 flex-shrink-0">
                                {item.image ? <img src={item.image} className="w-full h-full object-cover" alt={item.name}/> : <Package size={48} className="text-slate-300"/>}
                            </div>
                            {/* Info */}
                            <div className="flex-1 min-w-0">
                                <h1 className="text-2xl font-bold text-slate-900">{item.name}</h1>
                                <p className="text-sm text-slate-400 font-mono mt-1">{item.sku}</p>
                                <div className="flex flex-wrap gap-2 mt-3">
                                    <span className="px-3 py-1 rounded-lg bg-blue-100 text-blue-700 text-xs font-bold">{item.category}</span>
                                    {item.binNo && <span className="px-3 py-1 rounded-lg bg-slate-100 text-slate-600 text-xs font-bold">BIN: {item.binNo}</span>}
                                    {can.viewFinancials && <span className="px-3 py-1 rounded-lg bg-purple-100 text-purple-700 text-xs font-bold">{formatCurrency(item.price)}</span>}
                                    <span className="px-3 py-1 rounded-lg bg-slate-100 text-slate-600 text-xs font-bold">{language === 'en' ? 'Unit' : 'Unit'}: {item.unit || 'pcs'}</span>
                                </div>
                                <div className="flex items-center gap-3 mt-4">
                                    <span className={`px-4 py-2 rounded-xl text-sm font-bold ${isLowStock ? 'bg-rose-100 text-rose-700 ring-2 ring-rose-200' : 'bg-emerald-100 text-emerald-700'}`}>
                                        {formatNumber(item.quantity)} {item.unit || 'pcs'}
                                    </span>
                                    {isLowStock && <span className="flex items-center gap-1 text-xs text-rose-600 font-semibold"><AlertCircle size={14}/> {language === 'en' ? 'Low Stock' : 'Stok Rendah'} (Min: {item.minLevel})</span>}
                                </div>
                            </div>
                            {/* Quick Actions */}
                            <div className="flex flex-col gap-2 flex-shrink-0">
                                {can.doStockIn && (
                                    <button onClick={() => { setSelectedItem(item); setStockAction('IN'); setIsStockModalOpen(true); }} className="px-4 py-2.5 rounded-xl bg-emerald-500 text-white hover:bg-emerald-600 transition-all shadow-sm font-bold text-sm flex items-center gap-2" data-testid="product-detail-stock-in-btn">
                                        <Plus size={16}/> {language === 'en' ? 'Stock In' : 'Stok Masuk'}
                                    </button>
                                )}
                                {can.doStockOut && (
                                    <button onClick={() => { setSelectedItem(item); setStockAction('OUT'); setIsStockModalOpen(true); }} className="px-4 py-2.5 rounded-xl bg-rose-500 text-white hover:bg-rose-600 transition-all shadow-sm font-bold text-sm flex items-center gap-2" data-testid="product-detail-stock-out-btn">
                                        <Minus size={16}/> {language === 'en' ? 'Stock Out' : 'Stok Keluar'}
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* QR Code, Barcode & Asset Tag */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* QR Code */}
                    <Card>
                        <div className="text-center">
                            <h3 className="text-sm font-bold text-slate-700 mb-3 flex items-center justify-center gap-2"><QrCode size={16}/> QR Code</h3>
                            <div className="bg-white p-4 rounded-xl border border-slate-100 inline-block">
                                <QRCodeSVG id="product-qr-code" value={publicUrl} size={160} level="H" includeMargin={true}/>
                            </div>
                            <p className="text-[10px] text-slate-400 mt-2 break-all px-4">{publicUrl}</p>
                            <button onClick={handleDownloadQR} className="mt-3 px-4 py-2 rounded-xl bg-[#10056E] text-white text-xs font-bold hover:bg-[#0d0459] transition-colors flex items-center gap-2 mx-auto" data-testid="download-qr-btn">
                                <Download size={14}/> Download QR
                            </button>
                        </div>
                    </Card>

                    {/* Barcode */}
                    <Card>
                        <div className="text-center">
                            <h3 className="text-sm font-bold text-slate-700 mb-3 flex items-center justify-center gap-2"><Barcode size={16}/> Barcode (SKU)</h3>
                            <div id="product-barcode" className="bg-white p-4 rounded-xl border border-slate-100 inline-block overflow-hidden">
                                <BarcodeComponent value={item.sku} width={1.5} height={60} fontSize={12} margin={5}/>
                            </div>
                            <button onClick={handleDownloadBarcode} className="mt-3 px-4 py-2 rounded-xl bg-[#10056E] text-white text-xs font-bold hover:bg-[#0d0459] transition-colors flex items-center gap-2 mx-auto" data-testid="download-barcode-btn">
                                <Download size={14}/> Download Barcode
                            </button>
                        </div>
                    </Card>

                    {/* Asset Tag Sticker */}
                    <Card>
                        <div className="text-center">
                            <h3 className="text-sm font-bold text-slate-700 mb-3 flex items-center justify-center gap-2"><Tag size={16}/> Asset Tag Sticker</h3>
                            <div className="bg-white p-3 rounded-xl border-2 border-dashed border-slate-300">
                                <div id="asset-tag-sticker">
                                    <div className="tag" style={{border: '2px solid #333', borderRadius: '6px', padding: '8px', display: 'flex', gap: '10px', alignItems: 'center'}}>
                                        <div className="qr-side">
                                            <QRCodeSVG value={publicUrl} size={70} level="M"/>
                                        </div>
                                        <div className="info-side" style={{flex: 1, overflow: 'hidden', textAlign: 'left'}}>
                                            <div style={{fontSize: '12px', fontWeight: 'bold', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}>{item.name}</div>
                                            <div style={{fontSize: '10px', color: '#555', fontFamily: 'monospace', marginTop: '2px'}}>{item.sku}</div>
                                            <div style={{fontSize: '9px', color: '#777', marginTop: '2px'}}>{item.category}</div>
                                            {item.binNo && <div style={{fontSize: '9px', color: '#777', marginTop: '1px'}}>BIN: {item.binNo}</div>}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <button onClick={handlePrintAssetTag} className="mt-3 px-4 py-2 rounded-xl bg-[#10056E] text-white text-xs font-bold hover:bg-[#0d0459] transition-colors flex items-center gap-2 mx-auto" data-testid="print-asset-tag-btn">
                                <Printer size={14}/> Print Sticker
                            </button>
                        </div>
                    </Card>
                </div>

                {/* Tabs for Logs */}
                <Card noPadding>
                    <div className="border-b border-slate-100 px-4 pt-3">
                        <div className="flex gap-1 overflow-x-auto">
                            {[
                                { id: 'overview', label: language === 'en' ? 'Overview' : 'Ringkasan' },
                                { id: 'stock', label: language === 'en' ? 'Stock In/Out' : 'Stok Masuk/Keluar' },
                                { id: 'price', label: language === 'en' ? 'Price Log' : 'Log Harga' },
                                { id: 'po', label: language === 'en' ? 'PO Log' : 'Log PO' },
                                { id: 'burst', label: language === 'en' ? 'Burst Report' : 'Laporan Burst' },
                            ].map(tab => (
                                <button key={tab.id} onClick={() => setDetailTab(tab.id)} className={`px-4 py-2.5 text-sm font-semibold rounded-t-xl transition-colors whitespace-nowrap ${detailTab === tab.id ? 'bg-[#10056E]/10 text-[#10056E] border-b-2 border-[#10056E]' : 'text-slate-400 hover:text-slate-600'}`} data-testid={`product-tab-${tab.id}`}>
                                    {tab.label}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div className="p-5">
                        {loadingProductLogs ? (
                            <div className="flex items-center justify-center py-12"><Loader2 className="animate-spin text-[#10056E]" size={32}/></div>
                        ) : (
                            <>
                                {/* Overview Tab */}
                                {detailTab === 'overview' && (
                                    <div className="space-y-4">
                                        {/* Usage Since Last PO */}
                                        <div className="bg-slate-50 rounded-xl p-4 border border-slate-100">
                                            <h4 className="text-xs font-bold text-slate-500 uppercase mb-3">{language === 'en' ? 'Usage Since Last PO' : 'Penggunaan Sejak PO Terakhir'}</h4>
                                            {usageSinceLastPO ? (
                                                <div className="flex items-center gap-4">
                                                    <span className="text-2xl font-bold text-rose-600">{usageSinceLastPO.totalUsed} {item.unit || 'pcs'}</span>
                                                    <span className="text-xs text-slate-500">{language === 'en' ? 'used since' : 'digunakan sejak'} {usageSinceLastPO.supplier} ({new Date(usageSinceLastPO.poDate).toLocaleDateString('en-MY')})</span>
                                                </div>
                                            ) : <p className="text-sm text-slate-400">{language === 'en' ? 'No completed PO found' : 'Tiada PO selesai'}</p>}
                                        </div>

                                        {/* Low Stock Alert */}
                                        {isLowStock && (
                                            <div className="bg-rose-50 rounded-xl p-4 border border-rose-200">
                                                <div className="flex items-center gap-3">
                                                    <AlertCircle size={20} className="text-rose-500"/>
                                                    <div>
                                                        <h4 className="text-sm font-bold text-rose-700">{language === 'en' ? 'Low Stock Alert' : 'Amaran Stok Rendah'}</h4>
                                                        <p className="text-xs text-rose-600 mt-1">{language === 'en' ? `Current stock (${item.quantity}) is below minimum level (${item.minLevel})` : `Stok semasa (${item.quantity}) di bawah tahap minimum (${item.minLevel})`}</p>
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        {/* Burst Limit Info */}
                                        {item.burstLimit > 0 && (
                                            <div className="bg-amber-50 rounded-xl p-4 border border-amber-200">
                                                <h4 className="text-xs font-bold text-amber-700 uppercase mb-2">{language === 'en' ? 'Monthly Burst Limit' : 'Had Burst Bulanan'}</h4>
                                                <div className="flex items-center gap-3">
                                                    <span className="text-xl font-bold text-amber-700">{item.burstLimit} {item.unit || 'pcs'}/month</span>
                                                    {logs.burstReport.length > 0 && logs.burstReport[0].totalOut > item.burstLimit && (
                                                        <span className="px-2 py-1 rounded-lg bg-rose-100 text-rose-700 text-xs font-bold">{language === 'en' ? 'EXCEEDED' : 'MELEBIHI'}</span>
                                                    )}
                                                </div>
                                            </div>
                                        )}

                                        {/* Quick Stats */}
                                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                            <div className="bg-emerald-50 rounded-xl p-3 text-center border border-emerald-100">
                                                <div className="text-lg font-bold text-emerald-700">{logs.stockTransactions.filter(t => t.type === 'IN').length}</div>
                                                <div className="text-xs text-emerald-600">{language === 'en' ? 'Stock In' : 'Stok Masuk'}</div>
                                            </div>
                                            <div className="bg-rose-50 rounded-xl p-3 text-center border border-rose-100">
                                                <div className="text-lg font-bold text-rose-700">{logs.stockTransactions.filter(t => t.type === 'OUT').length}</div>
                                                <div className="text-xs text-rose-600">{language === 'en' ? 'Stock Out' : 'Stok Keluar'}</div>
                                            </div>
                                            <div className="bg-purple-50 rounded-xl p-3 text-center border border-purple-100">
                                                <div className="text-lg font-bold text-purple-700">{logs.priceChanges.length}</div>
                                                <div className="text-xs text-purple-600">{language === 'en' ? 'Price Changes' : 'Perubahan Harga'}</div>
                                            </div>
                                            <div className="bg-blue-50 rounded-xl p-3 text-center border border-blue-100">
                                                <div className="text-lg font-bold text-blue-700">{logs.poLogs.length}</div>
                                                <div className="text-xs text-blue-600">{language === 'en' ? 'Purchase Orders' : 'Pesanan Belian'}</div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Stock In/Out Tab */}
                                {detailTab === 'stock' && (
                                    <div>
                                        {logs.stockTransactions.length > 0 ? (
                                            <div className="overflow-x-auto">
                                                <table className="w-full text-sm">
                                                    <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
                                                        <tr>
                                                            <th className="p-3 text-left">{language === 'en' ? 'Date' : 'Tarikh'}</th>
                                                            <th className="p-3 text-center">{language === 'en' ? 'Type' : 'Jenis'}</th>
                                                            <th className="p-3 text-center">{language === 'en' ? 'Qty' : 'Kuantiti'}</th>
                                                            <th className="p-3 text-left">{language === 'en' ? 'By' : 'Oleh'}</th>
                                                            <th className="p-3 text-left">{language === 'en' ? 'Notes' : 'Nota'}</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="divide-y divide-slate-100">
                                                        {logs.stockTransactions.map((tx, i) => (
                                                            <tr key={i} className="hover:bg-slate-50">
                                                                <td className="p-3 text-xs text-slate-600">{new Date(tx.timestamp).toLocaleString('en-MY')}</td>
                                                                <td className="p-3 text-center">
                                                                    <span className={`px-2 py-1 rounded-lg text-xs font-bold ${tx.type === 'IN' ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>
                                                                        {tx.type === 'IN' ? 'IN' : 'OUT'}
                                                                    </span>
                                                                </td>
                                                                <td className="p-3 text-center font-bold">{tx.quantity} {item.unit || 'pcs'}</td>
                                                                <td className="p-3 text-xs text-slate-600">{tx.userName || tx.user}</td>
                                                                <td className="p-3 text-xs text-slate-500">{tx.reason || '-'}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        ) : <p className="text-sm text-slate-400 text-center py-8">{language === 'en' ? 'No stock transactions yet' : 'Tiada transaksi stok'}</p>}
                                    </div>
                                )}

                                {/* Price Log Tab */}
                                {detailTab === 'price' && (
                                    <div>
                                        {logs.priceChanges.length > 0 ? (
                                            <div className="overflow-x-auto">
                                                <table className="w-full text-sm">
                                                    <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
                                                        <tr>
                                                            <th className="p-3 text-left">{language === 'en' ? 'Date' : 'Tarikh'}</th>
                                                            <th className="p-3 text-right">{language === 'en' ? 'Old Price' : 'Harga Lama'}</th>
                                                            <th className="p-3 text-right">{language === 'en' ? 'New Price' : 'Harga Baru'}</th>
                                                            <th className="p-3 text-left">{language === 'en' ? 'Changed By' : 'Diubah Oleh'}</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="divide-y divide-slate-100">
                                                        {logs.priceChanges.map((tx, i) => {
                                                            const meta = tx.metadata || {};
                                                            return (
                                                                <tr key={i} className="hover:bg-slate-50">
                                                                    <td className="p-3 text-xs text-slate-600">{new Date(tx.timestamp).toLocaleString('en-MY')}</td>
                                                                    <td className="p-3 text-right font-mono text-sm">{formatCurrency(meta.oldPrice || 0)}</td>
                                                                    <td className="p-3 text-right font-mono text-sm font-bold">{formatCurrency(meta.newPrice || 0)}</td>
                                                                    <td className="p-3 text-xs text-slate-600">{meta.changedByName || tx.userName || tx.user}</td>
                                                                </tr>
                                                            );
                                                        })}
                                                    </tbody>
                                                </table>
                                            </div>
                                        ) : <p className="text-sm text-slate-400 text-center py-8">{language === 'en' ? 'No price changes recorded' : 'Tiada perubahan harga'}</p>}
                                    </div>
                                )}

                                {/* PO Log Tab */}
                                {detailTab === 'po' && (
                                    <div>
                                        {logs.poLogs.length > 0 ? (
                                            <div className="overflow-x-auto">
                                                <table className="w-full text-sm">
                                                    <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
                                                        <tr>
                                                            <th className="p-3 text-left">{language === 'en' ? 'Date' : 'Tarikh'}</th>
                                                            <th className="p-3 text-left">{language === 'en' ? 'Supplier' : 'Pembekal'}</th>
                                                            <th className="p-3 text-center">{language === 'en' ? 'Ordered' : 'Dipesan'}</th>
                                                            <th className="p-3 text-center">{language === 'en' ? 'Received' : 'Diterima'}</th>
                                                            <th className="p-3 text-center">{language === 'en' ? 'Status' : 'Status'}</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="divide-y divide-slate-100">
                                                        {logs.poLogs.map((po, i) => (
                                                            <tr key={i} className="hover:bg-slate-50">
                                                                <td className="p-3 text-xs text-slate-600">{new Date(po.date).toLocaleDateString('en-MY')}</td>
                                                                <td className="p-3 text-sm font-medium text-slate-700">{po.supplier}</td>
                                                                <td className="p-3 text-center font-bold">{po.orderedQty} {item.unit || 'pcs'}</td>
                                                                <td className="p-3 text-center font-bold">{po.receivedQty} {item.unit || 'pcs'}</td>
                                                                <td className="p-3 text-center">
                                                                    <span className={`px-2 py-1 rounded-lg text-xs font-bold ${po.status === 'Selesai' ? 'bg-emerald-100 text-emerald-700' : po.status === 'Batal' ? 'bg-slate-100 text-slate-600' : 'bg-amber-100 text-amber-700'}`}>
                                                                        {po.status === 'Selesai' ? (language === 'en' ? 'Completed' : 'Selesai') : po.status === 'Batal' ? (language === 'en' ? 'Cancelled' : 'Batal') : (language === 'en' ? 'Pending' : 'Belum Selesai')}
                                                                    </span>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        ) : <p className="text-sm text-slate-400 text-center py-8">{language === 'en' ? 'No purchase orders found' : 'Tiada pesanan belian'}</p>}
                                    </div>
                                )}

                                {/* Burst Report Tab */}
                                {detailTab === 'burst' && (
                                    <div>
                                        {logs.burstReport.length > 0 ? (
                                            <div className="space-y-3">
                                                {item.burstLimit > 0 && (
                                                    <div className="flex items-center gap-2 text-xs text-slate-500 mb-2">
                                                        <Info size={14}/> {language === 'en' ? `Monthly burst limit: ${item.burstLimit} ${item.unit || 'pcs'}` : `Had burst bulanan: ${item.burstLimit} ${item.unit || 'pcs'}`}
                                                    </div>
                                                )}
                                                <div className="overflow-x-auto">
                                                    <table className="w-full text-sm">
                                                        <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
                                                            <tr>
                                                                <th className="p-3 text-left">{language === 'en' ? 'Month' : 'Bulan'}</th>
                                                                <th className="p-3 text-center">{language === 'en' ? 'Total Out' : 'Jumlah Keluar'}</th>
                                                                <th className="p-3 text-center">{language === 'en' ? 'Transactions' : 'Transaksi'}</th>
                                                                {item.burstLimit > 0 && <th className="p-3 text-center">{language === 'en' ? 'Status' : 'Status'}</th>}
                                                            </tr>
                                                        </thead>
                                                        <tbody className="divide-y divide-slate-100">
                                                            {logs.burstReport.map((r, i) => (
                                                                <tr key={i} className="hover:bg-slate-50">
                                                                    <td className="p-3 font-medium text-slate-700">{r.month}</td>
                                                                    <td className="p-3 text-center font-bold">{r.totalOut} {item.unit || 'pcs'}</td>
                                                                    <td className="p-3 text-center text-slate-500">{r.transactions}</td>
                                                                    {item.burstLimit > 0 && (
                                                                        <td className="p-3 text-center">
                                                                            <span className={`px-2 py-1 rounded-lg text-xs font-bold ${r.totalOut > item.burstLimit ? 'bg-rose-100 text-rose-700' : 'bg-emerald-100 text-emerald-700'}`}>
                                                                                {r.totalOut > item.burstLimit ? (language === 'en' ? 'EXCEEDED' : 'MELEBIHI') : 'OK'}
                                                                            </span>
                                                                        </td>
                                                                    )}
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>
                                        ) : <p className="text-sm text-slate-400 text-center py-8">{language === 'en' ? 'No burst data available' : 'Tiada data burst'}</p>}
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </Card>
            </div>
        );
    };

    // --- Renders ---

    const renderInventoryOps = (isSettings = false) => {
        // Product Detail View
        if (!isSettings && viewingProductId) {
            return renderProductDetail();
        }
        // Bulk Stock Out page
        if (!isSettings && bulkStockOutMode) {
            return (
                <div className="space-y-6">
                    <button onClick={() => { setBulkStockOutMode(false); setBulkStockOutItems([]); setBulkProofImage(''); setBulkSelectedProduct(''); setBulkProofError(false); }} className="flex items-center gap-2 text-sm font-semibold text-slate-500 hover:text-slate-800 transition-colors" data-testid="bulk-stockout-back-btn">
                        <ArrowLeft size={16} /> {t("inventory.backToList")}
                    </button>
                    <div>
                        <h2 className="text-3xl font-bold text-slate-900">{t('inventory.bulkStockOut')}</h2>
                        <p className="text-slate-500 mt-1">{language === "en" ? "Record stock out for multiple items at once" : "Rekod stok keluar untuk pelbagai item sekaligus"}</p>
                    </div>
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6">
                        <div className="lg:col-span-2 space-y-4 lg:space-y-6">
                            {/* Add Item Card */}
                            <Card>
                                <div className="flex items-center gap-3 mb-4 lg:mb-5">
                                    <div className="w-9 h-9 lg:w-10 lg:h-10 rounded-xl bg-rose-500 text-white flex items-center justify-center"><Minus size={18} /></div>
                                    <div>
                                        <h3 className="text-sm lg:text-base font-bold text-slate-900">{t("inventory.addItem")}</h3>
                                        <p className="text-xs text-slate-500 hidden sm:block">{t("inventory.selectFromInventory")}</p>
                                    </div>
                                </div>
                                <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-end">
                                    <div className="flex-1">
                                        <label className="block text-xs font-bold text-slate-600 mb-2">{language === "en" ? "SELECT PRODUCT" : "PILIH PRODUK"} *</label>
                                        <SearchableDropdown
                                            options={inventory.filter(i => !bulkStockOutItems.find(b => b.id === i.id))}
                                            value={bulkSelectedProduct}
                                            onChange={(val) => { setBulkSelectedProduct(val); setBulkAddQty(1); }}
                                            placeholder={t("inventory.searchAndSelectItem")}
                                            searchPlaceholder="Taip nama produk..."
                                            getOptionLabel={(item) => `${item.name} (${item.quantity} ${item.unit || 'pcs'})`}
                                            getOptionValue={(item) => item.id}
                                            noResultsText={t("inventory.noProductFound")}
                                            className=""
                                            data-testid="bulk-item-select"
                                        />
                                    </div>
                                    <div className="flex gap-3 sm:gap-3">
                                        <div className="w-24 sm:w-32">
                                            <label className="block text-xs font-bold text-slate-600 mb-2">QTY *</label>
                                            <input
                                                type="number" min="1" value={bulkAddQty}
                                                onChange={(e) => setBulkAddQty(parseInt(e.target.value) || 0)}
                                                className={`w-full px-3 sm:px-4 py-2.5 sm:py-3 rounded-xl border outline-none transition-all font-bold text-sm ${
                                                    bulkSelectedProduct && bulkAddQty > (inventory.find(i => i.id === bulkSelectedProduct)?.quantity || 0)
                                                        ? 'border-rose-500 bg-rose-50 text-rose-700 ring-2 ring-rose-200'
                                                        : 'border-slate-200 bg-white text-slate-700 focus:border-rose-500 focus:ring-2 focus:ring-rose-200'
                                                }`}
                                                data-testid="bulk-item-qty"
                                            />
                                        </div>
                                        <div className="flex items-end">
                                            <Button type="button" data-testid="bulk-add-item-btn" className="bg-rose-500 hover:bg-rose-600 text-white h-[42px] sm:h-[46px] px-4 sm:px-5 shadow-lg shadow-rose-500/30" onClick={() => {
                                        const itemId = bulkSelectedProduct;
                                        const qty = bulkAddQty;
                                        if (!itemId) { showToast(t("inventory.pleaseSelectProduct"), "error"); return; }
                                        if (qty <= 0) { showToast(t("inventory.invalidQty"), "error"); return; }
                                        const invItem = inventory.find(i => i.id === itemId);
                                        if (!invItem) return;
                                        if (qty > invItem.quantity) return;
                                        setBulkStockOutItems(prev => [...prev, { id: invItem.id, name: invItem.name, sku: invItem.sku, unit: invItem.unit || 'pcs', qty, stock: invItem.quantity }]);
                                        setBulkSelectedProduct('');
                                        setBulkAddQty(1);
                                    }}>
                                        <Plus size={16} /> <span className="hidden sm:inline">{t("common.add")}</span>
                                    </Button>
                                        </div>
                                    </div>
                                </div>
                                {/* Tambah section - stock warning */}
                                {bulkSelectedProduct && (() => {
                                    const sel = inventory.find(i => i.id === bulkSelectedProduct);
                                    if (!sel) return null;
                                    const isOver = bulkAddQty > sel.quantity;
                                    return (
                                        <div className={`mt-3 flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-bold ${isOver ? 'bg-rose-50 text-rose-600' : 'bg-slate-50 text-slate-500'}`} data-testid="tambah-stock-info">
                                            {isOver ? <AlertCircle size={14} /> : <Package size={14} />}
                                            <span>Baki stok: <strong>{sel.quantity} {sel.unit || 'pcs'}</strong></span>
                                            {isOver && <span className="ml-1">&mdash; Melebihi baki stok!</span>}
                                        </div>
                                    );
                                })()}
                            </Card>
                            {/* Items Table */}
                            <Card>
                                <div className="flex items-center justify-between mb-4">
                                    <h3 className="text-sm lg:text-base font-bold text-slate-900">{t("inventory.itemDetails")} <span className="text-rose-500 ml-1">{bulkStockOutItems.length}</span></h3>
                                </div>
                                {bulkStockOutItems.length === 0 ? (
                                    <div className="text-center py-8 lg:py-12 text-slate-400">
                                        <Package size={36} className="mx-auto mb-3 opacity-50" />
                                        <p className="font-medium text-sm">{t("inventory.noItemsYet")}</p>
                                        <p className="text-xs mt-1">{t("inventory.selectFromDropdown")}</p>
                                    </div>
                                ) : (
                                    <>
                                    {/* Mobile card view */}
                                    <div className="space-y-3 sm:hidden">
                                        {bulkStockOutItems.map((item, idx) => (
                                            <div key={item.id} className="p-3 rounded-xl border border-slate-100 bg-slate-50/50">
                                                <div className="flex items-start justify-between mb-2">
                                                    <div>
                                                        <div className="font-semibold text-slate-800 text-sm">{item.name}</div>
                                                        <div className="text-xs text-slate-500">{item.sku || '-'}</div>
                                                    </div>
                                                    <button type="button" onClick={() => setBulkStockOutItems(prev => prev.filter((_, i) => i !== idx))} className="text-rose-400 hover:text-rose-600 transition-colors p-1">
                                                        <Trash2 size={16} />
                                                    </button>
                                                </div>
                                                <div className="flex items-center justify-between gap-3">
                                                    <span className="px-2 py-1 rounded-lg bg-slate-100 text-xs font-bold text-slate-600">Baki: {item.stock} {item.unit}</span>
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-xs font-bold text-slate-500">QTY:</span>
                                                        <input
                                                            type="number"
                                                            min="1"
                                                            max={item.stock}
                                                            value={item.qty}
                                                            onChange={(e) => {
                                                                const val = parseInt(e.target.value) || 0;
                                                                setBulkStockOutItems(prev => prev.map((it, i) => i === idx ? { ...it, qty: val } : it));
                                                            }}
                                                            className={`w-16 text-center px-2 py-1.5 rounded-lg border font-bold text-sm outline-none ${item.qty > item.stock ? 'border-rose-500 bg-rose-50 text-rose-700 ring-2 ring-rose-200' : 'border-slate-200 text-slate-700 focus:border-rose-500 focus:ring-2 focus:ring-rose-200'}`}
                                                        />
                                                    </div>
                                                </div>
                                                {item.qty > item.stock && (
                                                    <div className="mt-2 flex items-center gap-1.5 text-rose-600 text-xs font-bold" data-testid={`qty-warning-mobile-${idx}`}>
                                                        <AlertCircle size={12} />
                                                        <span>Melebihi baki stok ({item.stock} {item.unit})</span>
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                    {/* Desktop table view */}
                                    <div className="overflow-x-auto hidden sm:block">
                                        <table className="w-full">
                                            <thead>
                                                <tr className="border-b-2 border-slate-100">
                                                    <th className="text-left text-xs font-bold text-slate-500 py-3 px-2">{language === "en" ? "ITEM DETAILS" : "BUTIRAN ITEM"}</th>
                                                    <th className="text-left text-xs font-bold text-slate-500 py-3 px-2">SKU</th>
                                                    <th className="text-center text-xs font-bold text-slate-500 py-3 px-2">BAKI</th>
                                                    <th className="text-center text-xs font-bold text-slate-500 py-3 px-2">QTY KELUAR</th>
                                                    <th className="text-center text-xs font-bold text-slate-500 py-3 px-2">{language === "en" ? "ACTIONS" : "TINDAKAN"}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {bulkStockOutItems.map((item, idx) => (
                                                    <tr key={item.id} className="border-b border-slate-50 hover:bg-slate-50/50">
                                                        <td className="py-3 px-2">
                                                            <div className="font-semibold text-slate-800">{item.name}</div>
                                                        </td>
                                                        <td className="py-3 px-2 text-sm text-slate-500">{item.sku || '-'}</td>
                                                        <td className="py-3 px-2 text-center">
                                                            <span className="px-2 py-1 rounded-lg bg-slate-100 text-sm font-bold text-slate-600">{item.stock} {item.unit}</span>
                                                        </td>
                                                        <td className="py-3 px-2 text-center">
                                                            <div className="inline-flex flex-col items-center gap-1">
                                                                <input
                                                                    type="number"
                                                                    min="1"
                                                                    max={item.stock}
                                                                    value={item.qty}
                                                                    onChange={(e) => {
                                                                        const val = parseInt(e.target.value) || 0;
                                                                        setBulkStockOutItems(prev => prev.map((it, i) => i === idx ? { ...it, qty: val } : it));
                                                                    }}
                                                                    className={`w-20 text-center px-2 py-1.5 rounded-lg border font-bold outline-none ${item.qty > item.stock ? 'border-rose-500 bg-rose-50 text-rose-700 ring-2 ring-rose-200' : 'border-slate-200 text-slate-700 focus:border-rose-500 focus:ring-2 focus:ring-rose-200'}`}
                                                                />
                                                                {item.qty > item.stock && (
                                                                    <span className="text-rose-600 text-[10px] font-bold leading-tight" data-testid={`qty-warning-desktop-${idx}`}>
                                                                        Melebihi baki ({item.stock})
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </td>
                                                        <td className="py-3 px-2 text-center">
                                                            <button type="button" onClick={() => setBulkStockOutItems(prev => prev.filter((_, i) => i !== idx))} className="text-rose-400 hover:text-rose-600 transition-colors">
                                                                <Trash2 size={16} />
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                    </>
                                )}
                            </Card>
                        </div>
                        {/* Right Sidebar - Details */}
                        <div className="lg:col-span-1">
                            <Card className="sticky top-4">
                                <form onSubmit={handleBulkStockOut}>
                                    <div className="flex items-center gap-3 mb-5">
                                        <div className="w-10 h-10 rounded-xl bg-slate-800 text-white flex items-center justify-center"><FileText size={20} /></div>
                                        <div>
                                            <h3 className="text-base font-bold text-slate-900">{t("inventory.itemDetails")}</h3>
                                            <p className="text-xs text-slate-500">{t("inventory.stockOut")}</p>
                                        </div>
                                    </div>
                                    <div className="space-y-4">
                                        <div>
                                            <label className="block text-xs font-bold text-slate-600 mb-2">{language === "en" ? "REASON / REFERENCE" : "SEBAB / RUJUKAN"} *</label>
                                            <input name="reason" required placeholder={language === "en" ? "E.g.: cooking, daily sales, event" : "Cth: masak, jualan harian, event"} className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-white focus:border-rose-500 focus:ring-2 focus:ring-rose-200 outline-none transition-all font-medium text-slate-700" data-testid="bulk-reason-input" />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-bold text-slate-600 mb-2">{language === "en" ? "PROOF DOCUMENT / IMAGE" : "BUKTI DOKUMEN / GAMBAR"} <span className="text-rose-500">*</span></label>
                                            <div
                                                className={`border-2 border-dashed p-4 rounded-xl text-center transition-all cursor-pointer ${
                                                    bulkProofError && !bulkProofImage ? 'border-rose-500 bg-rose-50/50 ring-2 ring-rose-200' :
                                                    isDraggingProof ? 'border-rose-500 bg-rose-50/50 scale-[1.02]' :
                                                    bulkProofImage ? 'border-emerald-400 bg-emerald-50/50' : 'border-slate-300 hover:border-rose-400 hover:bg-rose-50/30'
                                                }`}
                                                onClick={() => document.getElementById('bulk-proof-upload').click()}
                                                onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setIsDraggingProof(true); }}
                                                onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); setIsDraggingProof(true); }}
                                                onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); setIsDraggingProof(false); }}
                                                onDrop={(e) => {
                                                    e.preventDefault(); e.stopPropagation(); setIsDraggingProof(false);
                                                    const file = e.dataTransfer.files[0];
                                                    if (file && file.type.startsWith('image/')) {
                                                        const reader = new FileReader();
                                                        reader.onloadend = () => { setBulkProofImage(reader.result); setBulkProofError(false); };
                                                        reader.readAsDataURL(file);
                                                    } else {
                                                        showToast(t("inventory.uploadImageOnly"), "error");
                                                    }
                                                }}
                                                data-testid="bulk-proof-dropzone"
                                            >
                                                <input
                                                    type="file"
                                                    accept="image/*"
                                                    onChange={(e) => {
                                                        const file = e.target.files[0];
                                                        if (file) {
                                                            const reader = new FileReader();
                                                            reader.onloadend = () => { setBulkProofImage(reader.result); setBulkProofError(false); };
                                                            reader.readAsDataURL(file);
                                                        }
                                                    }}
                                                    className="hidden"
                                                    id="bulk-proof-upload"
                                                />
                                                {bulkProofImage ? (
                                                    <div className="space-y-2">
                                                        <div className="w-24 h-24 mx-auto rounded-lg overflow-hidden border-2 border-emerald-500">
                                                            <img src={bulkProofImage} alt="Preview" className="w-full h-full object-cover" />
                                                        </div>
                                                        <div className="flex items-center justify-center gap-1 text-emerald-600">
                                                            <CheckCircle size={14} />
                                                            <p className="text-xs font-bold">{language === "en" ? "Uploaded" : "Dimuat naik"}</p>
                                                        </div>
                                                        <button type="button" onClick={(e) => { e.stopPropagation(); setBulkProofImage(''); }} className="text-xs text-slate-500 hover:text-rose-600 underline">{language === "en" ? "Change" : "Tukar"}</button>
                                                    </div>
                                                ) : (
                                                    <div>
                                                        <Upload size={28} className={`mx-auto mb-2 ${isDraggingProof ? 'text-rose-500' : 'text-slate-400'}`} />
                                                        <p className="text-xs font-bold text-slate-600">{isDraggingProof ? (language === 'en' ? 'Drop to upload' : 'Lepaskan untuk muat naik') : (language === 'en' ? 'Drag & drop or click to upload' : 'Seret & lepas atau klik untuk muat naik')}</p>
                                                        <p className="text-xs text-rose-500 mt-1">{language === "en" ? "* Required" : "* Wajib"}</p>
                                                    </div>
                                                )}
                                            </div>
                                            {bulkProofError && !bulkProofImage && (
                                                <div className="flex items-center gap-1.5 mt-2 text-rose-600 bg-rose-50 px-3 py-2 rounded-lg animate-pulse" data-testid="proof-upload-warning">
                                                    <AlertCircle size={14} />
                                                    <span className="text-xs font-bold">{t("inventory.pleaseUploadProof")}</span>
                                                </div>
                                            )}
                                        </div>
                                        <div className="pt-3 border-t border-slate-200">
                                            <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">{t("inventory.additionalInfoOptional")}</p>
                                            <div className="space-y-3">
                                                <div>
                                                    <label className="block text-xs font-bold text-slate-600 mb-1">{language === "en" ? "PIC NAME" : "NAMA PIC"}</label>
                                                    <input name="picName" placeholder={language === "en" ? "Person In Charge Name" : "Nama Person In Charge"} className="w-full px-3 py-2.5 rounded-xl border border-slate-200 bg-white focus:border-rose-500 focus:ring-2 focus:ring-rose-200 outline-none transition-all text-sm font-medium text-slate-700" data-testid="bulk-pic-name" />
                                                </div>
                                                <div>
                                                    <label className="block text-xs font-bold text-slate-600 mb-1">{language === "en" ? "PIC PHONE NO." : "NO. TEL PIC"}</label>
                                                    <input name="picContact" placeholder="012-3456789" className="w-full px-3 py-2.5 rounded-xl border border-slate-200 bg-white focus:border-rose-500 focus:ring-2 focus:ring-rose-200 outline-none transition-all text-sm font-medium text-slate-700" data-testid="bulk-pic-contact" />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    {/* Summary Footer */}
                                    <div className="mt-6 bg-gradient-to-br from-slate-900 to-slate-800 -mx-6 px-6 -mb-6 pb-6 pt-5 rounded-b-[1.5rem]">
                                        <div className="flex justify-between items-center mb-4">
                                            <span className="text-slate-400 text-sm font-medium">{t("inventory.totalItems")}</span>
                                            <span className="text-white text-2xl font-extrabold">{bulkStockOutItems.length}</span>
                                        </div>
                                        <div className="flex justify-between items-center mb-5">
                                            <span className="text-slate-400 text-sm font-medium">{t("inventory.totalUnitsOut")}</span>
                                            <span className="text-rose-400 text-lg font-bold">{bulkStockOutItems.reduce((s, i) => s + (i.qty || 0), 0)}</span>
                                        </div>
                                        <Button fullWidth type="submit" disabled={isSubmitting || bulkStockOutItems.length === 0} className="bg-rose-500 hover:bg-rose-600 text-white text-base font-bold py-4" data-testid="bulk-stockout-submit-btn">
                                            {isSubmitting ? (
                                                <><Loader2 className="animate-spin" size={20} /> {t("common.processing")}</>
                                            ) : (
                                                <><CheckCircle size={20} /> {t("inventory.confirmBulkOut")}</>
                                            )}
                                        </Button>
                                    </div>
                                </form>
                            </Card>
                        </div>
                    </div>
                </div>
            );
        }

        // Filter inventory based on category and low stock
        let displayInventory = filteredInventory;
        
        // Apply category filter (only for Operasi Stok, not settings)
        if (!isSettings && categoryFilter !== 'ALL') {
            displayInventory = displayInventory.filter(item => item.category === categoryFilter);
        }
        
        // Apply low stock filter (only for Operasi Stok, not settings)
        if (!isSettings && showLowStockOnly) {
            displayInventory = displayInventory.filter(item => item.quantity <= item.minLevel);
        }
        
        return (
        <div className="space-y-6">
            {/* Search and Filters */}
            {!isSettings ? (
                <>
                <Card>
                    <div className="space-y-4">
                        {/* Filters Row - Only for Operasi Stok */}
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                            {/* Search Bar */}
                            <div className="md:col-span-1">
                                <label className="block text-xs font-semibold text-slate-500 mb-2">
                                    <div className="flex items-center gap-1.5">
                                        <Search size={14} />
                                        <span>{language === "en" ? "SEARCH" : "CARIAN"}</span>
                                    </div>
                                </label>
                                <div className="relative">
                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                                    <input 
                                        className="w-full pl-10 pr-3 py-2.5 rounded-xl border border-slate-200 bg-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none transition-all text-sm font-medium text-slate-700" 
                                        placeholder={t('inventory.searchProduct')} 
                                        value={searchTerm} 
                                        onChange={e => setSearchTerm(e.target.value)} 
                                    />
                                </div>
                            </div>

                            {/* Low Stock Filter */}
                            <div>
                                <label className="block text-xs font-semibold text-slate-500 mb-2">
                                    <div className="flex items-center gap-1.5">
                                        <AlertCircle size={14} />
                                        <span>{language === 'en' ? 'FILTER' : 'PENAPIS'}</span>
                                    </div>
                                </label>
                                <label className="flex items-center gap-2.5 h-[42px] px-3 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 cursor-pointer transition-colors">
                                    <input
                                        type="checkbox"
                                        checked={showLowStockOnly}
                                        onChange={(e) => setShowLowStockOnly(e.target.checked)}
                                        className="w-4 h-4 rounded border-slate-300 text-rose-500 focus:ring-rose-500"
                                    />
                                    <AlertCircle size={16} className="text-rose-500" />
                                    <span className="text-sm font-medium text-slate-700">{t('dashboard.lowStock')}</span>
                                    {showLowStockOnly && displayInventory.length > 0 && (
                                        <span className="ml-auto px-2 py-0.5 rounded-full bg-rose-100 text-rose-700 text-xs font-bold">
                                            {displayInventory.length}
                                        </span>
                                    )}
                                </label>
                            </div>

                            {/* Category Filter */}
                            <div>
                                <label className="block text-xs font-semibold text-slate-500 mb-2">
                                    <div className="flex items-center gap-1.5">
                                        <Layers size={14} />
                                        <span>{language === 'en' ? 'CATEGORY' : 'KATEGORI'}</span>
                                    </div>
                                </label>
                                <div className="relative">
                                    <select
                                        value={categoryFilter}
                                        onChange={(e) => setCategoryFilter(e.target.value)}
                                        className="w-full px-3 py-2.5 pr-9 rounded-xl border border-slate-200 bg-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none transition-all appearance-none text-sm font-medium text-slate-700"
                                    >
                                        <option value="ALL">{t('inventory.allCategories')}</option>
                                        {projectCategories.map(cat => (
                                            <option key={cat} value={cat}>{cat}</option>
                                        ))}
                                    </select>
                                    <ChevronRight className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 rotate-90 pointer-events-none" size={16} />
                                </div>
                            </div>

                            {/* Keluar Pukal Button - 4th column */}
                            <div className="flex items-end">
                                <button
                                    onClick={() => setBulkStockOutMode(true)}
                                    className="w-full flex items-center justify-center gap-2 px-4 py-2.5 h-[42px] bg-rose-500 hover:bg-rose-600 text-white text-sm font-bold rounded-xl transition-colors shadow-lg shadow-rose-500/20"
                                    data-testid="bulk-stockout-btn"
                                >
                                    <Minus size={16} /> {t('inventory.bulkStockOut')}
                                </button>
                            </div>
                        </div>

                        {/* Active Filters */}
                        {(categoryFilter !== 'ALL' || showLowStockOnly) && (
                            <div className="flex items-center justify-between pt-2 border-t border-slate-100">
                                <div className="flex flex-wrap gap-2">
                                    {categoryFilter !== 'ALL' && (
                                        <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-medium">
                                            <Layers size={12} />
                                            {categoryFilter}
                                            <button onClick={() => setCategoryFilter('ALL')} className="ml-1 hover:text-blue-900">
                                                <X size={12} />
                                            </button>
                                        </span>
                                    )}
                                    {showLowStockOnly && (
                                        <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-rose-50 text-rose-700 rounded-full text-xs font-medium">
                                            <AlertCircle size={12} />
                                            {t("inventory.lowStockAlert")}
                                            <button onClick={() => setShowLowStockOnly(false)} className="ml-1 hover:text-rose-900">
                                                <X size={12} />
                                            </button>
                                        </span>
                                    )}
                                </div>
                                <Button 
                                    variant="ghost"                                    onClick={() => { setCategoryFilter('ALL'); setShowLowStockOnly(false); }}
                                    className="text-xs"
                                >
                                    <X size={14} />
                                    Reset
                                </Button>
                            </div>
                        )}
                    </div>
                </Card>
                </>
            ) : (
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:justify-between">
                    <div className="relative flex-1 sm:max-w-md">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                        <input 
                            className="w-full pl-10 pr-3 py-2.5 rounded-xl border border-slate-200 bg-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none transition-all text-sm font-medium text-slate-700" 
                            placeholder={t("inventory.searchProduct")} 
                            value={searchTerm} 
                            onChange={e => setSearchTerm(e.target.value)} 
                        />
                    </div>
                    <div className="flex gap-2">
                        <Button variant="secondary" onClick={() => setIsCategoryModalOpen(true)} icon={Layers}>{t("settings.manageCategory")}</Button>
                        <Button onClick={() => { setSelectedItem(null); setIsItemModalOpen(true); }} icon={Plus}>{t("settings.newProduct")}</Button>
                    </div>
                </div>
            )}
            
            {/* Table View */}
            <Card noPadding>
                {/* Desktop Table */}
                <div className="overflow-x-auto hidden md:block">
                    <table className="w-full text-sm">
                        <thead className="bg-gradient-to-r from-slate-50 to-slate-100/50 text-slate-600 font-semibold text-xs uppercase tracking-wider">
                            <tr>
                                <th className="p-4 text-left">{language === 'en' ? 'PRODUCT' : 'PRODUK'}</th>
                                <th className="p-4 text-left">{language === 'en' ? 'CATEGORY' : 'KATEGORI'}</th>
                                {can.viewFinancials && <th className="p-4 text-right">{language === 'en' ? 'PRICE' : 'HARGA'}</th>}
                                <th className="p-4 text-center">{language === 'en' ? 'STOCK' : 'STOK'}</th>
                                {!isSettings && <th className="p-4 text-left">{language === 'en' ? 'USAGE SINCE LAST PO' : 'GUNA SEJAK PO TERAKHIR'}</th>}
                                {isSettings && <th className="p-4 text-center">{language === 'en' ? 'BURST LIMIT (MONTHLY)' : 'HAD BURST (BULANAN)'}</th>}
                                <th className="p-4 text-center">{language === 'en' ? 'ACTIONS' : 'TINDAKAN'}</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 bg-white">
                            {displayInventory.map(item => {
                                // Get latest price change for this item
                                const itemPriceLogs = priceLogs.filter(log => log.itemId === item.id);
                                const latestPriceLog = itemPriceLogs.length > 0 ? itemPriceLogs[0] : null;
                                const latestMeta = latestPriceLog?.metadata;
                                const hasPriceChange = latestMeta && latestMeta.newPrice !== latestMeta.oldPrice;
                                const isPriceIncrease = hasPriceChange && latestMeta.newPrice > latestMeta.oldPrice;
                                
                                // Calculate usage since last completed PO
                                let usageSinceLastPO = null;
                                if (!isSettings) {
                                    // Find last completed PO containing this item
                                    const completedPOsWithItem = projectPOs
                                        .filter(po => po.status === 'Selesai' && po.items.some(i => i.id === item.id))
                                        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
                                    
                                    if (completedPOsWithItem.length > 0) {
                                        const lastPO = completedPOsWithItem[0];
                                        const poItem = lastPO.items.find(i => i.id === item.id);
                                        const poDate = new Date(lastPO.createdAt);
                                        
                                        // Calculate total OUT since PO date
                                        const outTransactions = projectTransactions.filter(tx => 
                                            tx.itemId === item.id && 
                                            tx.type === 'OUT' && 
                                            new Date(tx.timestamp) >= poDate
                                        );
                                        
                                        const totalUsed = outTransactions.reduce((sum, tx) => sum + tx.quantity, 0);
                                        
                                        usageSinceLastPO = {
                                            totalUsed,
                                            poNumber: lastPO.id,
                                            supplier: lastPO.supplier,
                                            orderedQty: poItem?.quantity || 0,
                                            receivedQty: poItem?.receivedQty || 0,
                                            poDate: poDate
                                        };
                                    }
                                }
                                
                                return (
                                <tr key={item.id} className="hover:bg-slate-50 transition-colors">
                                    <td className="p-4">
                                        <div className="flex items-center gap-3">
                                            <div 
                                                className="w-12 h-12 rounded-xl bg-gradient-to-br from-slate-100 to-slate-50 flex items-center justify-center overflow-hidden border border-slate-200 shadow-sm cursor-pointer hover:ring-2 hover:ring-emerald-500 transition-all"
                                                onClick={() => item.image && setViewingImage(item.image)}
                                                title={item.image ? "Klik untuk lihat imej penuh" : ""}
                                            >
                                                {item.image ? <img src={item.image} className="w-full h-full object-cover" alt=""/> : <Package size={20} className="text-slate-400"/>}
                                            </div>
                                            <div>
                                                <div className="font-bold text-slate-800">{item.name}</div>
                                                <div className="text-xs text-slate-400 font-mono mt-0.5">{item.sku}</div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="p-4">
                                        <Badge color="blue">{item.category}</Badge>
                                    </td>
                                    {can.viewFinancials && (
                                        <td className="p-4 text-right">
                                            <div className="flex items-center justify-end gap-1.5">
                                                <span className="font-bold text-slate-800">{formatCurrency(item.price)}</span>
                                                {isSettings && hasPriceChange && (
                                                    <div className={`flex items-center justify-center w-5 h-5 rounded ${
                                                        isPriceIncrease ? 'bg-emerald-100' : 'bg-rose-100'
                                                    }`} title={isPriceIncrease ? t('settings.priceUp') : t('settings.priceDown')}>
                                                        {isPriceIncrease ? (
                                                            <TrendingUp size={12} className="text-emerald-600" />
                                                        ) : (
                                                            <TrendingDown size={12} className="text-rose-600" />
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </td>
                                    )}
                                    <td className="p-4 text-center">
                                        <div className="flex items-center justify-center gap-2">
                                            <span className={`px-3 py-1.5 rounded-lg text-sm font-bold ${
                                                item.quantity <= item.minLevel 
                                                    ? 'bg-rose-100 text-rose-700 ring-2 ring-rose-200' 
                                                    : 'bg-emerald-100 text-emerald-700'
                                            }`}>
                                                {formatNumber(item.quantity)} {item.unit || 'pcs'}
                                            </span>
                                            {item.quantity <= item.minLevel && (
                                                <AlertCircle size={16} className="text-rose-500" title={t("inventory.lowStockAlert")} />
                                            )}
                                        </div>
                                    </td>
                                    {!isSettings && (
                                        <td className="p-4">
                                            {usageSinceLastPO ? (
                                                <div className="text-xs">
                                                    <div className="flex items-center gap-1.5 mb-1">
                                                        <ArrowDownLeft size={14} className="text-rose-500" />
                                                        <span className="font-bold text-rose-600">{usageSinceLastPO.totalUsed} {item.unit || 'pcs'}</span>
                                                        <span className="text-slate-400">digunakan</span>
                                                    </div>
                                                    <div className="text-slate-600">
                                                        <div className="font-medium">PO: {usageSinceLastPO.supplier}</div>
                                                        <div className="text-slate-400">
                                                            {language === 'en' ? 'Received' : 'Diterima'}: {usageSinceLastPO.receivedQty || usageSinceLastPO.orderedQty} {item.unit || 'pcs'}
                                                        </div>
                                                    </div>
                                                </div>
                                            ) : (
                                                <span className="text-xs text-slate-400">{t("dashboard.noCompletedPO")}</span>
                                            )}
                                        </td>
                                    )}
                                    {isSettings && (
                                        <td className="p-4 text-center">
                                            <span className="px-3 py-1.5 rounded-lg text-sm font-bold bg-rose-100 text-rose-700 ring-2 ring-rose-200">
                                                {item.burstLimit || 0}
                                            </span>
                                        </td>
                                    )}
                                    <td className="p-4 text-center">
                                        <div className="flex justify-center gap-2">
                                            {isSettings ? (
                                                <>
                                                    {can.viewFinancials && (
                                                        <button 
                                                            onClick={() => { setSelectedItemForPriceLog(item); setShowPriceLogModal(true); }} 
                                                            className="p-2.5 hover:bg-purple-50 rounded-xl text-purple-600 transition-colors border border-transparent hover:border-purple-200"
                                                            title={t("settings.priceLogTooltip")}
                                                        >
                                                            <DollarSign size={18}/>
                                                        </button>
                                                    )}
                                                    <button 
                                                        onClick={() => { setSelectedItem(item); setIsItemModalOpen(true); }} 
                                                        className="p-2.5 hover:bg-blue-50 rounded-xl text-blue-600 transition-colors border border-transparent hover:border-blue-200"
                                                        title={t("settings.editProductTooltip")}
                                                    >
                                                        <Edit2 size={18}/>
                                                    </button>
                                                    <button 
                                                        onClick={() => requestConfirm(t("inventory.deleteProduct"), t("inventory.deleteProductConfirm"), () => db.deleteItem(item.id).then(fetchData))} 
                                                        className="p-2.5 hover:bg-rose-50 rounded-xl text-rose-600 transition-colors border border-transparent hover:border-rose-200"
                                                        title={t("settings.deleteProductTooltip")}
                                                    >
                                                        <Trash2 size={18}/>
                                                    </button>
                                                </>
                                            ) : (
                                                <>
                                                    {can.doStockIn && (
                                                        <button 
                                                            onClick={() => { setSelectedItem(item); setStockAction('IN'); setIsStockModalOpen(true); }} 
                                                            className="px-3 py-2 rounded-xl bg-emerald-500 text-white hover:bg-emerald-600 transition-all shadow-sm hover:shadow-md font-medium text-sm flex items-center gap-1.5"
                                                            title={t("inventory.stockIn")}
                                                        >
                                                            <Plus size={16} />
                                                            {t("inventory.in")}
                                                        </button>
                                                    )}
                                                    {can.doStockOut && (
                                                        <button 
                                                            onClick={() => { setSelectedItem(item); setStockAction('OUT'); setIsStockModalOpen(true); }} 
                                                            className="px-3 py-2 rounded-xl bg-rose-500 text-white hover:bg-rose-600 transition-all shadow-sm hover:shadow-md font-medium text-sm flex items-center gap-1.5"
                                                            title={t("inventory.stockOut")}
                                                        >
                                                            <Minus size={16} />
                                                            {t("inventory.out")}
                                                        </button>
                                                    )}
                                                    <button 
                                                        onClick={() => openProductDetail(item)} 
                                                        className="px-3 py-2 rounded-xl bg-[#10056E] text-white hover:bg-[#0d0459] transition-all shadow-sm hover:shadow-md font-medium text-sm flex items-center gap-1.5"
                                                        title={language === 'en' ? 'View Details' : 'Lihat Butiran'}
                                                        data-testid={`view-product-${item.id}`}
                                                    >
                                                        <Eye size={16} />
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                                );
                            })}
                        </tbody>
                    </table>
                    {displayInventory.length === 0 && (
                        <div className="p-12 text-center bg-slate-50">
                            <Package size={48} className="mx-auto mb-3 text-slate-300" />
                            <p className="text-slate-400 text-sm font-medium">{t("settings.noProductsFound")}</p>
                            <p className="text-slate-400 text-xs mt-1">{language === "en" ? "Try adjusting your filters or search." : "Cuba laraskan penapis atau carian anda."}</p>
                        </div>
                    )}
                </div>

                {/* Mobile Card List */}
                <div className="md:hidden divide-y divide-slate-100">
                    {displayInventory.map(item => (
                        <div key={item.id} className="p-4 hover:bg-slate-50 transition-colors">
                            <div className="flex items-start gap-3 mb-3">
                                <div 
                                    className="w-11 h-11 rounded-xl bg-gradient-to-br from-slate-100 to-slate-50 flex items-center justify-center overflow-hidden border border-slate-200 shadow-sm flex-shrink-0 cursor-pointer"
                                    onClick={() => item.image && setViewingImage(item.image)}
                                >
                                    {item.image ? <img src={item.image} className="w-full h-full object-cover" alt=""/> : <Package size={18} className="text-slate-400"/>}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="font-bold text-slate-800 text-sm leading-tight">{item.name}</div>
                                    <div className="text-[11px] text-slate-400 font-mono mt-0.5">{item.sku}</div>
                                </div>
                                <span className={`px-2.5 py-1 rounded-lg text-xs font-bold flex-shrink-0 ${
                                    item.quantity <= item.minLevel 
                                        ? 'bg-rose-100 text-rose-700' 
                                        : 'bg-emerald-100 text-emerald-700'
                                }`}>
                                    {formatNumber(item.quantity)} {item.unit || 'pcs'}
                                </span>
                            </div>
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <Badge color="blue">{item.category}</Badge>
                                    {can.viewFinancials && <span className="text-xs font-bold text-slate-600">{formatCurrency(item.price)}</span>}
                                </div>
                                <div className="flex gap-1.5">
                                    {isSettings ? (
                                        <>
                                            <button onClick={() => { setSelectedItem(item); setIsItemModalOpen(true); }} className="p-2 hover:bg-blue-50 rounded-lg text-blue-600"><Edit2 size={16}/></button>
                                            <button onClick={() => requestConfirm(t("inventory.deleteProduct"), t("inventory.deleteProductConfirm"), () => db.deleteItem(item.id).then(fetchData))} className="p-2 hover:bg-rose-50 rounded-lg text-rose-600"><Trash2 size={16}/></button>
                                        </>
                                    ) : (
                                        <>
                                            {can.doStockIn && <button onClick={() => { setSelectedItem(item); setStockAction('IN'); setIsStockModalOpen(true); }} className="px-2.5 py-1.5 rounded-lg bg-emerald-500 text-white text-xs font-bold flex items-center gap-1"><Plus size={14}/>{t('inventory.in')}</button>}
                                            {can.doStockOut && <button onClick={() => { setSelectedItem(item); setStockAction('OUT'); setIsStockModalOpen(true); }} className="px-2.5 py-1.5 rounded-lg bg-rose-500 text-white text-xs font-bold flex items-center gap-1"><Minus size={14}/>{t('inventory.out')}</button>}
                                            <button onClick={() => openProductDetail(item)} className="p-1.5 rounded-lg bg-[#10056E] text-white"><Eye size={14}/></button>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                    {displayInventory.length === 0 && (
                        <div className="p-12 text-center">
                            <Package size={48} className="mx-auto mb-3 text-slate-300" />
                            <p className="text-slate-400 text-sm font-medium">{t("settings.noProductsFound")}</p>
                        </div>
                    )}
                </div>
            </Card>
        </div>
        );
    };

    const renderPurchaseOrders = () => {
        // Helper functions for calendar
        const getDaysInMonth = (date) => new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
        const getFirstDayOfMonth = (date) => new Date(date.getFullYear(), date.getMonth(), 1).getDay();
        const MONTHS_MS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
        const DAYS_MS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
        
        const getPOsForDate = (date) => {
            return filteredPOs.filter(po => {
                const poDate = new Date(po.date);
                return poDate.getDate() === date && 
                       poDate.getMonth() === poCalendarDate.getMonth() && 
                       poDate.getFullYear() === poCalendarDate.getFullYear();
            });
        };

        // CREATE PO VIEW
        if (poViewMode === 'create') return (
            <div className="space-y-6">
                <Button variant="ghost" onClick={() => { setPoViewMode('list'); setEditingPO(null); setPoItems([]); }} icon={ArrowLeft} className="pl-0 hover:bg-transparent hover:text-[#10056E]">{t('po.backToList')}</Button>
                <div className="flex items-center justify-between">
                    <div>
                        <h2 className="text-3xl font-bold text-slate-900">{editingPO ? t('po.editPO') : t('po.createPO')}</h2>
                        <p className="text-slate-500 mt-1">{editingPO ? `${t('po.editPO')} #${editingPO.po_number || editingPO.id?.slice(-6).toUpperCase()}` : t('po.createPO')}</p>
                    </div>
                </div>
                
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Left Column - Items */}
                    <div className="lg:col-span-2 space-y-6">
                        <Card>
                            <div className="flex items-center gap-3 mb-6">
                                <div className="p-2 bg-[#10056E]/10 rounded-xl">
                                    <ShoppingBag size={20} className="text-[#10056E]" />
                                </div>
                                <div>
                                    <h3 className="font-bold text-lg text-slate-800">{t('po.addItem')}</h3>
                                    <p className="text-xs text-slate-500">{t('inventory.selectFromInventory')}</p>
                                </div>
                            </div>
                            <div className="flex flex-col md:flex-row gap-4 items-start bg-gradient-to-br from-slate-50 to-emerald-50/30 p-5 rounded-2xl border border-slate-200">
                                <div className="flex-1 w-full">
                                    <label className="block text-xs font-bold text-slate-600 mb-2">{language === 'en' ? 'SELECT PRODUCT *' : 'PILIH PRODUK *'}</label>
                                    
                                    {/* Searchable Dropdown */}
                                    <SearchableDropdown
                                        options={projectInventory}
                                        value={currentPOItem.id}
                                        onChange={(value) => {
                                            const i = projectInventory.find(x => x.id === value);
                                            setCurrentPOItem({ ...currentPOItem, id: value, cost: i?.price || 0 });
                                        }}
                                        placeholder={t('po.selectItem')}
                                        searchPlaceholder={t('po.searchProduct')}
                                        getOptionLabel={(option) => `${option.name} (${option.sku}) - ${t('dashboard.stockLabel')}: ${option.quantity} ${option.unit || 'pcs'}`}
                                        getOptionValue={(option) => option.id}
                                        noResultsText={t('po.noProductFound')}
                                    />
                                    
                                    {/* SKU Info Overlay - Show when item is selected */}
                                    {currentPOItem.id && (() => {
                                        const selectedItem = projectInventory.find(x => x.id === currentPOItem.id);
                                        return selectedItem ? (
                                            <div className="mt-2 px-3 py-2 bg-blue-50 rounded-lg border border-blue-200 shadow-md">
                                                <div className="flex items-center gap-3 text-xs">
                                                    <span className="font-semibold text-blue-900">SKU: {selectedItem.sku}</span>
                                                    <span className="text-blue-300">|</span>
                                                    <span className="text-blue-700">Unit: {selectedItem.unit || 'pcs'}</span>
                                                    <span className="text-blue-300">|</span>
                                                    <span className={selectedItem.quantity < selectedItem.minLevel ? 'text-orange-600 font-semibold' : 'text-emerald-600 font-semibold'}>
                                                        {t('dashboard.stockLabel')}: {selectedItem.quantity}
                                                    </span>
                                                </div>
                                            </div>
                                        ) : null;
                                    })()}
                                </div>
                                <div className="w-28 mt-6 md:mt-0">
                                    <label className="block text-xs font-bold text-slate-600 mb-2">QTY *</label>
                                    <input 
                                        type="number" 
                                        value={currentPOItem.qty} 
                                        onChange={e => setCurrentPOItem({ ...currentPOItem, qty: e.target.value })}
                                        className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none transition-all text-center font-bold text-slate-700"
                                        min="1"
                                    />
                                </div>
                                {can.viewFinancials && (
                                    <div className="w-36 mt-6 md:mt-0">
                                        <label className="block text-xs font-bold text-slate-600 mb-2">{language === "en" ? "COST/UNIT (RM)" : "KOS/UNIT (RM)"} *</label>
                                        <input 
                                            type="number" 
                                            step="0.01"
                                            value={currentPOItem.cost} 
                                            onChange={e => setCurrentPOItem({ ...currentPOItem, cost: e.target.value })}
                                            className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none transition-all text-slate-700 font-medium"
                                            min="0"
                                        />
                                    </div>
                                )}
                                <div className="mt-6 md:mt-0">
                                    <label className="block text-xs font-bold text-slate-600 mb-2 opacity-0">.</label>
                                    <Button 
                                        onClick={() => { 
                                            if (!currentPOItem.id) return; 
                                            const p = projectInventory.find(x => x.id === currentPOItem.id); 
                                            setPoItems([...poItems, { ...p, quantity: parseInt(currentPOItem.qty), cost: parseFloat(currentPOItem.cost) }]); 
                                            setCurrentPOItem({ id: '', qty: 1, cost: 0 });
                                        }} 
                                        icon={Plus}
                                        className="whitespace-nowrap bg-[#10056E] hover:bg-[#0d0459] shadow-lg shadow-[#10056E]/30"
                                    >
                                        {t('common.add')}
                                    </Button>
                                </div>
                            </div>
                            
                            {/* Items Table */}
                            <div className="mt-6">
                                <div className="flex items-center justify-between mb-4">
                                    <h4 className="font-bold text-slate-700 flex items-center gap-2">
                                        <span>{t('inventory.itemDetails')}</span>
                                        <span className="px-2 py-0.5 bg-[#10056E]/10 text-[#10056E] rounded-full text-xs font-bold">{poItems.length}</span>
                                    </h4>
                                </div>
                                <div className="overflow-hidden rounded-2xl border border-slate-200 shadow-sm">
                                    <table className="w-full text-sm">
                                        <thead className="bg-gradient-to-r from-slate-50 to-slate-100/50 text-slate-600 font-semibold text-xs uppercase tracking-wider">
                                            <tr>
                                                <th className="p-4 text-left">{language === "en" ? "ITEM DETAILS" : "BUTIRAN ITEM"}</th>
                                                <th className="p-4 text-center">UNIT</th>
                                                <th className="p-4 text-center">QTY</th>
                                                {can.viewFinancials && (
                                                    <>
                                                        <th className="p-4 text-right">{language === "en" ? "UNIT COST" : "KOS UNIT"}</th>
                                                        <th className="p-4 text-right">{language === "en" ? "AMOUNT" : "JUMLAH"}</th>
                                                    </>
                                                )}
                                                <th className="p-4 text-center">{language === "en" ? "ACTIONS" : "TINDAKAN"}</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-100 bg-white">
                                            {poItems.map((item, idx) => (
                                                <tr key={idx} className="hover:bg-slate-50 transition-colors">
                                                    <td className="p-4">
                                                        <div className="font-bold text-slate-800">{item.name}</div>
                                                        <div className="text-xs text-slate-400 font-medium">{item.sku}</div>
                                                    </td>
                                                    <td className="p-4 text-center">
                                                        <span className="px-2 py-1 bg-slate-100 rounded-md text-slate-600 font-medium text-xs">{item.unit || 'pcs'}</span>
                                                    </td>
                                                    <td className="p-4 text-center">
                                                        <input
                                                            type="number"
                                                            min="1"
                                                            value={item.quantity}
                                                            onChange={(e) => {
                                                                const newQty = parseInt(e.target.value) || 1;
                                                                const updatedItems = [...poItems];
                                                                updatedItems[idx] = { ...item, quantity: newQty };
                                                                setPoItems(updatedItems);
                                                            }}
                                                            className="w-20 px-2 py-1.5 text-center font-bold text-slate-800 border border-slate-200 rounded-lg focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none transition-all hover:border-emerald-400"
                                                        />
                                                    </td>
                                                    {can.viewFinancials && (
                                                        <>
                                                            <td className="p-4 text-right">
                                                                <input
                                                                    type="number"
                                                                    min="0"
                                                                    step="0.01"
                                                                    value={item.cost}
                                                                    onChange={(e) => {
                                                                        const newCost = parseFloat(e.target.value) || 0;
                                                                        const updatedItems = [...poItems];
                                                                        updatedItems[idx] = { ...item, cost: newCost };
                                                                        setPoItems(updatedItems);
                                                                    }}
                                                                    className="w-28 px-2 py-1.5 text-right font-medium text-slate-700 border border-slate-200 rounded-lg focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none transition-all hover:border-emerald-400"
                                                                />
                                                            </td>
                                                            <td className="p-4 text-right font-bold text-[#10056E]">{formatCurrency(item.quantity * item.cost)}</td>
                                                        </>
                                                    )}
                                                    <td className="p-4 text-center">
                                                        <button 
                                                            onClick={() => setPoItems(poItems.filter((_, x) => x !== idx))} 
                                                            className="p-2 hover:bg-rose-50 rounded-lg text-rose-500 transition-colors"
                                                            title={t("po.deleteItem")}
                                                        >
                                                            <Trash2 size={16}/>
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                    {poItems.length === 0 && (
                                        <div className="p-12 text-center bg-slate-50">
                                            <Package size={48} className="mx-auto mb-3 text-slate-300" />
                                            <p className="text-slate-400 text-sm font-medium">{t("inventory.noItemsYet")}</p>
                                            <p className="text-slate-400 text-xs mt-1">{t("inventory.selectFromDropdown")}</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </Card>
                    </div>
                    
                    {/* Right Column - Order Details */}
                    <div className="space-y-6">
                        <Card className="sticky top-6 h-fit !pb-0">
                            <div className="flex items-center gap-3 mb-6">
                                <div className="p-2 bg-blue-50 rounded-xl">
                                    <FileText size={20} className="text-blue-600" />
                                </div>
                                <div>
                                    <h3 className="font-bold text-lg text-slate-800">{t("po.orderItems")}</h3>
                                    <p className="text-xs text-slate-500">{t("po.supplier")}</p>
                                </div>
                            </div>
                            <form onSubmit={handleCreatePO} className="space-y-5" key={editingPO?.id || 'new'}>
                                <div>
                                    <label className="block text-xs font-bold text-slate-600 mb-2">{language === "en" ? "SUPPLIER" : "PEMBEKAL"} *</label>
                                    <div className="flex gap-2">
                                        <div className="flex-1 relative">
                                            <select name="supplier" required defaultValue={editingPO?.supplier || ''} className="w-full h-[46px] px-4 py-3 pr-10 rounded-xl border border-slate-200 bg-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none transition-all appearance-none font-medium text-slate-700">
                                                <option value="">{t('po.selectSupplier')}...</option>
                                                {projectSuppliers.map(s => <option key={s.id} value={s.name}>{s.name}</option>)}
                                            </select>
                                            <ChevronRight className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 rotate-90 pointer-events-none" size={16} />
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => { setEditingSupplier(null); setIsSupplierModalOpen(true); }}
                                            className="w-[46px] h-[46px] flex items-center justify-center rounded-xl bg-[#10056E] hover:bg-[#0d0459] text-white transition-all shadow-md hover:shadow-lg flex-shrink-0"
                                            title={t('po.addNewSupplier')}
                                        >
                                            <Plus size={20} />
                                        </button>
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-slate-600 mb-2">{language === "en" ? "ORDER DATE" : "TARIKH PESANAN"} *</label>
                                    <input 
                                        name="date" 
                                        type="date" 
                                        required 
                                        defaultValue={editingPO?.date ? new Date(editingPO.date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0]}
                                        className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none transition-all font-medium text-slate-700"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-slate-600 mb-2">{language === "en" ? "NOTES" : "NOTA"}</label>
                                    <textarea 
                                        name="notes" 
                                        placeholder={t('po.notesPlaceholder')}
                                        rows={4}
                                        defaultValue={editingPO?.notes || ''}
                                        className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none transition-all resize-none text-slate-700"
                                    />
                                </div>
                                
                                {/* Summary Panel */}
                                <div className="mt-6 pt-5 border-t border-slate-200 bg-gradient-to-br from-slate-900 to-slate-800 -mx-8 px-8 -mb-8 pb-8 rounded-b-[2rem]">
                                    <div className="flex items-center justify-between text-white mb-2">
                                        <span className="text-sm font-medium opacity-80">{t("inventory.totalItems")}</span>
                                        <span className="text-2xl font-bold">{poItems.length}</span>
                                    </div>
                                    {can.viewFinancials && (
                                        <div className="flex items-center justify-between text-white mb-5 pb-5 border-b border-slate-700">
                                            <span className="text-sm font-medium opacity-80">{t("po.grandTotal")}</span>
                                            <span className="text-2xl font-extrabold text-[#10056E]">{formatCurrency(poItems.reduce((acc, i) => acc + (i.quantity * i.cost), 0))}</span>
                                        </div>
                                    )}
                                    <Button fullWidth type="submit" disabled={isSubmitting} className="bg-[#10056E] hover:bg-[#0d0459] text-white text-base font-bold py-4">
                                        {isSubmitting ? (
                                            <><Loader2 className="animate-spin" size={20} /> {t("common.processing")}</>
                                        ) : (
                                            <><CheckCircle size={20} /> {editingPO ? t('po.updatePO') : t('po.createPO')}</>
                                        )}
                                    </Button>
                                </div>
                            </form>
                        </Card>
                    </div>
                </div>
            </div>
        );

        // LIST VIEW
        return (
            <div className="space-y-6">
                {/* Filters + Actions - Inline on desktop, stacked on mobile */}
                <Card>
                    <div className="space-y-4 md:space-y-0">
                        <div className="flex flex-col md:flex-row md:items-end md:gap-3">
                            {/* Supplier Filter */}
                            <div className="mb-3 md:mb-0 md:flex-1 md:min-w-0">
                                <label className="block text-xs font-semibold text-slate-500 mb-2 md:hidden">
                                    <div className="flex items-center gap-1.5">
                                        <Store size={14} />
                                        <span>{language === "en" ? "SUPPLIER" : "PEMBEKAL"}</span>
                                    </div>
                                </label>
                                <div className="relative">
                                    <select 
                                        value={poFilterSupplier} 
                                        onChange={(e) => setPoFilterSupplier(e.target.value)}
                                        className="w-full px-3 py-2.5 pr-9 rounded-xl border border-slate-200 bg-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none transition-all appearance-none text-sm font-medium text-slate-700"
                                    >
                                        <option value="All">{t('po.allSuppliers')}</option>
                                        {[...new Set(projectPOs.map(po => po.supplier))].map(s => (
                                            <option key={s} value={s}>{s}</option>
                                        ))}
                                    </select>
                                    <ChevronRight className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 rotate-90 pointer-events-none" size={16} />
                                </div>
                            </div>

                            {/* Status Filter */}
                            <div className="mb-3 md:mb-0 md:flex-1 md:min-w-0">
                                <label className="block text-xs font-semibold text-slate-500 mb-2 md:hidden">
                                    <div className="flex items-center gap-1.5">
                                        <CheckCircle size={14} />
                                        <span>STATUS</span>
                                    </div>
                                </label>
                                <div className="relative">
                                    <select 
                                        value={poFilterStatus} 
                                        onChange={(e) => setPoFilterStatus(e.target.value)}
                                        className="w-full px-3 py-2.5 pr-9 rounded-xl border border-slate-200 bg-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none transition-all appearance-none text-sm font-medium text-slate-700"
                                    >
                                        <option value="All">{t('po.allStatuses')}</option>
                                        <option value="Belum Selesai">{t('po.incomplete')}</option>
                                        <option value="Selesai">{t("po.completed")}</option>
                                        <option value="Batal">{t("po.cancelled")}</option>
                                    </select>
                                    <ChevronRight className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 rotate-90 pointer-events-none" size={16} />
                                </div>
                            </div>

                            {/* Date Filter */}
                            <div className="mb-3 md:mb-0 md:flex-1 md:min-w-0">
                                <label className="block text-xs font-semibold text-slate-500 mb-2 md:hidden">
                                    <div className="flex items-center gap-1.5">
                                        <CalendarIcon size={14} />
                                        <span>{language === "en" ? "DATE RANGE" : "JULAT TARIKH"}</span>
                                    </div>
                                </label>
                                <div className="relative">
                                    <select 
                                        value={poDateFilter}
                                        onChange={(e) => setPoDateFilter(e.target.value)}
                                        className="w-full px-3 py-2.5 pr-9 rounded-xl border border-slate-200 bg-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none transition-all appearance-none text-sm font-medium text-slate-700"
                                    >
                                        <option value="ALL_TIME">{t('po.allTime')}</option>
                                        <option value="TODAY">{t('po.today')}</option>
                                        <option value="YESTERDAY">{t('po.yesterday')}</option>
                                        <option value="THIS_WEEK">{t('po.thisWeek')}</option>
                                        <option value="LAST_WEEK">{t('po.lastWeek')}</option>
                                        <option value="LAST_7_DAYS">{language === 'en' ? 'Last 7 Days' : '7 Hari Terakhir'}</option>
                                        <option value="THIS_MONTH">{t('po.thisMonth')}</option>
                                        <option value="LAST_MONTH">{t('po.lastMonth')}</option>
                                        <option value="LAST_30_DAYS">{language === 'en' ? 'Last 30 Days' : '30 Hari Terakhir'}</option>
                                        <option value="CUSTOM_RANGE">{t('po.customRange')}</option>
                                    </select>
                                    <ChevronRight className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 rotate-90 pointer-events-none" size={16} />
                                </div>
                            </div>

                            {/* Discrepancy Filter */}
                            <div className="mb-3 md:mb-0">
                                <label className="block text-xs font-semibold text-slate-500 mb-2 md:hidden">
                                    <div className="flex items-center gap-1.5">
                                        <Filter size={14} />
                                        <span>LAIN-LAIN</span>
                                    </div>
                                </label>
                                <label className="flex items-center gap-2.5 h-[42px] px-3 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 cursor-pointer transition-colors whitespace-nowrap">
                                    <input 
                                        type="checkbox"
                                        checked={poFilterDiscrepancy}
                                        onChange={(e) => setPoFilterDiscrepancy(e.target.checked)}
                                        className="w-4 h-4 rounded border-slate-300 text-orange-500 focus:ring-orange-500"
                                    />
                                    <AlertCircle size={16} className="text-orange-500" />
                                    <span className="text-sm font-medium text-slate-700">Discrepancy</span>
                                </label>
                            </div>

                            {/* View Toggle + PO Baru */}
                            <div className="flex items-center gap-3">
                                <div className="flex items-center bg-white rounded-xl border border-slate-200 p-1 shadow-sm">
                                    <button 
                                        onClick={() => setPoListViewType('table')}
                                        className={`p-2.5 rounded-[10px] transition-all ${poListViewType === 'table' ? 'bg-[#10056E] text-white shadow-[#10056E]/30 shadow-lg' : 'text-slate-400 hover:text-[#10056E]'}`}
                                        title={t('po.listView')}
                                    >
                                        <LayoutGrid size={18} />
                                    </button>
                                    <button 
                                        onClick={() => setPoListViewType('calendar')}
                                        className={`p-2.5 rounded-[10px] transition-all ${poListViewType === 'calendar' ? 'bg-[#10056E] text-white shadow-[#10056E]/30 shadow-lg' : 'text-slate-400 hover:text-[#10056E]'}`}
                                        title={t('po.calendarView')}
                                    >
                                        <Calendar size={18} />
                                    </button>
                                </div>
                                <Button onClick={() => setPoViewMode('create')} icon={Plus} className="whitespace-nowrap">{t('po.newPO')}</Button>
                            </div>
                        </div>

                        {/* Custom Date Range */}
                        {poDateFilter === 'CUSTOM_RANGE' && (
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
                                <div>
                                    <label className="block text-xs font-semibold text-slate-500 mb-2">{language === "en" ? "FROM DATE" : "DARI TARIKH"}</label>
                                    <input 
                                        type="date"
                                        value={customDateFrom}
                                        onChange={(e) => setCustomDateFrom(e.target.value)}
                                        className="w-full px-3 py-2.5 rounded-xl border border-slate-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none transition-all text-sm"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-semibold text-slate-500 mb-2">{language === "en" ? "TO DATE" : "HINGGA TARIKH"}</label>
                                    <input 
                                        type="date"
                                        value={customDateTo}
                                        onChange={(e) => setCustomDateTo(e.target.value)}
                                        className="w-full px-3 py-2.5 rounded-xl border border-slate-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none transition-all text-sm"
                                    />
                                </div>
                            </div>
                        )}

                        {/* Active Filters & Reset */}
                        <div className="flex items-center justify-between pt-2 border-t border-slate-100">
                            <div className="flex flex-wrap gap-2">
                                {poFilterSupplier !== 'All' && (
                                    <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-[#10056E]/10 text-[#10056E] rounded-full text-xs font-medium">
                                        <Store size={12} />
                                        {poFilterSupplier}
                                        <button onClick={() => setPoFilterSupplier('All')} className="ml-1 hover:text-[#0d0459]">
                                            <X size={12} />
                                        </button>
                                    </span>
                                )}
                                {poFilterStatus !== 'All' && (
                                    <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-medium">
                                        <CheckCircle size={12} />
                                        {poFilterStatus}
                                        <button onClick={() => setPoFilterStatus('All')} className="ml-1 hover:text-blue-900">
                                            <X size={12} />
                                        </button>
                                    </span>
                                )}
                                {poDateFilter !== 'ALL_TIME' && (
                                    <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-purple-50 text-purple-700 rounded-full text-xs font-medium">
                                        <CalendarIcon size={12} />
                                        {poDateFilter}
                                        <button onClick={() => { setPoDateFilter('ALL_TIME'); setCustomDateFrom(''); setCustomDateTo(''); }} className="ml-1 hover:text-purple-900">
                                            <X size={12} />
                                        </button>
                                    </span>
                                )}
                                {poFilterDiscrepancy && (
                                    <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-orange-50 text-orange-700 rounded-full text-xs font-medium">
                                        <AlertCircle size={12} />
                                        Discrepancy
                                        <button onClick={() => setPoFilterDiscrepancy(false)} className="ml-1 hover:text-orange-900">
                                            <X size={12} />
                                        </button>
                                    </span>
                                )}
                            </div>
                            {(poFilterSupplier !== 'All' || poFilterStatus !== 'All' || poDateFilter !== 'ALL_TIME' || poFilterDiscrepancy) && (
                                <Button 
                                    variant="ghost" 
                                    onClick={() => { setPoFilterSupplier('All'); setPoFilterStatus('All'); setPoDateFilter('ALL_TIME'); setCustomDateFrom(''); setCustomDateTo(''); setPoFilterDiscrepancy(false); }}
                                    className="text-xs"
                                >
                                    <X size={14} />
                                    Reset Semua
                                </Button>
                            )}
                        </div>
                    </div>
                </Card>

                {/* Table or Calendar View */}
                {poListViewType === 'table' ? (
                    <Card noPadding>
                        {/* Desktop Table */}
                        <div className="overflow-x-auto hidden md:block">
                            <table className="w-full text-sm">
                                <thead className="bg-slate-50 text-slate-500 font-medium text-xs uppercase tracking-wider">
                                    <tr>
                                        <th className="p-4 text-left">PO NO</th>
                                        <th className="p-4 text-left">{language === "en" ? "DATE" : "TARIKH"}</th>
                                        <th className="p-4 text-left">{language === "en" ? "SUPPLIER" : "PEMBEKAL"}</th>
                                        <th className="p-4 text-center">{language === "en" ? "ITEMS" : "ITEM"}</th>
                                        {can.viewFinancials && <th className="p-4 text-right">{language === "en" ? "TOTAL COST" : "JUMLAH KOS"}</th>}
                                        <th className="p-4 text-center">STATUS</th>
                                        <th className="p-4 text-center">{language === "en" ? "ACTIONS" : "TINDAKAN"}</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100">
                                    {filteredPOs.map(po => {
                                        const hasDiscrepancy = po.hasDiscrepancy || po.items?.some(i => i.receivedQty !== undefined && i.receivedQty !== i.quantity);
                                        const kurangItems = po.items?.filter(i => i.receivedQty !== undefined && i.receivedQty < i.quantity) || [];
                                        const lebihItems = po.items?.filter(i => i.receivedQty !== undefined && i.receivedQty > i.quantity) || [];
                                        
                                        return (
                                        <tr 
                                            key={po.id} 
                                            onClick={() => setSelectedPOId(po.id)}
                                            className="hover:bg-slate-50 transition-colors cursor-pointer"
                                        >
                                            <td className="p-4">
                                                <div className="flex items-center gap-2">
                                                    <span className="font-bold text-slate-700">#{po.id.slice(-6).toUpperCase()}</span>
                                                    {po.status === 'Selesai' && hasDiscrepancy && (
                                                        <div className="flex gap-1.5">
                                                            {kurangItems.length > 0 && (
                                                                <div className="flex items-center gap-1 px-2 py-0.5 bg-orange-100 rounded-md" title={`${kurangItems.length} item kurang`}>
                                                                    <TrendingDown size={14} className="text-orange-600" />
                                                                    <span className="text-xs font-bold text-orange-700">{kurangItems.length}</span>
                                                                </div>
                                                            )}
                                                            {lebihItems.length > 0 && (
                                                                <div className="flex items-center gap-1 px-2 py-0.5 bg-blue-100 rounded-md" title={`${lebihItems.length} item lebih`}>
                                                                    <TrendingUp size={14} className="text-blue-600" />
                                                                    <span className="text-xs font-bold text-blue-700">{lebihItems.length}</span>
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="p-4">
                                                <div className="flex items-center gap-2 text-slate-600">
                                                    <CalendarIcon size={14} className="text-slate-400" />
                                                    {new Date(po.date).toLocaleDateString('en-MY')}
                                                </div>
                                            </td>
                                            <td className="p-4 font-medium text-slate-700">{po.supplier}</td>
                                            <td className="p-4 text-center text-slate-600">{po.items.length} items</td>
                                            {can.viewFinancials && <td className="p-4 text-right font-bold text-slate-800">{formatCurrency(po.totalCost)}</td>}
                                            <td className="p-4 text-center">
                                                <Badge color={
                                                    po.status === 'Selesai' ? 'green' : 
                                                    po.status === 'Batal' ? 'slate' : 
                                                    'blue'
                                                }>{po.status === 'Selesai' ? t("po.completed") : po.status === 'Batal' ? t("po.cancelled") : t("po.incomplete")}</Badge>
                                            </td>
                                            <td className="p-4 text-center">
                                                <ChevronRight size={18} className="text-slate-400" />
                                            </td>
                                        </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>

                        {/* Mobile Card List */}
                        <div className="md:hidden divide-y divide-slate-100">
                            {filteredPOs.map(po => (
                                <div key={po.id} onClick={() => setSelectedPOId(po.id)} className="p-4 hover:bg-slate-50 transition-colors cursor-pointer active:bg-slate-100">
                                    <div className="flex items-center justify-between mb-2">
                                        <span className="font-bold text-slate-800 text-sm">#{po.id.slice(-6).toUpperCase()}</span>
                                        <Badge color={po.status === 'Selesai' ? 'green' : po.status === 'Batal' ? 'slate' : 'blue'}>{po.status === 'Selesai' ? t("po.completed") : po.status === 'Batal' ? t("po.cancelled") : t("po.incomplete")}</Badge>
                                    </div>
                                    <div className="text-sm font-medium text-slate-700 mb-1.5">{po.supplier}</div>
                                    <div className="flex items-center justify-between text-xs text-slate-500">
                                        <div className="flex items-center gap-3">
                                            <span className="flex items-center gap-1"><CalendarIcon size={12}/> {new Date(po.date).toLocaleDateString('en-MY')}</span>
                                            <span>{po.items.length} items</span>
                                        </div>
                                        {can.viewFinancials && <span className="font-bold text-slate-700">{formatCurrency(po.totalCost)}</span>}
                                    </div>
                                </div>
                            ))}
                        </div>

                        {filteredPOs.length === 0 && (
                            <div className="p-12 text-center text-slate-400">
                                <ShoppingBag size={48} className="mx-auto mb-4 opacity-20" />
                                <p>{t("po.noPOFound")}</p>
                            </div>
                        )}
                    </Card>
                ) : (
                    // CALENDAR VIEW - Redesigned for Mobile
                    <Card>
                        {/* Calendar Header */}
                        <div className="flex items-center justify-between mb-6">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-[#10056E]/10 rounded-xl">
                                    <CalendarIcon size={24} className="text-[#10056E]" />
                                </div>
                                <div>
                                    <h3 className="text-2xl font-bold text-slate-900">
                                        {MONTHS_MS[poCalendarDate.getMonth()]} {poCalendarDate.getFullYear()}
                                    </h3>
                                    <p className="text-sm text-slate-500 mt-0.5">{filteredPOs.length} POs this month</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-2 bg-slate-50 rounded-xl p-1">
                                <button 
                                    onClick={() => setPoCalendarDate(new Date(poCalendarDate.getFullYear(), poCalendarDate.getMonth() - 1, 1))}
                                    className="p-2.5 hover:bg-white rounded-lg transition-all shadow-sm"
                                    title="Previous Month"
                                >
                                    <ChevronLeft size={20} className="text-slate-600" />
                                </button>
                                <button 
                                    onClick={() => setPoCalendarDate(new Date())}
                                    className="px-3 py-2 text-sm font-medium text-slate-700 hover:bg-white rounded-lg transition-all"
                                    title="Today"
                                >
                                    Today
                                </button>
                                <button 
                                    onClick={() => setPoCalendarDate(new Date(poCalendarDate.getFullYear(), poCalendarDate.getMonth() + 1, 1))}
                                    className="p-2.5 hover:bg-white rounded-lg transition-all shadow-sm"
                                    title="Next Month"
                                >
                                    <ChevronRight size={20} className="text-slate-600" />
                                </button>
                            </div>
                        </div>
                        
                        {/* Calendar Grid */}
                        <div className="grid grid-cols-7 gap-1 sm:gap-2">
                            {/* Day Headers */}
                            {DAYS_MS.map(day => (
                                <div key={day} className="text-center text-xs font-bold text-slate-500 uppercase py-2 sm:py-3">
                                    <span className="hidden sm:inline">{['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][DAYS_MS.indexOf(day)]}</span>
                                    <span className="sm:hidden">{day}</span>
                                </div>
                            ))}
                            
                            {/* Empty cells for offset */}
                            {Array.from({ length: getFirstDayOfMonth(poCalendarDate) }).map((_, i) => (
                                <div key={`empty-${i}`} className="aspect-square" />
                            ))}
                            
                            {/* Date cells */}
                            {Array.from({ length: getDaysInMonth(poCalendarDate) }).map((_, i) => {
                                const date = i + 1;
                                const posForDate = getPOsForDate(date);
                                const isToday = new Date().getDate() === date && 
                                               new Date().getMonth() === poCalendarDate.getMonth() && 
                                               new Date().getFullYear() === poCalendarDate.getFullYear();
                                const hasPOs = posForDate.length > 0;
                                
                                return (
                                    <div 
                                        key={date} 
                                        className={`
                                            aspect-square border rounded-xl p-1 sm:p-2 transition-all cursor-pointer group
                                            ${isToday ? 'bg-[#10056E]/10 border-[#10056E] ring-2 ring-[#10056E]/20' : 
                                              hasPOs ? 'bg-blue-50 border-blue-200 hover:border-blue-400 hover:shadow-md' : 
                                              'bg-white border-slate-100 hover:border-slate-300'}
                                        `}
                                    >
                                        <div className="h-full flex flex-col">
                                            {/* Date Number */}
                                            <div className={`text-xs sm:text-sm font-bold mb-1 ${
                                                isToday ? 'text-[#10056E]' : 
                                                hasPOs ? 'text-blue-700' : 
                                                'text-slate-600'
                                            }`}>
                                                {date}
                                            </div>
                                            
                                            {/* PO Indicators */}
                                            {hasPOs && (
                                                <div className="flex-1 flex flex-col gap-0.5 overflow-hidden">
                                                    {/* Desktop View: Show mini PO cards */}
                                                    <div className="hidden sm:flex flex-col gap-0.5">
                                                        {posForDate.slice(0, 2).map((po) => (
                                                            <div 
                                                                key={po.id}
                                                                onClick={() => setSelectedPOId(po.id)}
                                                                className={`text-[10px] px-1.5 py-0.5 rounded-md cursor-pointer truncate transition-all hover:scale-105 ${
                                                                    po.status === 'Selesai' ? 'bg-emerald-500 text-white' : 
                                                                    po.status === 'Batal' ? 'bg-slate-400 text-white' :
                                                                    'bg-[#10056E] text-white'
                                                                }`}
                                                                title={`${po.supplier} - ${formatCurrency(po.totalCost)}`}
                                                            >
                                                                {po.supplier}
                                                            </div>
                                                        ))}
                                                    </div>
                                                    
                                                    {/* Mobile View: Show count badge */}
                                                    <div className="sm:hidden flex items-center justify-center flex-1">
                                                        <div className="w-5 h-5 rounded-full bg-blue-500 text-white text-[10px] font-bold flex items-center justify-center">
                                                            {posForDate.length}
                                                        </div>
                                                    </div>
                                                    
                                                    {/* More indicator */}
                                                    {posForDate.length > 2 && (
                                                        <div className="hidden sm:block text-[9px] text-blue-600 text-center font-medium">
                                                            +{posForDate.length - 2} more
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        
                        {/* Calendar Legend */}
                        <div className="mt-6 pt-4 border-t border-slate-100 flex flex-wrap gap-4 text-xs">
                            <div className="flex items-center gap-2">
                                <div className="w-3 h-3 rounded bg-[#10056E]/10 border border-[#10056E]"></div>
                                <span className="text-slate-600">{t("po.today")}</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <div className="w-3 h-3 rounded bg-emerald-500"></div>
                                <span className="text-slate-600">{t("po.completed")}</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <div className="w-3 h-3 rounded bg-[#10056E]"></div>
                                <span className="text-slate-600">{t("po.incomplete")}</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <div className="w-3 h-3 rounded bg-slate-400"></div>
                                <span className="text-slate-600">{t("po.cancelled")}</span>
                            </div>
                        </div>
                    </Card>
                )}

                {/* PO Details Modal */}
                <Modal isOpen={!!selectedPO} onClose={() => setSelectedPOId(null)} title={`PO #${selectedPO?.id.slice(-6).toUpperCase()}`} maxWidth="max-w-4xl">
                    {selectedPO && (
                        <div className="space-y-6">
                            {/* Header Info */}
                            <div className="grid grid-cols-2 gap-4 bg-slate-50 p-4 rounded-2xl border border-slate-100">
                                <div>
                                    <label className="text-xs font-bold text-slate-400 uppercase">{language === "en" ? "SUPPLIER" : "PEMBEKAL"}</label>
                                    <div className="font-bold text-slate-800 mt-1">{selectedPO.supplier}</div>
                                </div>
                                <div>
                                    <label className="text-xs font-bold text-slate-400 uppercase">{language === "en" ? "DATE" : "TARIKH"}</label>
                                    <div className="font-bold text-slate-800 mt-1">{new Date(selectedPO.date).toLocaleDateString('en-MY')}</div>
                                </div>
                            </div>

                            {/* Item Pesanan Section */}
                            <div>
                                <div className="flex items-center justify-between mb-3">
                                    <div className="flex items-center gap-2">
                                        <ShoppingBag size={18} className="text-[#10056E]" />
                                        <h4 className="font-bold text-slate-800">{t("po.orderItems")}</h4>
                                    </div>
                                    {/* Export Buttons */}
                                    <div className="flex gap-2">
                                        {can.viewFinancials && (
                                        <button 
                                            onClick={() => exportPOToExcel(selectedPO, activeProject, projectSuppliers, t, language)}
                                            className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-[#10056E] border border-[#10056E]/30 rounded-lg hover:bg-[#10056E]/5 transition-colors"
                                            data-testid="po-export-excel-btn"
                                        >
                                            <Download size={16} />
                                            Excel
                                        </button>
                                        )}
                                        <button 
                                            onClick={() => exportPOToPDF(selectedPO, activeProject, projectSuppliers, t, language)}
                                            className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-slate-600 bg-slate-100 border border-slate-200 rounded-lg hover:bg-slate-200 transition-colors"
                                            data-testid="po-export-pdf-btn"
                                        >
                                            <Printer size={16} />
                                            PDF
                                        </button>
                                    </div>
                                </div>
                                <div className="overflow-hidden rounded-xl border border-slate-100">
                                    <table className="w-full text-sm">
                                        <thead className="bg-slate-50 text-slate-500 text-xs uppercase font-semibold">
                                            <tr>
                                                <th className="p-3 text-left">{language === "en" ? "ITEM" : "ITEM"}</th>
                                                <th className="p-3 text-left">SKU</th>
                                                <th className="p-3 text-center">UNIT</th>
                                                <th className="p-3 text-center">QTY</th>
                                                <th className="p-3 text-right">{language === "en" ? "COST" : "KOS"}</th>
                                                <th className="p-3 text-right">{language === "en" ? "AMOUNT" : "JUMLAH"}</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-100">
                                            {selectedPO.items.map((item, idx) => {
                                                const hasReceived = item.receivedQty !== undefined;
                                                const isPadan = hasReceived && item.receivedQty === item.quantity;
                                                const isKurang = hasReceived && item.receivedQty < item.quantity;
                                                const isLebih = hasReceived && item.receivedQty > item.quantity;
                                                const difference = hasReceived ? item.receivedQty - item.quantity : 0;
                                                
                                                return (
                                                <tr key={idx}>
                                                    <td className="p-3 font-medium">{item.name}</td>
                                                    <td className="p-3 text-slate-500 text-xs">{item.sku || '-'}</td>
                                                    <td className="p-3 text-center text-slate-600">{item.unit || 'pcs'}</td>
                                                    <td className="p-3 text-center font-bold">
                                                        {hasReceived ? (
                                                            <div className="flex items-center justify-center gap-2">
                                                                <span className={
                                                                    isPadan ? "text-emerald-600" : 
                                                                    isKurang ? "text-orange-600" : 
                                                                    "text-blue-600"
                                                                }>
                                                                    {item.receivedQty} / {item.quantity}
                                                                </span>
                                                                {isPadan && <Equal size={14} className="text-emerald-600" />}
                                                                {isKurang && (
                                                                    <div className="flex items-center gap-1 px-2 py-0.5 bg-orange-100 rounded-md">
                                                                        <TrendingDown size={14} className="text-orange-600" />
                                                                        <span className="text-xs font-bold text-orange-700">{Math.abs(difference)}</span>
                                                                    </div>
                                                                )}
                                                                {isLebih && (
                                                                    <div className="flex items-center gap-1 px-2 py-0.5 bg-blue-100 rounded-md">
                                                                        <TrendingUp size={14} className="text-blue-600" />
                                                                        <span className="text-xs font-bold text-blue-700">+{difference}</span>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        ) : item.quantity}
                                                    </td>
                                                    {can.viewFinancials && (
                                                        <>
                                                            <td className="p-3 text-right">{formatCurrency(item.cost || 0)}</td>
                                                            <td className="p-3 text-right font-bold">{formatCurrency((item.cost || 0) * item.quantity)}</td>
                                                        </>
                                                    )}
                                                </tr>
                                                );
                                            })}
                                        </tbody>
                                        {can.viewFinancials && (
                                            <tfoot className="bg-slate-50 font-bold">
                                                <tr>
                                                    <td colSpan="5" className="p-3 text-right">{t("po.totalColon")}</td>
                                                    <td className="p-3 text-right text-[#10056E]">{formatCurrency(selectedPO.totalCost)}</td>
                                                </tr>
                                            </tfoot>
                                        )}
                                    </table>
                                </div>
                            </div>
                            
                            {/* Status Message for Completed PO */}
                            {selectedPO.status === 'Selesai' && (
                                <div className={`p-4 rounded-xl border ${
                                    selectedPO.hasDiscrepancy ? 'bg-orange-50 border-orange-200' : 'bg-emerald-50 border-emerald-200'
                                }`}>
                                    <div className="flex items-start gap-3">
                                        {selectedPO.hasDiscrepancy ? (
                                            <AlertCircle size={20} className="text-orange-600 shrink-0 mt-0.5" />
                                        ) : (
                                            <CheckCircle size={20} className="text-emerald-600 shrink-0 mt-0.5" />
                                        )}
                                        <p className={`text-sm font-medium ${
                                            selectedPO.hasDiscrepancy ? 'text-orange-800' : 'text-emerald-800'
                                        }`}>
                                            {selectedPO.hasDiscrepancy 
                                                ? (t('po.orderCompletedDiscrepancy'))
                                                : (t('po.stockAddedSuccessfully'))
                                            }
                                        </p>
                                    </div>
                                </div>
                            )}

                            {/* Status Message for Cancelled PO */}
                            {selectedPO.status === 'Batal' && (
                                <div className="p-4 rounded-xl border bg-slate-50 border-slate-200">
                                    <div className="flex items-start gap-3">
                                        <Ban size={20} className="text-slate-600 shrink-0 mt-0.5" />
                                        <div className="flex-1">
                                            <p className="text-sm font-medium text-slate-700">
                                                {language === 'en' ? 'This Purchase Order has been cancelled.' : 'Purchase Order ini telah dibatalkan.'}
                                            </p>
                                            <p className="text-xs text-slate-500 mt-1">
                                                {language === 'en' ? 'You can delete this PO if it is no longer needed.' : 'Anda boleh memadam PO ini jika tidak diperlukan lagi.'}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}
                            
                            {/* Action Buttons for Pending PO */}
                            {selectedPO.status === 'Belum Selesai' && (
                                <div className="flex gap-3 pt-4 border-t border-slate-100 justify-end">
                                    <Button 
                                        variant="secondary" 
                                        onClick={() => requestConfirm(t('po.cancelPO'), t('po.cancelPOConfirm'), () => handleCancelPO(selectedPO.id))}
                                        icon={Ban}
                                        className="text-slate-600"
                                    >
                                        {t('po.cancelPO')}
                                    </Button>
                                    <Button 
                                        variant="secondary" 
                                        onClick={() => handleEditPO(selectedPO)}
                                        icon={Edit2}
                                    >
                                        {t('common.edit')}
                                    </Button>
                                    <Button 
                                        onClick={() => { 
                                            setReceivePOModal({ isOpen: true, po: selectedPO }); 
                                            setReceiveDate(new Date().toISOString().split('T')[0]); 
                                            const initActuals = {};
                                            selectedPO.items.forEach(item => {
                                                initActuals[item.id] = item.quantity;
                                            });
                                            setPoActuals(initActuals); 
                                        }}
                                        icon={PackageCheck}
                                        className="bg-[#10056E] hover:bg-[#0d0459]"
                                    >
                                        {t('po.receivePO')}
                                    </Button>
                                </div>
                            )}

                            {/* Action Buttons for Cancelled PO */}
                            {selectedPO.status === 'Batal' && (
                                <div className="flex gap-3 pt-4 border-t border-slate-100 justify-end">
                                    <Button 
                                        variant="danger" 
                                        onClick={() => requestConfirm(
                                            t('po.deletePO'), 
                                            t('po.deletePOConfirm'), 
                                            () => handleDeletePO(selectedPO.id)
                                        )}
                                        icon={Trash2}
                                    >
                                        {t('po.deletePO')}
                                    </Button>
                                </div>
                            )}
                        </div>
                    )}
                </Modal>

                {/* Receive Goods Modal */}
                <Modal 
                    isOpen={receivePOModal.isOpen} 
                    onClose={() => setReceivePOModal({ isOpen: false, po: null })} 
                    title={t("po.receiveTitle")}
                    maxWidth="max-w-3xl"
                >
                    {receivePOModal.po && (
                        <form onSubmit={handleConfirmReceivePO} className="space-y-6">
                            {/* Info Alert */}
                            <div className="flex items-start gap-3 p-4 rounded-xl bg-emerald-50 border border-emerald-200">
                                <AlertCircle size={20} className="text-emerald-600 shrink-0 mt-0.5" />
                                <p className="text-sm text-emerald-800">
                                    {language === "en" ? 'Please confirm actual quantities received. Inventory will be updated according to "Received" quantities. Discrepancies will be recorded.' : 'Sila sahkan kuantiti sebenar yang diterima. Inventori akan dikemaskini mengikut jumlah "Diterima". Perbezaan akan direkodkan dalam sistem.'}
                                </p>
                            </div>

                            {/* Date Field */}
                            <div>
                                <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">{language === "en" ? "DATE GOODS RECEIVED *" : "TARIKH BARANG DITERIMA *"}</label>
                                <input 
                                    type="date" 
                                    value={receiveDate}
                                    onChange={(e) => setReceiveDate(e.target.value)}
                                    required
                                    className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none transition-all"
                                />
                            </div>

                            {/* Bulk Actions */}
                            <div className="flex gap-3">
                                <Button 
                                    type="button"
                                    variant="secondary"
                                    onClick={() => {
                                        const newActuals = {};
                                        receivePOModal.po.items.forEach(item => {
                                            newActuals[item.id] = item.quantity;
                                        });
                                        setPoActuals(newActuals);
                                    }}
                                    className="flex-1"
                                >
                                    {t("po.receiveAllFullReset")}
                                </Button>
                                <Button 
                                    type="button"
                                    variant="secondary"
                                    onClick={() => {
                                        const newActuals = {};
                                        receivePOModal.po.items.forEach(item => {
                                            newActuals[item.id] = 0;
                                        });
                                        setPoActuals(newActuals);
                                    }}
                                    className="flex-1"
                                >
                                    {t("po.clearAll")}
                                </Button>
                            </div>

                            {/* Items List */}
                            <div className="space-y-3 max-h-[400px] overflow-y-auto">
                                {receivePOModal.po.items.map(item => {
                                    const receivedQty = poActuals[item.id] !== undefined ? parseInt(poActuals[item.id]) : item.quantity;
                                    const isPadan = receivedQty === item.quantity;
                                    const isKurang = receivedQty < item.quantity;
                                    const isLebih = receivedQty > item.quantity;
                                    
                                    return (
                                        <div key={item.id} className="bg-slate-50 p-4 rounded-xl border border-slate-100">
                                            <div className="flex items-center justify-between gap-4">
                                                <div className="flex-1">
                                                    <div className="font-bold text-slate-800">{item.name}</div>
                                                    <div className="text-xs text-slate-400">{item.sku || '-'}</div>
                                                    <div className="mt-1 flex items-center gap-2">
                                                        <span className="text-xs text-slate-500">{language === 'en' ? 'Ordered' : 'Dipesan'}: {item.quantity} {item.unit || 'pcs'}</span>
                                                        {isPadan && (
                                                            <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs rounded-full font-bold flex items-center gap-1">
                                                                <Equal size={12} />
                                                                {t('stockCheck.match')}
                                                            </span>
                                                        )}
                                                        {isKurang && (
                                                            <span className="px-2 py-0.5 bg-orange-100 text-orange-700 text-xs rounded-full font-bold flex items-center gap-1">
                                                                <TrendingDown size={12} />
                                                                {t('stockCheck.shortage')}
                                                            </span>
                                                        )}
                                                        {isLebih && (
                                                            <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full font-bold flex items-center gap-1">
                                                                <TrendingUp size={12} />
                                                                {t('stockCheck.excess')}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="flex flex-col items-end gap-2">
                                                    <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">{language === 'en' ? 'QUANTITY RECEIVED' : 'KUANTITI DITERIMA'}</label>
                                                    <div className="flex items-center gap-2">
                                                        <button 
                                                            type="button"
                                                            onClick={() => setPoActuals({...poActuals, [item.id]: Math.max(0, receivedQty - 1)})}
                                                            className="w-8 h-8 flex items-center justify-center rounded-lg border border-slate-200 hover:bg-slate-100 transition-colors text-slate-600"
                                                        >
                                                            <Minus size={16} />
                                                        </button>
                                                        <input 
                                                            type="number"
                                                            value={receivedQty}
                                                            onChange={(e) => setPoActuals({...poActuals, [item.id]: parseInt(e.target.value) || 0})}
                                                            className="w-20 h-8 text-center rounded-lg border border-slate-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none font-bold text-base"
                                                            min="0"
                                                        />
                                                        <button 
                                                            type="button"
                                                            onClick={() => setPoActuals({...poActuals, [item.id]: receivedQty + 1})}
                                                            className="w-8 h-8 flex items-center justify-center rounded-lg border border-slate-200 hover:bg-slate-100 transition-colors text-slate-600"
                                                        >
                                                            <Plus size={16} />
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>

                            {/* Action Buttons */}
                            <div className="flex gap-3 pt-4 border-t border-slate-100 justify-end">
                                <Button 
                                    type="button"
                                    variant="secondary" 
                                    onClick={() => setReceivePOModal({ isOpen: false, po: null })}
                                >
                                    {t("po.backToList")}
                                </Button>
                                <Button 
                                    type="submit"
                                    disabled={isSubmitting}
                                    icon={isSubmitting ? Loader2 : CheckCircle}
                                    className="bg-emerald-500 hover:bg-emerald-600"
                                >
                                    {isSubmitting ? t('common.processing') : t('po.confirmUpdateStock')}
                                </Button>
                            </div>
                        </form>
                    )}
                </Modal>
            </div>
        );
    };

    const renderStockCheck = () => {
        // IN-PROGRESS AUDIT VIEW
        if (activeStockCheck && activeStockCheck.status === 'In Progress') {
            // Use activeStockCheck directly for instant updates
            const liveCheck = activeStockCheck;
            const filteredItems = liveCheck.items.filter(i => 
                i.name.toLowerCase().includes(stockCheckSearch.toLowerCase()) ||
                i.sku.toLowerCase().includes(stockCheckSearch.toLowerCase())
            );
            
            // Optimized update function with immediate state update
            const updateItemQty = (originalIdx, newQty, shouldConfirm = false) => {
                const newItems = [...liveCheck.items];
                newItems[originalIdx].actualQty = newQty;
                
                // Mark as confirmed when shouldConfirm is true OR when user has interacted (actualQty is not null)
                if (shouldConfirm || newQty !== null) {
                    newItems[originalIdx].confirmed = true;
                }
                
                const completed = newItems.filter(x => x.confirmed).length;
                const progress = Math.round((completed / newItems.length) * 100);
                
                // Create new state object with updated items and progress
                const updatedCheck = { ...liveCheck, items: newItems, progress };
                
                // Update local state immediately for instant feedback
                setActiveStockCheck(updatedCheck);
                
                // Debounced database save (will be called after 500ms of no changes)
                clearTimeout(window.auditSaveTimeout);
                window.auditSaveTimeout = setTimeout(() => {
                    db.updateStockCheck(liveCheck.id, { items: newItems, progress }).catch(err => {
                        console.error('Failed to save audit:', err);
                    });
                }, 500);
            };
            
            return (
                <div className="space-y-6">
                    {/* Audit Header */}
                    <Card className="bg-gradient-to-br from-blue-600 to-blue-800 text-white border-none shadow-xl sticky top-0 z-20">
                        <div className="flex flex-col sm:flex-row justify-between items-start gap-4 mb-6">
                            <div>
                                <div className="flex items-center gap-3 mb-2">
                                    <Badge className="bg-amber-400 text-amber-950 border-amber-500">{t("stockCheck.inProgress")}</Badge>
                                    <span className="text-sm font-medium opacity-80">{t("stockCheck.auditStock")}</span>
                                </div>
                                <h2 className="text-xl sm:text-2xl font-bold mb-1">{liveCheck.auditorName}</h2>
                                <p className="text-blue-100 text-sm">#{liveCheck.id?.substring(0, 7).toUpperCase() || 'K910IU'}</p>
                                <p className="text-blue-200 text-sm mt-1">{new Date(liveCheck.startDate).toLocaleString('en-MY', { dateStyle: 'medium' })}</p>
                            </div>
                            <div className="flex gap-2 sm:gap-3 w-full sm:w-auto">
                                <Button 
                                    variant="secondary" 
                                    className="bg-white/10 hover:bg-white/20 text-white border-white/20 flex-1 sm:flex-initial justify-center text-xs sm:text-sm"
                                    icon={ArrowLeft}
                                    onClick={() => {
                                        clearTimeout(window.auditSaveTimeout);
                                        setActiveStockCheck(null);
                                    }}
                                >
                                    {t("common.close")}
                                </Button>
                                <Button 
                                    onClick={() => handleFinalizeStockCheck(liveCheck)} 
                                    className="bg-emerald-500 hover:bg-emerald-600 text-white border-none shadow-lg flex-1 sm:flex-initial justify-center text-xs sm:text-sm"
                                >
                                    <CheckCircle size={16} />
                                    {t("common.confirm")}
                                </Button>
                            </div>
                        </div>
                        
                        {/* Progress Bar */}
                        <div>
                            <div className="flex justify-between items-center mb-2">
                                <span className="text-sm font-semibold text-blue-100 uppercase tracking-wide">{t("stockCheck.auditProgress")}</span>
                                <span className="text-xl font-bold">{liveCheck.progress || 0}%</span>
                            </div>
                            <div className="bg-white/20 rounded-full h-3 overflow-hidden backdrop-blur-sm">
                                <div 
                                    className="bg-emerald-400 h-full rounded-full shadow-lg transition-[width] duration-300 ease-out" 
                                    style={{ width: `${liveCheck.progress || 0}%` }}
                                ></div>
                            </div>
                        </div>
                    </Card>
                    
                    {/* Search Bar */}
                    <div className="relative">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
                        <input 
                            className="w-full pl-12 pr-4 py-4 rounded-2xl border border-slate-200 bg-white text-slate-700 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
                            placeholder={t("stockCheck.searchItem")}
                            value={stockCheckSearch}
                            onChange={e => setStockCheckSearch(e.target.value)}
                        />
                    </div>
                    
                    {/* Items List */}
                    <div className="space-y-3">
                        {filteredItems.map((item, idx) => {
                            const originalIdx = liveCheck.items.findIndex(x => x.itemId === item.itemId);
                            const isChecked = item.confirmed; // Use confirmed flag instead of actualQty check
                            const diff = (item.actualQty || 0) - item.systemQty;
                            const hasDiscrepancy = isChecked && diff !== 0;
                            
                            // Get current category from inventory
                            const inventoryItem = projectInventory.find(inv => inv.id === item.itemId);
                            const itemCategory = inventoryItem?.category || item.category || 'Others';
                            
                            // Determine status
                            let statusLabel = '';
                            let statusColor = '';
                            let statusBgColor = '';
                            if (diff === 0) {
                                statusLabel = t('stockCheck.match');
                                statusColor = 'text-white';
                                statusBgColor = 'bg-emerald-500';
                            } else if (diff > 0) {
                                statusLabel = `${t('stockCheck.excess')} +${diff}`;
                                statusColor = 'text-blue-700';
                                statusBgColor = 'bg-blue-100';
                            } else {
                                statusLabel = `${t('stockCheck.shortage')} ${diff}`;
                                statusColor = 'text-amber-700';
                                statusBgColor = 'bg-amber-100';
                            }
                            
                            return (
                                <Card 
                                    key={item.itemId} 
                                    className={`border-2 transition-colors duration-200 ${
                                        isChecked 
                                            ? hasDiscrepancy 
                                                ? 'bg-amber-50 border-amber-200' 
                                                : 'bg-emerald-50 border-emerald-200'
                                            : 'bg-white border-slate-100'
                                    }`}
                                >
                                    {/* Item Info Row */}
                                    <div className="flex items-start gap-3 mb-3">
                                        <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-slate-100 flex items-center justify-center shrink-0">
                                            {item.image ? (
                                                <img src={item.image} alt={item.name} className="w-full h-full object-cover rounded-xl" />
                                            ) : (
                                                <Package size={20} className="text-slate-400" />
                                            )}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                                                <h3 className="font-bold text-slate-800 text-sm sm:text-base truncate">{item.name}</h3>
                                                <span className={`px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-bold ${statusBgColor} ${statusColor}`}>
                                                    {statusLabel}
                                                </span>
                                            </div>
                                            <div className="flex items-center gap-2 text-[11px] text-slate-500">
                                                <span className="font-medium">{item.sku}</span>
                                                <Badge color="slate" className="text-[10px]">{itemCategory}</Badge>
                                            </div>
                                            <p className="text-xs text-slate-600 mt-1">
                                                {language === 'en' ? 'System' : 'Sistem'}: <span className="font-bold text-slate-800">{item.systemQty}</span> {item.unit}
                                            </p>
                                        </div>
                                    </div>
                                    
                                    {/* Quantity Input Row - full width on mobile */}
                                    <div className="flex items-center justify-between gap-2 bg-slate-50/80 rounded-xl p-2 -mx-1 sm:mx-0">
                                        <span className="text-xs text-slate-500 font-medium pl-1">{language === 'en' ? 'Physical' : 'Fizikal'}:</span>
                                        <div className="flex items-center gap-1.5">
                                            <div className="flex items-center bg-white rounded-lg border border-slate-200">
                                                <button
                                                    onClick={() => {
                                                        const currentQty = item.actualQty || 0;
                                                        if (currentQty > 0) updateItemQty(originalIdx, currentQty - 1, false);
                                                    }}
                                                    className="w-8 h-9 rounded-l-lg hover:bg-slate-100 text-slate-600 font-bold transition-colors flex items-center justify-center active:scale-95"
                                                >-</button>
                                                <input 
                                                    type="number" 
                                                    className={`w-16 sm:w-20 h-9 text-center font-bold text-base outline-none border-x border-slate-200 ${
                                                        isChecked && diff === 0
                                                            ? 'bg-emerald-100 text-emerald-700 border-emerald-300'
                                                            : isChecked && diff > 0
                                                                ? 'bg-blue-50 text-blue-900'
                                                                : isChecked && diff < 0
                                                                    ? 'bg-amber-50 text-amber-900'
                                                                    : 'bg-white text-slate-800'
                                                    }`}
                                                    placeholder="0" 
                                                    value={item.actualQty ?? ''} 
                                                    onChange={(e) => { 
                                                        const newQty = e.target.value === '' ? null : parseInt(e.target.value);
                                                        updateItemQty(originalIdx, newQty, false);
                                                    }} 
                                                />
                                                <button
                                                    onClick={() => {
                                                        const currentQty = item.actualQty || 0;
                                                        updateItemQty(originalIdx, currentQty + 1, false);
                                                    }}
                                                    className="w-8 h-9 rounded-r-lg hover:bg-slate-100 text-slate-600 font-bold transition-colors flex items-center justify-center active:scale-95"
                                                >+</button>
                                            </div>
                                            <button 
                                                onClick={() => { 
                                                    const newItems = [...liveCheck.items];
                                                    newItems[originalIdx].actualQty = item.systemQty;
                                                    newItems[originalIdx].confirmed = true;
                                                    const completed = newItems.filter(x => x.confirmed).length;
                                                    const progress = Math.round((completed / newItems.length) * 100);
                                                    const updatedCheck = { ...liveCheck, items: newItems, progress };
                                                    setActiveStockCheck(updatedCheck);
                                                    clearTimeout(window.auditSaveTimeout);
                                                    db.updateStockCheck(liveCheck.id, { items: newItems, progress }).catch(err => console.error('Failed to save audit:', err));
                                                }} 
                                                className={`w-9 h-9 rounded-lg transition-all duration-200 flex items-center justify-center active:scale-95 ${
                                                    item.confirmed && diff === 0
                                                        ? 'bg-emerald-500 text-white shadow-md'
                                                        : item.confirmed
                                                            ? 'bg-amber-500 text-white shadow-md'
                                                            : diff === 0
                                                                ? 'bg-white border border-emerald-300 text-emerald-500 hover:bg-emerald-50'
                                                                : 'bg-white border border-slate-200 text-slate-400 hover:bg-emerald-50 hover:text-emerald-600'
                                                }`}
                                            >
                                                <CheckCircle size={18}/>
                                            </button>
                                        </div>
                                    </div>
                                </Card>
                            );
                        })}
                    </div>
                </div>
            );
        }
        
        // LISTING VIEW
        return (
            <div className="space-y-4 sm:space-y-6">
                {/* Filters and Search */}
                <div className="bg-white p-4 sm:p-6 rounded-2xl sm:rounded-3xl border border-slate-100 shadow-sm space-y-3 md:space-y-0">
                    {/* Desktop: all inline | Mobile: stacked */}
                    <div className="flex flex-col md:flex-row md:items-center md:gap-3 space-y-3 md:space-y-0">
                    {/* Search */}
                    <div className="relative md:flex-1 md:min-w-0">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                        <input 
                            className="w-full pl-11 pr-4 py-2.5 bg-slate-50 rounded-xl outline-none text-sm font-medium text-slate-700 placeholder:text-slate-400 border border-transparent focus:border-emerald-500 focus:bg-white transition-all" 
                            placeholder={t("stockCheck.searchAuditor")} 
                            value={auditListSearch}
                            onChange={(e) => setAuditListSearch(e.target.value)}
                        />
                    </div>
                    
                    {/* Filters Row: Status + Date side by side */}
                    <div className="grid grid-cols-2 md:flex gap-2 md:gap-3">
                        <select 
                            className="bg-slate-50 rounded-xl pl-3 pr-8 py-2.5 text-xs sm:text-sm font-semibold text-slate-600 border border-transparent outline-none focus:bg-white focus:border-emerald-500 transition-all w-full md:w-auto"
                            value={auditStatusFilter}
                            onChange={(e) => setAuditStatusFilter(e.target.value)}
                        >
                            <option value="ALL">{t("stockCheck.allStatuses")}</option>
                            <option value="ONGOING">{t("stockCheck.inProgress")}</option>
                            <option value="COMPLETED">{t("stockCheck.complete")}</option>
                        </select>
                        <select 
                            className="bg-slate-50 rounded-xl pl-3 pr-8 py-2.5 text-xs sm:text-sm font-semibold text-slate-600 border border-transparent outline-none focus:bg-white focus:border-emerald-500 transition-all w-full md:w-auto"
                            value={auditDateFilter}
                            onChange={(e) => setAuditDateFilter(e.target.value)}
                        >
                            <option value="ALL_TIME">{t('stockCheck.allTime')}</option>
                            <option value="TODAY">{t('stockCheck.today')}</option>
                            <option value="YESTERDAY">{t('stockCheck.yesterday')}</option>
                            <option value="THIS_WEEK">{t('stockCheck.thisWeek')}</option>
                            <option value="LAST_WEEK">{t('stockCheck.lastWeek')}</option>
                            <option value="LAST_7_DAYS">{language === 'en' ? 'Last 7 Days' : '7 Hari Terakhir'}</option>
                            <option value="THIS_MONTH">{t('stockCheck.thisMonth')}</option>
                            <option value="LAST_MONTH">{t('stockCheck.lastMonth')}</option>
                            <option value="LAST_30_DAYS">{language === 'en' ? 'Last 30 Days' : '30 Hari Terakhir'}</option>
                            <option value="CUSTOM_RANGE">{t('stockCheck.customRange')}</option>
                        </select>
                    </div>
                    
                    {/* Actions Row: Discrepancy Toggle + Sesi Baru side by side */}
                    <div className="grid grid-cols-2 md:flex gap-2 md:gap-3">
                        <button
                            onClick={() => setShowDiscrepancyOnly(!showDiscrepancyOnly)}
                            className={`flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl text-sm font-bold transition-all ${
                                showDiscrepancyOnly 
                                    ? 'bg-amber-500 text-white shadow-lg' 
                                    : 'bg-slate-50 text-slate-600 hover:bg-slate-100'
                            }`}
                        >
                            <AlertTriangle size={15} />
                            {t('stockCheck.discrepancies')}
                        </button>
                        <Button 
                            onClick={() => setIsStockCheckModalOpen(true)} 
                            icon={Plus}
                            className="bg-[#10056E] hover:bg-[#0d0459] text-white shadow-lg shadow-[#10056E]/30 justify-center text-sm whitespace-nowrap"
                        >
                            {t('stockCheck.startNewAudit')}
                        </Button>
                    </div>
                    </div>
                    
                    {/* Custom Date Range */}
                    {auditDateFilter === 'CUSTOM_RANGE' && (
                        <div className="flex gap-4 mt-4">
                            <div className="flex-1">
                                <label className="block text-xs font-bold text-slate-500 mb-2">{language === 'en' ? 'FROM DATE' : 'DARI TARIKH'}</label>
                                <input 
                                    type="date" 
                                    className="w-full px-4 py-3 bg-slate-50 rounded-xl text-sm font-medium text-slate-700 border border-transparent outline-none focus:border-emerald-500 focus:bg-white transition-all"
                                    value={auditDateFrom}
                                    onChange={(e) => setAuditDateFrom(e.target.value)}
                                />
                            </div>
                            <div className="flex-1">
                                <label className="block text-xs font-bold text-slate-500 mb-2">{language === 'en' ? 'TO DATE' : 'HINGGA TARIKH'}</label>
                                <input 
                                    type="date" 
                                    className="w-full px-4 py-3 bg-slate-50 rounded-xl text-sm font-medium text-slate-700 border border-transparent outline-none focus:border-emerald-500 focus:bg-white transition-all"
                                    value={auditDateTo}
                                    onChange={(e) => setAuditDateTo(e.target.value)}
                                />
                            </div>
                        </div>
                    )}
                </div>
                
                {/* Audit Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                    {(() => {
                        // Apply filters
                        let filtered = projectStockChecks;
                        
                        // Search filter
                        if (auditListSearch) {
                            filtered = filtered.filter(check => 
                                check.auditorName?.toLowerCase().includes(auditListSearch.toLowerCase()) ||
                                check.notes?.toLowerCase().includes(auditListSearch.toLowerCase()) ||
                                check.id?.toLowerCase().includes(auditListSearch.toLowerCase())
                            );
                        }
                        
                        // Status filter
                        if (auditStatusFilter === 'ONGOING') {
                            filtered = filtered.filter(check => check.status === 'In Progress');
                        } else if (auditStatusFilter === 'COMPLETED') {
                            filtered = filtered.filter(check => check.status === 'Completed');
                        }
                        
                        // Date filter
                        if (auditDateFilter !== 'ALL_TIME') {
                            const now = new Date();
                            let startDate, endDate;
                            
                            if (auditDateFilter === 'TODAY') {
                                startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0);
                                endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
                            } else if (auditDateFilter === 'YESTERDAY') {
                                startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1, 0, 0, 0);
                                endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1, 23, 59, 59);
                            } else if (auditDateFilter === 'THIS_WEEK') {
                                const dayOfWeek = now.getDay();
                                const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek; // Monday as start of week
                                startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() + diff, 0, 0, 0);
                                endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() + diff + 6, 23, 59, 59);
                            } else if (auditDateFilter === 'LAST_WEEK') {
                                const dayOfWeek = now.getDay();
                                const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
                                startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() + diff - 7, 0, 0, 0);
                                endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() + diff - 1, 23, 59, 59);
                            } else if (auditDateFilter === 'LAST_7_DAYS') {
                                startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 6, 0, 0, 0);
                                endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
                            } else if (auditDateFilter === 'THIS_MONTH') {
                                startDate = new Date(now.getFullYear(), now.getMonth(), 1);
                                endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);
                            } else if (auditDateFilter === 'LAST_MONTH') {
                                startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
                                endDate = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59);
                            } else if (auditDateFilter === 'LAST_30_DAYS') {
                                startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 29, 0, 0, 0);
                                endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
                            } else if (auditDateFilter === 'CUSTOM_RANGE' && auditDateFrom && auditDateTo) {
                                startDate = new Date(auditDateFrom);
                                endDate = new Date(auditDateTo);
                                endDate.setHours(23, 59, 59);
                            }
                            
                            if (startDate && endDate) {
                                filtered = filtered.filter(check => {
                                    const checkDate = new Date(check.startDate);
                                    return checkDate >= startDate && checkDate <= endDate;
                                });
                            }
                        }
                        
                        // Discrepancy filter
                        if (showDiscrepancyOnly) {
                            filtered = filtered.filter(check => {
                                const hasDiscrepancy = check.items?.some(i => i.actualQty !== null && i.actualQty !== i.systemQty);
                                return hasDiscrepancy;
                            });
                        }
                        
                        if (filtered.length === 0) {
                            if (projectStockChecks.length === 0) return null;
                            return (
                                <div className="col-span-full">
                                    <Card className="p-8 sm:p-12 text-center">
                                        <ClipboardCheck size={48} className="mx-auto mb-3 text-slate-300" />
                                        <h3 className="text-lg font-bold text-slate-700 mb-2">{t("stockCheck.noAuditFound")}</h3>
                                        <p className="text-slate-500 text-sm">{language === "en" ? "Try adjusting your search filters." : "Cuba laraskan penapis carian anda."}</p>
                                    </Card>
                                </div>
                            );
                        }
                        
                        return filtered.map(check => {
                            // Calculate statistics
                            const totalItems = check.items?.length || 0;
                            const confirmedItems = check.items?.filter(i => i.confirmed).length || 0;
                            const matchedItems = check.items?.filter(i => i.actualQty === i.systemQty).length || 0;
                            const lebihItems = check.items?.filter(i => i.actualQty !== null && i.actualQty > i.systemQty).length || 0;
                            const kurangItems = check.items?.filter(i => i.actualQty !== null && i.actualQty < i.systemQty).length || 0;
                            const progress = check.progress || 0;
                            const isCompleted = check.status === 'Completed';
                            
                            return (
                                <Card 
                                key={check.id} 
                                className="group hover:shadow-xl transition-all cursor-pointer border-2 hover:border-blue-200"
                                onClick={() => setActiveStockCheck(check)}
                            >
                                {/* Status Badge & Date */}
                                <div className="flex justify-between items-start mb-4">
                                    <Badge 
                                        className={`text-xs font-bold px-3 py-1.5 ${isCompleted ? 'bg-emerald-100 text-emerald-700 border-emerald-200' : 'bg-amber-100 text-amber-700 border-amber-200'}`}
                                    >
                                        {isCompleted ? t("stockCheck.completed") : t("stockCheck.inProgress")}
                                    </Badge>
                                    <span className="text-xs text-slate-500 font-medium">
                                        {new Date(check.startDate).toLocaleDateString('en-MY', { 
                                            day: '2-digit', 
                                            month: 'short',
                                            year: 'numeric'
                                        })}
                                    </span>
                                </div>
                                
                                {/* Auditor Info */}
                                <div className="mb-4">
                                    <h3 className="text-lg font-bold text-slate-800 mb-1">{check.auditorName}</h3>
                                    <p className="text-xs text-slate-400">
                                        #{check.id?.substring(0, 8).toUpperCase() || 'ID'}
                                    </p>
                                </div>
                                
                                {/* Progress Bar for Ongoing Audits */}
                                {!isCompleted && (
                                    <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                                        <div className="flex justify-between items-center mb-1.5">
                                            <span className="text-xs text-blue-600 font-semibold">{t("stockCheck.progress")}</span>
                                            <span className="text-sm font-bold text-blue-700">{progress}%</span>
                                        </div>
                                        <div className="bg-white rounded-full h-2 overflow-hidden">
                                            <div 
                                                className="bg-blue-500 h-full rounded-full transition-all duration-300" 
                                                style={{ width: `${progress}%` }}
                                            ></div>
                                        </div>
                                    </div>
                                )}
                                
                                {/* Completion Date & Time for Completed Audits - Inline Compact */}
                                {isCompleted && check.completedDate && (
                                    <div className="mb-4 p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                                        <p className="text-xs text-emerald-600 font-semibold mb-1.5">{t("stockCheck.completionDate")}</p>
                                        <div className="flex items-center justify-between">
                                            <p className="text-sm font-bold text-emerald-900">
                                                {new Date(check.completedDate).toLocaleDateString('en-MY', { 
                                                    day: '2-digit', 
                                                    month: 'short', 
                                                    year: 'numeric' 
                                                })}
                                            </p>
                                            <p className="text-xs font-semibold text-emerald-700 bg-emerald-100 px-2 py-1 rounded">
                                                {new Date(check.completedDate).toLocaleTimeString('en-MY', { 
                                                    hour: '2-digit', 
                                                    minute: '2-digit'
                                                })}
                                            </p>
                                        </div>
                                    </div>
                                )}
                                
                                {/* Summary Stats with Colors */}
                                <div className="grid grid-cols-3 gap-1.5 sm:gap-2 mb-3 sm:mb-4">
                                    <div className="text-center p-1.5 sm:p-2 bg-emerald-50 rounded-lg border border-emerald-200">
                                        <p className="text-[10px] sm:text-xs text-emerald-600 font-semibold mb-0.5">{t("stockCheck.match")}</p>
                                        <p className="text-base sm:text-lg font-bold text-emerald-700">{matchedItems}</p>
                                    </div>
                                    <div className="text-center p-1.5 sm:p-2 bg-blue-50 rounded-lg border border-blue-200">
                                        <p className="text-[10px] sm:text-xs text-blue-600 font-semibold mb-0.5">{t("stockCheck.excess")}</p>
                                        <p className="text-base sm:text-lg font-bold text-blue-700">{lebihItems}</p>
                                    </div>
                                    <div className="text-center p-1.5 sm:p-2 bg-amber-50 rounded-lg border border-amber-200">
                                        <p className="text-[10px] sm:text-xs text-amber-600 font-semibold mb-0.5">{t("stockCheck.shortage")}</p>
                                        <p className="text-base sm:text-lg font-bold text-amber-700">{kurangItems}</p>
                                    </div>
                                </div>
                                
                                {/* Total Items */}
                                <div className="flex items-center justify-center py-2 mb-4 bg-slate-50 rounded-lg">
                                    <p className="text-xs text-slate-500 mr-2">{t("inventory.totalItems")}:</p>
                                    <p className="text-lg font-bold text-slate-800">{totalItems}</p>
                                </div>
                                
                                {/* Action Button */}
                                <div>
                                    {isCompleted ? (
                                        <Button 
                                            variant="secondary" 
                                            fullWidth
                                            icon={FileText}
                                            className="bg-slate-50 hover:bg-slate-100 text-slate-700 group-hover:bg-blue-50 group-hover:text-blue-700"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setAuditReportModal({ isOpen: true, audit: check });
                                            }}
                                        >
                                            {t("stockCheck.generateReport")}
                                        </Button>
                                    ) : (
                                        <button
                                            className="w-full px-6 py-3 rounded-xl bg-blue-500 hover:bg-blue-600 text-white font-semibold shadow-lg transition-all flex items-center justify-center gap-2"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setActiveStockCheck(check);
                                            }}
                                        >
                                            <Play size={18} />
                                            {t("stockCheck.continueAudit")}
                                        </button>
                                    )}
                                </div>
                            </Card>
                        );
                    });
                })()}
                </div>
                
                {/* Empty State - Only when no audits at all */}
                {projectStockChecks.length === 0 && (
                    <Card className="text-center py-10 sm:py-16">
                        <ClipboardCheck size={48} className="text-slate-300 mx-auto mb-3" />
                        <h3 className="text-lg font-bold text-slate-800 mb-2">{t("stockCheck.noStockAudit")}</h3>
                        <p className="text-slate-500 text-sm mb-5">{t("stockCheck.startNewAuditDesc")}</p>
                        <Button 
                            onClick={() => setIsStockCheckModalOpen(true)} 
                            icon={Plus}
                            className="bg-[#10056E] hover:bg-[#0d0459]"
                        >
                            {t("stockCheck.startNewAuditBtn")}
                        </Button>
                    </Card>
                )}
            </div>
        );
    };

    // Fetch transaction logs with filters
    const fetchTransactionLogs = async (page = 1) => {
        if (!activeProject) return;
        
        setLoadingTransactions(true);
        try {
            const params = {
                projectId: activeProject.id,
                page: page,
                limit: 25
            };
            
            if (sejarahSearch) params.search = sejarahSearch;
            // Exclude PRICE_CHANGE from transaction logs - only show inventory movements
            if (sejarahTypeFilter !== 'ALL') {
                params.transactionType = sejarahTypeFilter;
            } else {
                // When ALL is selected, exclude PRICE_CHANGE type
                params.excludeType = 'PRICE_CHANGE';
            }
            
            // Handle date filters
            if (sejarahTimeFilter === 'CUSTOM_RANGE' && sejarahDateFrom) {
                params.dateFrom = new Date(sejarahDateFrom).toISOString();
            } else if (sejarahTimeFilter === 'TODAY') {
                params.dateFrom = new Date(new Date().setHours(0, 0, 0, 0)).toISOString();
                params.dateTo = new Date(new Date().setHours(23, 59, 59, 999)).toISOString();
            } else if (sejarahTimeFilter === 'YESTERDAY') {
                const yesterday = new Date();
                yesterday.setDate(yesterday.getDate() - 1);
                params.dateFrom = new Date(yesterday.setHours(0, 0, 0, 0)).toISOString();
                params.dateTo = new Date(yesterday.setHours(23, 59, 59, 999)).toISOString();
            } else if (sejarahTimeFilter === 'THIS_WEEK') {
                const now = new Date();
                const firstDayOfWeek = new Date(now.setDate(now.getDate() - now.getDay()));
                params.dateFrom = new Date(firstDayOfWeek.setHours(0, 0, 0, 0)).toISOString();
            } else if (sejarahTimeFilter === 'LAST_WEEK') {
                const now = new Date();
                const lastWeekStart = new Date(now.setDate(now.getDate() - now.getDay() - 7));
                const lastWeekEnd = new Date(lastWeekStart);
                lastWeekEnd.setDate(lastWeekEnd.getDate() + 6);
                params.dateFrom = new Date(lastWeekStart.setHours(0, 0, 0, 0)).toISOString();
                params.dateTo = new Date(lastWeekEnd.setHours(23, 59, 59, 999)).toISOString();
            } else if (sejarahTimeFilter === 'LAST_7_DAYS') {
                const date = new Date();
                date.setDate(date.getDate() - 7);
                params.dateFrom = date.toISOString();
            } else if (sejarahTimeFilter === 'THIS_MONTH') {
                const now = new Date();
                const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
                params.dateFrom = new Date(firstDayOfMonth.setHours(0, 0, 0, 0)).toISOString();
            } else if (sejarahTimeFilter === 'LAST_MONTH') {
                const now = new Date();
                const firstDayOfLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
                const lastDayOfLastMonth = new Date(now.getFullYear(), now.getMonth(), 0);
                params.dateFrom = new Date(firstDayOfLastMonth.setHours(0, 0, 0, 0)).toISOString();
                params.dateTo = new Date(lastDayOfLastMonth.setHours(23, 59, 59, 999)).toISOString();
            } else if (sejarahTimeFilter === 'LAST_30_DAYS') {
                const date = new Date();
                date.setDate(date.getDate() - 30);
                params.dateFrom = date.toISOString();
            }
            
            if (sejarahTimeFilter === 'CUSTOM_RANGE' && sejarahDateTo) {
                params.dateTo = new Date(sejarahDateTo).toISOString();
            }
            
            const result = await db.getTransactionLogs(params);
            setTransactionLogs(result.data || []);
            setTransactionLogsMeta({
                total: result.total || 0,
                page: result.page || 1,
                totalPages: result.totalPages || 1
            });
            setSejarahCurrentPage(page);
        } catch (error) {
            console.error('Error fetching transaction logs:', error);
            showToast(t('history.errorFetchingLog'), 'error');
        } finally {
            setLoadingTransactions(false);
        }
    };

    // Fetch transaction logs when filters change
    useEffect(() => {
        if (activeTab === 'transactions' && sejarahTab === 'log' && activeProject) {
            fetchTransactionLogs(1);
        }
    }, [activeTab, sejarahTab, activeProject, sejarahSearch, sejarahTypeFilter, sejarahTimeFilter, sejarahDateFrom, sejarahDateTo]);

    const handleResetSejarahFilters = () => {
        setSejarahSearch('');
        setSejarahTypeFilter('ALL');
        setSejarahTimeFilter('ALL_TIME');
        setSejarahDateFrom('');
        setSejarahDateTo('');
    };

    const handleTransactionClick = async (transactionId) => {
        try {
            const transaction = await db.getTransactionDetail(transactionId);
            setSelectedTransaction(transaction);
            setIsTransactionDetailOpen(true);
        } catch (error) {
            console.error('Error fetching transaction detail:', error);
            showToast(t('history.errorFetchingDetail'), 'error');
        }
    };

    const renderHistory = () => (
        <div className="space-y-4 sm:space-y-6">
            {/* Tabs */}
            <div className="flex p-1 bg-white rounded-2xl border border-slate-100 shadow-sm overflow-x-auto no-scrollbar w-full sm:w-fit">
                <button 
                    className={`px-4 sm:px-6 py-2.5 rounded-xl text-xs sm:text-sm font-bold transition-all whitespace-nowrap flex-shrink-0 ${sejarahTab === 'log' ? 'bg-[#10056E] text-white shadow-md' : 'text-slate-500 hover:text-slate-700'}`} 
                    onClick={() => setSejarahTab('log')}
                >
                    {language === 'en' ? 'Transaction Log' : 'Log Transaksi'}
                </button>
                <button 
                    className={`px-4 sm:px-6 py-2.5 rounded-xl text-xs sm:text-sm font-bold transition-all whitespace-nowrap flex-shrink-0 ${sejarahTab === 'reports' ? 'bg-[#10056E] text-white shadow-md' : 'text-slate-500 hover:text-slate-700'}`} 
                    onClick={() => setSejarahTab('reports')}
                >
                    {t('history.monthlyBurstReport')}
                </button>
            </div>

            {sejarahTab === 'log' && (
                <>
                    {/* Advanced Filters */}
                    <div className="bg-white p-4 sm:p-6 rounded-2xl sm:rounded-3xl border border-slate-100 shadow-sm space-y-3 sm:space-y-4 md:space-y-0">
                        <div className="flex flex-col md:flex-row md:items-center md:gap-3 space-y-3 md:space-y-0">
                            {/* Search */}
                            <div className="relative md:flex-1 md:min-w-0">
                                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
                                <input 
                                    className="w-full pl-12 pr-4 py-2.5 bg-slate-50 rounded-xl outline-none text-sm font-medium text-slate-700 placeholder:text-slate-400 border border-transparent focus:border-emerald-500 focus:bg-white transition-all" 
                                    placeholder={t('history.searchItemReason')} 
                                    value={sejarahSearch}
                                    onChange={(e) => setSejarahSearch(e.target.value)}
                                />
                            </div>
                            
                            {/* Type Filter */}
                            <select 
                                className="bg-slate-50 rounded-xl pl-4 pr-10 py-2.5 text-sm font-semibold text-slate-600 border border-transparent outline-none focus:bg-white focus:border-emerald-500 transition-all w-full md:w-auto"
                                value={sejarahTypeFilter}
                                onChange={(e) => setSejarahTypeFilter(e.target.value)}
                            >
                                <option value="ALL">{t('history.allTypes')}</option>
                                <option value="STOK_MASUK">{t('history.stockIn')}</option>
                                <option value="STOK_KELUAR">{t('history.stockOut')}</option>
                                <option value="PO_DITERIMA">{language === 'en' ? 'PO Received' : 'PO Diterima'}</option>
                                <option value="PELARASAN_AUDIT">{language === 'en' ? 'Audit Adjustment' : 'Pelarasan Audit'}</option>
                            </select>
                            
                            {/* Time Preset Filter */}
                            <select 
                                className="bg-slate-50 rounded-xl pl-4 pr-10 py-2.5 text-sm font-semibold text-slate-600 border border-transparent outline-none focus:bg-white focus:border-emerald-500 transition-all w-full md:w-auto"
                                value={sejarahTimeFilter}
                                onChange={(e) => setSejarahTimeFilter(e.target.value)}
                            >
                                <option value="ALL_TIME">{t('history.allTime')}</option>
                                <option value="TODAY">{t('history.today')}</option>
                                <option value="YESTERDAY">{t('history.yesterday')}</option>
                                <option value="THIS_WEEK">{t('history.thisWeek')}</option>
                                <option value="LAST_WEEK">{t('history.lastWeek')}</option>
                                <option value="LAST_7_DAYS">{language === 'en' ? 'Last 7 Days' : '7 Hari Terakhir'}</option>
                                <option value="THIS_MONTH">{t('history.thisMonth')}</option>
                                <option value="LAST_MONTH">{t('history.lastMonth')}</option>
                                <option value="LAST_30_DAYS">{language === 'en' ? 'Last 30 Days' : '30 Hari Terakhir'}</option>
                                <option value="CUSTOM_RANGE">{t('history.selectDateRange')}</option>
                            </select>
                        </div>
                        
                        {/* Custom Date Range */}
                        {sejarahTimeFilter === 'CUSTOM_RANGE' && (
                            <div className="flex flex-col md:flex-row gap-4 items-end">
                                <div className="flex-1">
                                    <label className="block text-xs font-bold text-slate-500 mb-2">{language === 'en' ? 'FROM DATE' : 'DARI TARIKH'}</label>
                                    <input 
                                        type="date" 
                                        className="w-full px-4 py-3 bg-slate-50 rounded-xl text-sm font-medium text-slate-700 border border-transparent outline-none focus:border-emerald-500 focus:bg-white transition-all"
                                        value={sejarahDateFrom}
                                        onChange={(e) => setSejarahDateFrom(e.target.value)}
                                    />
                                </div>
                                <div className="flex-1">
                                    <label className="block text-xs font-bold text-slate-500 mb-2">{language === 'en' ? 'TO DATE' : 'HINGGA TARIKH'}</label>
                                    <input 
                                        type="date" 
                                        className="w-full px-4 py-3 bg-slate-50 rounded-xl text-sm font-medium text-slate-700 border border-transparent outline-none focus:border-emerald-500 focus:bg-white transition-all"
                                        value={sejarahDateTo}
                                        onChange={(e) => setSejarahDateTo(e.target.value)}
                                    />
                                </div>
                                <button 
                                    onClick={handleResetSejarahFilters}
                                    className="px-6 py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-sm font-bold transition-all"
                                >
                                    Reset
                                </button>
                            </div>
                        )}
                        
                        {!loadingTransactions && (
                            <div className="text-xs font-semibold text-slate-500">
                                {language === 'en' ? 'Showing' : 'Menunjukkan'} {transactionLogs.length} {language === 'en' ? 'of' : 'daripada'} {transactionLogsMeta.total} {language === 'en' ? 'transactions' : 'transaksi'}
                            </div>
                        )}
                    </div>

                    {/* Transaction List */}
                    <div className="bg-white rounded-2xl sm:rounded-3xl border border-slate-100 shadow-sm overflow-hidden">
                        {loadingTransactions ? (
                            <div className="text-center py-20">
                                <Loader2 className="animate-spin mx-auto mb-3 text-[#10056E]" size={40} />
                                <p className="text-slate-500 font-medium">{language === 'en' ? 'Loading transactions...' : 'Memuatkan transaksi...'}</p>
                            </div>
                        ) : transactionLogs.length === 0 ? (
                            <div className="text-center py-20 text-slate-400">
                                <History size={48} className="mx-auto mb-3 opacity-30" />
                                <p className="font-medium">{t("history.noTransactionsFound")}</p>
                            </div>
                        ) : (
                            <>
                                {/* Desktop Table */}
                                <div className="hidden md:block">
                                <table className="w-full text-left text-sm">
                                    <thead className="bg-slate-50/80 text-slate-500 font-semibold uppercase text-xs tracking-wider">
                                        <tr>
                                            <th className="p-4 pl-6">{t("history.dateCol")}</th>
                                            <th className="p-4">{t("history.typeCol")}</th>
                                            <th className="p-4">Item</th>
                                            <th className="p-4 text-center">Qty</th>
                                            <th className="p-4">{t("history.userCol")}</th>
                                            <th className="p-4">{t("inventory.reason")}</th>
                                            <th className="p-4 text-center">{t("history.proof")}</th>
                                            <th className="p-4"></th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-50">
                                        {transactionLogs.map(tx => {
                                            const badgeColor = 
                                                tx.transactionType === 'STOK_MASUK' || tx.transactionType === 'PO_DITERIMA' 
                                                    ? 'bg-emerald-50 text-emerald-700' 
                                                    : tx.transactionType === 'STOK_KELUAR'
                                                    ? 'bg-rose-50 text-rose-700'
                                                    : 'bg-blue-50 text-blue-700';
                                            const label = 
                                                tx.transactionType === 'STOK_MASUK' ? 'IN' :
                                                tx.transactionType === 'STOK_KELUAR' ? 'OUT' :
                                                tx.transactionType === 'PO_DITERIMA' ? 'IN' :
                                                'AUDIT';
                                            return (
                                                <tr key={tx.id} className="hover:bg-slate-50/50 transition-colors cursor-pointer" onClick={() => handleTransactionClick(tx.id)}>
                                                    <td className="p-4 pl-6 text-slate-600 text-xs font-medium">{new Date(tx.timestamp).toLocaleDateString('en-MY', {day:'2-digit',month:'2-digit',year:'numeric'})}, {new Date(tx.timestamp).toLocaleTimeString('en-MY', {hour:'2-digit',minute:'2-digit'})}</td>
                                                    <td className="p-4"><span className={`inline-flex px-2.5 py-1 rounded-lg text-xs font-bold ${badgeColor}`}>{label}</span></td>
                                                    <td className="p-4"><div className="font-bold text-slate-800">{tx.itemName}</div></td>
                                                    <td className="p-4 text-center"><span className={`inline-flex items-center font-bold ${tx.type === 'IN' ? 'text-emerald-600' : 'text-rose-600'}`}>{tx.type === 'IN' ? '+' : '-'}{tx.quantity}</span></td>
                                                    <td className="p-4"><div className="text-sm text-slate-700 font-medium">{tx.userName || tx.user || 'N/A'}</div></td>
                                                    <td className="p-4 text-slate-600 text-sm max-w-xs truncate">{tx.reason}</td>
                                                    <td className="p-4 text-center">{tx.proofImage && <div className="w-10 h-10 rounded-lg overflow-hidden border border-slate-200 shadow-sm mx-auto cursor-pointer hover:ring-2 hover:ring-emerald-500 transition-all" onClick={(e) => { e.stopPropagation(); setViewingImage(tx.proofImage); }}><img src={tx.proofImage} className="w-full h-full object-cover" alt={t("history.proof")}/></div>}</td>
                                                    <td className="p-4"><ChevronRight size={18} className="text-slate-400" /></td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                                </div>

                                {/* Mobile Card List */}
                                <div className="md:hidden divide-y divide-slate-100">
                                    {transactionLogs.map(tx => {
                                        const badgeColor = 
                                            tx.transactionType === 'STOK_MASUK' || tx.transactionType === 'PO_DITERIMA' 
                                                ? 'bg-emerald-50 text-emerald-700' 
                                                : tx.transactionType === 'STOK_KELUAR'
                                                ? 'bg-rose-50 text-rose-700'
                                                : 'bg-blue-50 text-blue-700';
                                        const label = 
                                            tx.transactionType === 'STOK_MASUK' ? 'IN' :
                                            tx.transactionType === 'STOK_KELUAR' ? 'OUT' :
                                            tx.transactionType === 'PO_DITERIMA' ? 'IN' : 'AUDIT';
                                        return (
                                            <div key={tx.id} className="p-4 hover:bg-slate-50 transition-colors cursor-pointer active:bg-slate-100" onClick={() => handleTransactionClick(tx.id)}>
                                                <div className="flex items-start gap-3">
                                                    {tx.proofImage && (
                                                        <div className="w-12 h-12 rounded-lg overflow-hidden border border-slate-200 shadow-sm flex-shrink-0 cursor-pointer" onClick={(e) => { e.stopPropagation(); setViewingImage(tx.proofImage); }}>
                                                            <img src={tx.proofImage} className="w-full h-full object-cover" alt={t("history.proof")} data-testid={`mobile-proof-thumb-${tx.id}`}/>
                                                        </div>
                                                    )}
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-center justify-between mb-1.5">
                                                            <span className={`inline-flex px-2.5 py-1 rounded-lg text-xs font-bold ${badgeColor}`}>{label}</span>
                                                            <span className={`font-bold text-sm ${tx.type === 'IN' ? 'text-emerald-600' : 'text-rose-600'}`}>{tx.type === 'IN' ? '+' : '-'}{tx.quantity}</span>
                                                        </div>
                                                        <div className="font-bold text-slate-800 text-sm mb-1 truncate">{tx.itemName}</div>
                                                        <div className="flex items-center justify-between text-xs text-slate-500">
                                                            <span>{new Date(tx.timestamp).toLocaleDateString('en-MY')}, {new Date(tx.timestamp).toLocaleTimeString('en-MY', {hour:'2-digit',minute:'2-digit'})}</span>
                                                            <span>{tx.userName || tx.user || ''}</span>
                                                        </div>
                                                        {tx.reason && <div className="text-xs text-slate-400 mt-1 line-clamp-1">{tx.reason}</div>}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                                
                                {/* Pagination */}
                                {transactionLogsMeta.totalPages > 1 && (
                                    <div className="flex justify-between items-center px-6 py-4 border-t border-slate-100">
                                        <div className="text-sm text-slate-600">
                                            {language === 'en' ? 'Page' : 'Halaman'} {transactionLogsMeta.page} {language === 'en' ? 'of' : 'daripada'} {transactionLogsMeta.totalPages}
                                        </div>
                                        <div className="flex gap-2">
                                            <button 
                                                onClick={() => fetchTransactionLogs(sejarahCurrentPage - 1)}
                                                disabled={sejarahCurrentPage === 1}
                                                className="px-4 py-2 bg-slate-100 rounded-lg text-sm font-bold text-slate-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-200 transition-all"
                                            >
                                                {t("common.previous")}
                                            </button>
                                            <button 
                                                onClick={() => fetchTransactionLogs(sejarahCurrentPage + 1)}
                                                disabled={sejarahCurrentPage === transactionLogsMeta.totalPages}
                                                className="px-4 py-2 bg-slate-100 rounded-lg text-sm font-bold text-slate-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-200 transition-all"
                                            >
                                                {t("common.next")}
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </>
            )}

            {sejarahTab === 'reports' && (
                <>
                    {/* Month Selector + Download Button */}
                    <div className="bg-white p-6 rounded-3xl border border-slate-100 shadow-sm">
                        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                            <div className="flex items-center gap-3">
                                <label className="text-sm font-bold text-slate-600">{t("history.selectMonth")}:</label>
                                <select
                                    value={burstReportMonth.split('-')[1]}
                                    onChange={(e) => setBurstReportMonth(`${burstReportMonth.split('-')[0]}-${e.target.value}`)}
                                    className="px-3 py-2 bg-slate-50 rounded-xl text-sm font-medium text-slate-700 border border-slate-200 outline-none focus:border-emerald-500 focus:bg-white transition-all cursor-pointer"
                                    data-testid="burst-month-select"
                                >
                                    {['01','02','03','04','05','06','07','08','09','10','11','12'].map((m, idx) => (
                                        <option key={m} value={m}>{['January','February','March','April','May','June','July','August','September','October','November','December'][idx]}</option>
                                    ))}
                                </select>
                                <select
                                    value={burstReportMonth.split('-')[0]}
                                    onChange={(e) => setBurstReportMonth(`${e.target.value}-${burstReportMonth.split('-')[1]}`)}
                                    className="px-3 py-2 bg-slate-50 rounded-xl text-sm font-medium text-slate-700 border border-slate-200 outline-none focus:border-emerald-500 focus:bg-white transition-all cursor-pointer"
                                    data-testid="burst-year-select"
                                >
                                    {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 2 + i).map(y => (
                                        <option key={y} value={String(y)}>{y}</option>
                                    ))}
                                </select>
                            </div>
                            <button
                                onClick={async () => {
                                    const [selYear] = burstReportMonth.split('-').map(Number);
                                    const monthNames = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
                                    
                                    const wb = new ExcelJS.Workbook();
                                    const ws = wb.addWorksheet(`Burst ${selYear}`);
                                    
                                    // -- Header Row 1: Merged month group headers --
                                    const h1 = ['SKU', t('settings.productCol'), t('po.unitLabel')];
                                    monthNames.forEach(mn => { h1.push(mn, '', ''); });
                                    ws.addRow(h1);
                                    
                                    // Merge month group headers (each month spans 3 cols starting at col 4)
                                    for (let m = 0; m < 12; m++) {
                                        const startCol = 4 + m * 3;
                                        ws.mergeCells(1, startCol, 1, startCol + 2);
                                        const cell = ws.getCell(1, startCol);
                                        cell.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 11 };
                                        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1F2937' } };
                                        cell.alignment = { horizontal: 'center', vertical: 'middle' };
                                        cell.border = { top: {style:'thin'}, bottom: {style:'thin'}, left: {style:'thin'}, right: {style:'thin'} };
                                    }
                                    // Style fixed header cells (SKU, Produk, Unit) row 1
                                    for (let c = 1; c <= 3; c++) {
                                        const cell = ws.getCell(1, c);
                                        cell.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 11 };
                                        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1F2937' } };
                                        cell.alignment = { horizontal: 'center', vertical: 'middle' };
                                        cell.border = { top: {style:'thin'}, bottom: {style:'thin'}, left: {style:'thin'}, right: {style:'thin'} };
                                    }
                                    
                                    // -- Header Row 2: Sub-headers for each month --
                                    const h2 = ['', '', ''];
                                    monthNames.forEach(() => { h2.push(t('history.stockOutHeading'), t('history.burstLimit'), t('history.burstCount')); });
                                    ws.addRow(h2);
                                    // Style sub-headers
                                    for (let c = 1; c <= 3 + 12 * 3; c++) {
                                        const cell = ws.getCell(2, c);
                                        cell.font = { bold: true, size: 9 };
                                        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE5E7EB' } };
                                        cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
                                        cell.border = { top: {style:'thin'}, bottom: {style:'thin'}, left: {style:'thin'}, right: {style:'thin'} };
                                    }
                                    // Merge SKU/Produk/Unit across 2 header rows
                                    ws.mergeCells(1, 1, 2, 1);
                                    ws.mergeCells(1, 2, 2, 2);
                                    ws.mergeCells(1, 3, 2, 3);
                                    
                                    // -- Data Rows --
                                    const sortedInv = [...projectInventory].sort((a, b) => (a.sku || '').localeCompare(b.sku || ''));
                                    sortedInv.forEach(item => {
                                        const rowData = [item.sku || '-', item.name, item.unit || 'pcs'];
                                        const burstLimit = item.burstLimit || 0;
                                        
                                        for (let m = 0; m < 12; m++) {
                                            const mStart = new Date(selYear, m, 1);
                                            const mEnd = new Date(selYear, m + 1, 0, 23, 59, 59);
                                            let totalOut = 0;
                                            projectTransactions.forEach(tx => {
                                                if (tx.type === 'OUT' && tx.itemId === item.id) {
                                                    const d = new Date(tx.timestamp);
                                                    if (d >= mStart && d <= mEnd) totalOut += tx.quantity;
                                                }
                                            });
                                            const burstCount = burstLimit > 0 && totalOut > burstLimit ? totalOut - burstLimit : 0;
                                            rowData.push(totalOut, burstLimit, burstCount);
                                        }
                                        
                                        const excelRow = ws.addRow(rowData);
                                        
                                        // Style each month's 3 cells
                                        for (let m = 0; m < 12; m++) {
                                            const baseCol = 4 + m * 3;
                                            const burstLimit = item.burstLimit || 0;
                                            const totalOut = excelRow.getCell(baseCol).value;
                                            const burstCount = excelRow.getCell(baseCol + 2).value;
                                            
                                            // Stok Keluar cell
                                            const stockCell = excelRow.getCell(baseCol);
                                            stockCell.alignment = { horizontal: 'center' };
                                            stockCell.border = { top: {style:'thin'}, bottom: {style:'thin'}, left: {style:'thin'}, right: {style:'thin'} };
                                            
                                            // Had Burst cell - orange
                                            const burstCell = excelRow.getCell(baseCol + 1);
                                            burstCell.alignment = { horizontal: 'center' };
                                            burstCell.border = { top: {style:'thin'}, bottom: {style:'thin'}, left: {style:'thin'}, right: {style:'thin'} };
                                            if (burstLimit > 0) {
                                                burstCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFEF3C7' } };
                                                burstCell.font = { bold: true, color: { argb: 'FFD97706' } };
                                            }
                                            
                                            // Bilangan Burst cell - green/red
                                            const burstCountCell = excelRow.getCell(baseCol + 2);
                                            burstCountCell.alignment = { horizontal: 'center' };
                                            burstCountCell.font = { bold: true };
                                            burstCountCell.border = { top: {style:'thin'}, bottom: {style:'thin'}, left: {style:'thin'}, right: {style:'thin'} };
                                            if (burstCount > 0) {
                                                burstCountCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFEE2E2' } };
                                                burstCountCell.font = { bold: true, color: { argb: 'FFDC2626' } };
                                            } else {
                                                burstCountCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE0DAFF' } };
                                                burstCountCell.font = { bold: true, color: { argb: 'FF10056E' } };
                                            }
                                        }
                                        
                                        // Style fixed cols
                                        for (let c = 1; c <= 3; c++) {
                                            const cell = excelRow.getCell(c);
                                            cell.border = { top: {style:'thin'}, bottom: {style:'thin'}, left: {style:'thin'}, right: {style:'thin'} };
                                            if (c === 2) cell.font = { bold: true };
                                        }
                                    });
                                    
                                    // Set column widths
                                    ws.getColumn(1).width = 18;
                                    ws.getColumn(2).width = 35;
                                    ws.getColumn(3).width = 8;
                                    for (let m = 0; m < 12; m++) {
                                        ws.getColumn(4 + m * 3).width = 12;
                                        ws.getColumn(4 + m * 3 + 1).width = 11;
                                        ws.getColumn(4 + m * 3 + 2).width = 14;
                                    }
                                    
                                    // Row heights
                                    ws.getRow(1).height = 24;
                                    ws.getRow(2).height = 30;
                                    
                                    const buffer = await wb.xlsx.writeBuffer();
                                    saveAs(new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), `Burst_Report_${selYear}.xlsx`);
                                    showToast(t("history.burstReportDownloaded"));
                                }}
                                className="flex items-center gap-2 px-4 py-2 bg-[#10056E] hover:bg-[#0d0459] text-white text-sm font-bold rounded-xl transition-colors shadow-md"
                                data-testid="burst-download-excel-btn"
                            >
                                <Download size={16} /> {t('common.download')} Excel
                            </button>
                        </div>
                    </div>

                    {/* Burst Report */}
                    {(() => {
                        // Calculate burst violations for selected month
                        const [year, month] = burstReportMonth.split('-').map(Number);
                        const monthStart = new Date(year, month - 1, 1);
                        const monthEnd = new Date(year, month, 0, 23, 59, 59);
                        
                        // Calculate OUT transactions per item for the month
                        const monthlyOutflows = {};
                        projectTransactions.forEach(tx => {
                            if (tx.type === 'OUT') {
                                const txDate = new Date(tx.timestamp);
                                if (txDate >= monthStart && txDate <= monthEnd) {
                                    if (!monthlyOutflows[tx.itemId]) {
                                        monthlyOutflows[tx.itemId] = {
                                            itemId: tx.itemId,
                                            itemName: tx.itemName,
                                            totalOut: 0,
                                            transactions: []
                                        };
                                    }
                                    monthlyOutflows[tx.itemId].totalOut += tx.quantity;
                                    monthlyOutflows[tx.itemId].transactions.push(tx);
                                }
                            }
                        });
                        
                        // Check against burst limits
                        const burstViolations = [];
                        projectInventory.forEach(item => {
                            if (item.burstLimit && item.burstLimit > 0 && monthlyOutflows[item.id]) {
                                const outflow = monthlyOutflows[item.id];
                                if (outflow.totalOut > item.burstLimit) {
                                    burstViolations.push({
                                        ...item,
                                        monthlyOut: outflow.totalOut,
                                        excess: outflow.totalOut - item.burstLimit,
                                        percentage: ((outflow.totalOut / item.burstLimit) * 100).toFixed(1),
                                        transactionCount: outflow.transactions.length
                                    });
                                }
                            }
                        });
                        
                        // Sort by excess (highest first)
                        burstViolations.sort((a, b) => b.excess - a.excess);
                        
                        const monthName = new Date(year, month - 1).toLocaleDateString('en-MY', { month: 'long', year: 'numeric' });
                        
                        return (
                            <div className="bg-white rounded-3xl border border-slate-100 shadow-sm overflow-hidden">
                                <div className="p-6 border-b border-slate-100">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <h3 className="text-lg font-bold text-slate-800">{t('history.monthlyBurstReport')}</h3>
                                            <p className="text-sm text-slate-500 mt-1">{monthName}</p>
                                        </div>
                                        <div className="flex items-center gap-2 px-4 py-2 bg-amber-50 rounded-xl border border-amber-200">
                                            <AlertTriangle size={20} className="text-amber-600" />
                                            <span className="text-sm font-bold text-amber-700">{burstViolations.length} {t('history.itemsExceededLimit')}</span>
                                        </div>
                                    </div>
                                </div>
                                
                                {burstViolations.length === 0 ? (
                                    <div className="p-12 text-center">
                                        <CheckCircle size={64} className="mx-auto mb-4 text-emerald-400" />
                                        <h3 className="text-xl font-bold text-slate-700 mb-2">{t("history.noBurstViolations")}</h3>
                                        <p className="text-slate-500">{t("history.allItemsWithinLimit")}</p>
                                    </div>
                                ) : (
                                    <div className="overflow-x-auto">
                                        <table className="w-full">
                                            <thead className="bg-slate-50 border-b border-slate-200">
                                                <tr>
                                                    <th className="text-left p-4 text-xs font-bold text-slate-600 uppercase tracking-wider">{t("inventory.product")}</th>
                                                    <th className="text-left p-4 text-xs font-bold text-slate-600 uppercase tracking-wider">{t("history.categoryCol")}</th>
                                                    <th className="text-right p-4 text-xs font-bold text-slate-600 uppercase tracking-wider">{t("history.burstLimit")}</th>
                                                    <th className="text-right p-4 text-xs font-bold text-slate-600 uppercase tracking-wider">{t("inventory.out")} {t("po.thisMonth")}</th>
                                                    <th className="text-right p-4 text-xs font-bold text-slate-600 uppercase tracking-wider">{t("stockCheck.excess")}</th>
                                                    <th className="text-right p-4 text-xs font-bold text-slate-600 uppercase tracking-wider">{t("history.percentage")}</th>
                                                    <th className="text-center p-4 text-xs font-bold text-slate-600 uppercase tracking-wider">Status</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-slate-100">
                                                {burstViolations.map((item, idx) => (
                                                    <tr key={item.id} className="hover:bg-slate-50 transition-colors">
                                                        <td className="p-4">
                                                            <div className="flex items-center gap-3">
                                                                {item.image ? (
                                                                    <img src={item.image} className="w-10 h-10 rounded-lg object-cover border border-slate-200" alt={item.name} />
                                                                ) : (
                                                                    <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                                                                        <Package size={20} className="text-slate-400" />
                                                                    </div>
                                                                )}
                                                                <div>
                                                                    <div className="font-semibold text-slate-800">{item.name}</div>
                                                                    <div className="text-xs text-slate-500">{item.sku}</div>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td className="p-4">
                                                            <Badge color="slate">{item.category}</Badge>
                                                        </td>
                                                        <td className="p-4 text-right">
                                                            <span className="font-semibold text-slate-700">{formatNumber(item.burstLimit)} {item.unit}</span>
                                                        </td>
                                                        <td className="p-4 text-right">
                                                            <span className="font-bold text-rose-600">{formatNumber(item.monthlyOut)} {item.unit}</span>
                                                        </td>
                                                        <td className="p-4 text-right">
                                                            <span className="font-bold text-rose-700">+{formatNumber(item.excess)} {item.unit}</span>
                                                        </td>
                                                        <td className="p-4 text-right">
                                                            <span className="font-bold text-amber-600">{item.percentage}%</span>
                                                        </td>
                                                        <td className="p-4 text-center">
                                                            <Badge color="red">
                                                                <AlertTriangle size={12} className="inline mr-1" />
                                                                {t('history.exceededLimit')}
                                                            </Badge>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        );
                    })()}
                </>
            )}
        </div>
    );

    // Calculate dashboard metrics
    const pendingPOs = projectPOs.filter(po => po.status !== 'Selesai').length;
    const activeAudits = projectStockChecks.filter(sc => sc.status === 'In Progress').length;
    const todayTransactions = projectTransactions.filter(tx => {
        const txDate = new Date(tx.timestamp);
        const today = new Date();
        return txDate.toDateString() === today.toDateString();
    });
    const recentTransactions = [...projectTransactions].sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp)).slice(0, 10);
    
    // Calculate top moving items (most frequent transactions)
    const itemActivity = {};
    projectTransactions.forEach(tx => {
        if (!itemActivity[tx.itemId]) {
            itemActivity[tx.itemId] = { 
                itemId: tx.itemId, 
                itemName: tx.itemName, 
                totalIn: 0, 
                totalOut: 0, 
                count: 0,
                lastActivity: tx.timestamp
            };
        }
        itemActivity[tx.itemId].count++;
        if (tx.type === 'IN') itemActivity[tx.itemId].totalIn += tx.quantity;
        else itemActivity[tx.itemId].totalOut += tx.quantity;
        if (new Date(tx.timestamp) > new Date(itemActivity[tx.itemId].lastActivity)) {
            itemActivity[tx.itemId].lastActivity = tx.timestamp;
        }
    });
    const topMovingItems = Object.values(itemActivity)
        .sort((a, b) => b.count - a.count)
        .slice(0, 20); // Show top 20 most active products

    // Calculate category distribution for pie chart
    const categoryDistribution = {};
    projectInventory.forEach(item => {
        const cat = item.category || 'Others';
        if (!categoryDistribution[cat]) {
            categoryDistribution[cat] = { count: 0, value: 0 };
        }
        categoryDistribution[cat].count++;
        categoryDistribution[cat].value += (item.quantity * item.price) || 0;
    });

    // Calculate 30-day stock out data for line chart
    const stockOut30Days = useMemo(() => {
        const days = [];
        const now = new Date();
        for (let i = 29; i >= 0; i--) {
            const d = new Date(now);
            d.setDate(d.getDate() - i);
            const dateStr = d.toISOString().split('T')[0];
            const label = d.toLocaleDateString('en-MY', { day: '2-digit', month: 'short' });
            const count = projectTransactions.filter(tx => {
                if (tx.type !== 'OUT') return false;
                const txDate = new Date(tx.timestamp).toISOString().split('T')[0];
                return txDate === dateStr;
            }).length;
            days.push({ date: label, keluar: count });
        }
        return days;
    }, [projectTransactions]);

    const renderDashboard = () => (
        <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
            {/* Hero KPI Cards */}
            <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-5 gap-3 md:gap-4">
                {/* Total Value - Large Card - Only show if can view financials */}
                {can.viewFinancials && (
                    <Card className="col-span-1 md:col-span-2 lg:col-span-2 relative overflow-hidden bg-gradient-to-br from-[#10056E] to-[#070230] text-white border-none shadow-xl shadow-[#10056E]/20 cursor-pointer hover:shadow-2xl transition-all" onClick={() => setActiveTab('inventory')}>
                        <div className="relative z-10">
                            <p className="text-white/70 font-medium mb-1 flex items-center gap-2 text-sm">
                                <DollarSign size={16} /> {t('dashboard.inventoryValue')}
                            </p>
                            <h3 className="text-2xl sm:text-4xl font-bold tracking-tight mb-2">{formatCurrency(totalStockValue)}</h3>
                            <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
                                <div className="inline-flex items-center gap-1 px-2 py-0.5 sm:px-3 sm:py-1 bg-white/20 backdrop-blur-md rounded-lg text-[10px] sm:text-xs font-semibold">
                                    <TrendingUp size={12} /> +12% {t('dashboard.thisMonth')}
                                </div>
                                <div className="text-[10px] sm:text-xs text-white/70 font-medium">
                                    {formatNumber(projectInventory.length)} {t('dashboard.activeSKU')}
                                </div>
                            </div>
                        </div>
                        <div className="absolute right-0 bottom-0 opacity-10 transform translate-x-4 translate-y-4"><DollarSign size={100} className="sm:w-[140px] sm:h-[140px]" /></div>
                    </Card>
                )}
                
                {/* Low Stock Alert */}
                <Card className="group hover:border-rose-500 transition-all cursor-pointer" onClick={() => { setShowLowStockOnly(true); setActiveTab('inventory'); }}>
                    <div className="flex justify-between items-start mb-2 sm:mb-3">
                        <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl sm:rounded-2xl bg-rose-50 text-rose-600 flex items-center justify-center group-hover:scale-110 transition-transform">
                            <AlertTriangle size={20} />
                        </div>
                        {lowStockItems.length > 0 && <span className="text-[10px] sm:text-xs font-bold bg-rose-100 text-rose-600 px-1.5 py-0.5 sm:px-2 sm:py-1 rounded-lg animate-pulse">{t('dashboard.critical')}</span>}
                    </div>
                    <h3 className="text-2xl sm:text-3xl font-bold text-slate-800 mb-0.5">{formatNumber(lowStockItems.length)}</h3>
                    <p className="text-slate-500 text-xs sm:text-sm font-semibold">{t('dashboard.lowStock')}</p>
                </Card>
                
                {/* Pending POs */}
                <Card className="group hover:border-blue-500 transition-all cursor-pointer" onClick={() => setActiveTab('purchase_orders')}>
                    <div className="flex justify-between items-start mb-2 sm:mb-3">
                        <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl sm:rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center group-hover:scale-110 transition-transform">
                            <ShoppingBag size={20} />
                        </div>
                        {pendingPOs > 0 && <span className="text-[10px] sm:text-xs font-bold bg-blue-100 text-blue-600 px-1.5 py-0.5 sm:px-2 sm:py-1 rounded-lg">{t('dashboard.pending')}</span>}
                    </div>
                    <h3 className="text-2xl sm:text-3xl font-bold text-slate-800 mb-0.5">{formatNumber(pendingPOs)}</h3>
                    <p className="text-slate-500 text-xs sm:text-sm font-semibold">{t('dashboard.pendingPO')}</p>
                </Card>
                
                {/* Active Audits */}
                <Card className="group hover:border-amber-500 transition-all cursor-pointer" onClick={() => setActiveTab('stock_checks')}>
                    <div className="flex justify-between items-start mb-2 sm:mb-3">
                        <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl sm:rounded-2xl bg-amber-50 text-amber-600 flex items-center justify-center group-hover:scale-110 transition-transform">
                            <ClipboardCheck size={20} />
                        </div>
                        {activeAudits > 0 && <span className="text-[10px] sm:text-xs font-bold bg-amber-100 text-amber-600 px-1.5 py-0.5 sm:px-2 sm:py-1 rounded-lg">{t('dashboard.active')}</span>}
                    </div>
                    <h3 className="text-2xl sm:text-3xl font-bold text-slate-800 mb-0.5">{formatNumber(activeAudits)}</h3>
                    <p className="text-slate-500 text-xs sm:text-sm font-semibold">{t('dashboard.activeAudit')}</p>
                </Card>
            </div>

            {/* Quick Actions and AI Insights - All Inline */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                {/* Quick Actions */}
                <Card className="bg-gradient-to-r from-slate-50 to-white border-slate-200">
                    <div className="flex items-center gap-3 mb-3 sm:mb-4">
                        <Zap className="text-[#10056E]" size={20} />
                        <div>
                            <h3 className="font-bold text-slate-800 text-sm sm:text-base">{t('dashboard.quickActions')}</h3>
                            <p className="text-[10px] sm:text-xs text-slate-500">{t('dashboard.quickAccessMain')}</p>
                        </div>
                    </div>
                    <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2">
                        <button 
                            onClick={() => { setQuickActionType('IN'); setShowQuickActionSelector(true); }}
                            className="flex items-center justify-center sm:justify-start gap-1.5 sm:gap-2 px-2.5 sm:px-3 py-2 bg-emerald-500 hover:bg-emerald-600 text-white text-xs sm:text-sm font-bold rounded-xl transition-all shadow-lg shadow-emerald-500/30 active:scale-95"
                        >
                            <Plus size={14} /> {t('dashboard.stockIn')}
                        </button>
                        <button 
                            onClick={() => { setQuickActionType('OUT'); setShowQuickActionSelector(true); }}
                            className="flex items-center justify-center sm:justify-start gap-1.5 sm:gap-2 px-2.5 sm:px-3 py-2 bg-rose-500 hover:bg-rose-600 text-white text-xs sm:text-sm font-bold rounded-xl transition-all shadow-lg shadow-rose-500/30 active:scale-95"
                        >
                            <Minus size={14} /> {t('dashboard.stockOut')}
                        </button>
                        <button 
                            onClick={() => { setPoViewMode('create'); setActiveTab('purchase_orders'); }}
                            className="flex items-center justify-center sm:justify-start gap-1.5 sm:gap-2 px-2.5 sm:px-3 py-2 bg-blue-500 hover:bg-blue-600 text-white text-xs sm:text-sm font-bold rounded-xl transition-all shadow-lg shadow-blue-500/30 active:scale-95"
                        >
                            <ShoppingBag size={14} /> {t('dashboard.createPO')}
                        </button>
                        <button 
                            onClick={() => { setIsStockCheckModalOpen(true); }}
                            className="flex items-center justify-center sm:justify-start gap-1.5 sm:gap-2 px-2.5 sm:px-3 py-2 bg-amber-500 hover:bg-amber-600 text-white text-xs sm:text-sm font-bold rounded-xl transition-all shadow-lg shadow-amber-500/30 active:scale-95"
                        >
                            <ClipboardCheck size={14} /> {t('dashboard.startAudit')}
                        </button>
                    </div>
                </Card>

                {/* AI Insights */}
                {currentAppUser?.isSuperAdmin && (
                <Card className="bg-gradient-to-br from-purple-50 to-indigo-50 border-purple-200">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-indigo-600 text-white flex items-center justify-center shrink-0">
                            <Sparkles size={20} />
                        </div>
                        <div>
                            <h3 className="text-base font-bold text-purple-900">{t('dashboard.aiInsights')}</h3>
                            <p className="text-xs text-purple-600">{t('dashboard.smartAnalysis')}</p>
                        </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <button 
                            onClick={async () => {
                                try {
                                    setLoadingInsights(true);
                                    const result = await db.generateInsights(activeProject.id);
                                    setAiInsightsTitle(t('dashboard.generateAnalysis'));
                                    setAiInsightsContent(result.insights);
                                    setShowAIInsightsModal(true);
                                } catch (err) {
                                    console.error("Failed to generate insights:", err);
                                    alert((language === 'en' ? "Failed to generate AI insights: " : "Gagal menjana pandangan AI: ") + (err.response?.data?.detail || err.message));
                                } finally {
                                    setLoadingInsights(false);
                                }
                            }}
                            className="px-3 py-2 bg-purple-500 hover:bg-purple-600 text-white text-sm font-semibold rounded-xl transition-colors flex items-center gap-2 shadow-lg shadow-purple-500/20"
                            disabled={loadingInsights}
                        >
                            {loadingInsights ? (
                                <><Loader2 size={16} className="animate-spin" /> Menjana...</>
                            ) : (
                                <><Sparkles size={16} /> {t('dashboard.generateAnalysis')}</>
                            )}
                        </button>
                        <button 
                            onClick={async () => {
                                try {
                                    setLoadingForecast(true);
                                    const result = await db.generateForecast(activeProject.id);
                                    setAiInsightsTitle(t('dashboard.forecastInventory'));
                                    setAiInsightsContent(result.forecast);
                                    setShowAIInsightsModal(true);
                                } catch (err) {
                                    console.error("Failed to generate forecast:", err);
                                    alert((language === 'en' ? "Failed to generate forecast: " : "Gagal menjana forecast: ") + (err.response?.data?.detail || err.message));
                                } finally {
                                    setLoadingForecast(false);
                                }
                            }}
                            className="px-3 py-2 bg-indigo-500 hover:bg-indigo-600 text-white text-sm font-semibold rounded-xl transition-colors flex items-center gap-2 shadow-lg shadow-indigo-500/20"
                            disabled={loadingForecast}
                        >
                            {loadingForecast ? (
                                <><Loader2 size={16} className="animate-spin" /> Menjana...</>
                            ) : (
                                <><TrendingUp size={16} /> {t('dashboard.forecastInventory')}</>
                            )}
                        </button>
                    </div>
                </Card>
                )}
            </div>

            {/* Main Dashboard Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
                {/* Left Column - Analytics & Activities */}
                <div className="lg:col-span-2 space-y-4 md:space-y-6">
                    
                    {/* Charts & Analytics Section */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6">
                        {/* Stock Out 30 Days Line Chart */}
                        <Card>
                            <h3 className="font-bold text-slate-800 mb-3 flex items-center gap-2">
                                <TrendingDown size={18} className="text-rose-500" />
                                {t('dashboard.stockOut30Days')}
                            </h3>
                            <div className="h-[180px]">
                                <ResponsiveContainer width="100%" height="100%">
                                    <LineChart data={stockOut30Days}>
                                        <XAxis dataKey="date" tick={{ fontSize: 10 }} interval="preserveStartEnd" tickLine={false} axisLine={false} />
                                        <YAxis allowDecimals={false} tick={{ fontSize: 10 }} tickLine={false} axisLine={false} width={25} />
                                        <Tooltip contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 20px rgba(0,0,0,0.1)', fontSize: '12px' }} />
                                        <Line type="monotone" dataKey="keluar" stroke="#f43f5e" strokeWidth={2} dot={false} activeDot={{ r: 4, fill: '#f43f5e' }} />
                                    </LineChart>
                                </ResponsiveContainer>
                            </div>
                        </Card>

                        {/* Today's Activity */}
                        <Card>
                            <h3 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
                                <Clock size={20} className="text-blue-600" />
                                {t('dashboard.todayActivity')}
                            </h3>
                            <div className="space-y-4">
                                <div className="flex items-center justify-between p-3 bg-emerald-50 rounded-xl">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-lg bg-emerald-500 text-white flex items-center justify-center">
                                            <ArrowDownLeft size={20} />
                                        </div>
                                        <div>
                                            <div className="text-xs text-emerald-600 font-semibold">{t('dashboard.inLabel')}</div>
                                            <div className="text-sm font-bold text-slate-800">
                                                {todayTransactions.filter(t => t.type === 'IN').reduce((sum, t) => sum + t.quantity, 0)} {t('dashboard.unit')}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <div className="text-xs text-slate-500">{t('dashboard.transactions')}</div>
                                        <div className="text-lg font-bold text-emerald-600">
                                            {todayTransactions.filter(t => t.type === 'IN').length}
                                        </div>
                                    </div>
                                </div>
                                <div className="flex items-center justify-between p-3 bg-rose-50 rounded-xl">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-lg bg-rose-500 text-white flex items-center justify-center">
                                            <ArrowUpRight size={20} />
                                        </div>
                                        <div>
                                            <div className="text-xs text-rose-600 font-semibold">{t('dashboard.outLabel')}</div>
                                            <div className="text-sm font-bold text-slate-800">
                                                {todayTransactions.filter(t => t.type === 'OUT').reduce((sum, t) => sum + t.quantity, 0)} {t('dashboard.unit')}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <div className="text-xs text-slate-500">{t('dashboard.transactions')}</div>
                                        <div className="text-lg font-bold text-rose-600">
                                            {todayTransactions.filter(t => t.type === 'OUT').length}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </Card>
                    </div>

                    {/* Burst Limit Violations Warning - Moved Above Produk Paling Aktif */}
                    {burstViolations.length > 0 && (
                        <Card className="bg-gradient-to-r from-orange-50 to-amber-50 border-orange-200">
                            <div className="flex items-start gap-4">
                                <div className="w-12 h-12 rounded-xl bg-orange-500 text-white flex items-center justify-center shrink-0">
                                    <AlertTriangle size={24} />
                                </div>
                                <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-2">
                                        <h3 className="text-lg font-bold text-orange-900">Amaran Had Burst Bulanan</h3>
                                        <span className="px-2 py-0.5 bg-orange-500 text-white text-xs font-bold rounded-full animate-pulse">
                                            {burstViolations.length}
                                        </span>
                                    </div>
                                    <p className="text-sm text-orange-700 mb-4">
                                        {t("history.allItemsWithinLimit")}
                                    </p>
                                    <div className="space-y-2">
                                        {burstViolations.map(item => (
                                            <div key={item.id} className="flex items-center justify-between p-3 bg-white rounded-xl border border-orange-200">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-slate-100 to-slate-50 flex items-center justify-center overflow-hidden border border-slate-200">
                                                        {item.image ? <img src={item.image} className="w-full h-full object-cover" alt=""/> : <Package size={18} className="text-slate-400"/>}
                                                    </div>
                                                    <div>
                                                        <div className="font-bold text-slate-800 text-sm">{item.name}</div>
                                                        <div className="text-xs text-slate-500">{item.sku}</div>
                                                    </div>
                                                </div>
                                                <div className="text-right">
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-sm text-slate-600">{t("inventory.out")}:</span>
                                                        <span className="px-2 py-1 bg-rose-100 text-rose-700 font-bold text-sm rounded-md">
                                                            {item.monthlyOut}
                                                        </span>
                                                        <span className="text-sm text-slate-600">/</span>
                                                        <span className="text-sm font-bold text-slate-700">{item.burstLimit}</span>
                                                    </div>
                                                    <div className="text-xs text-orange-600 font-semibold mt-1">
                                                        {t('stockCheck.excess')}: +{item.exceeded} {language === 'en' ? 'units' : 'unit'}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </Card>
                    )}

                    {/* Top Moving Items - Taller to Fit All 20 Without Scroll */}
                    <Card>
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                                <TrendingUp size={20} className="text-purple-600" />
                                {language === 'en' ? 'Most Active Products (Top 20)' : 'Produk Paling Aktif (Top 20)'}
                            </h3>
                            <button onClick={() => setActiveTab('transactions')} className="text-sm font-semibold text-[#10056E] hover:text-[#0d0459]">
                                {t("dashboard.viewAll")}
                            </button>
                        </div>
                        {topMovingItems.length > 0 ? (
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead className="bg-slate-50 text-slate-500 font-semibold text-xs uppercase">
                                        <tr>
                                            <th className="p-3 text-left rounded-l-lg">{t("inventory.product")}</th>
                                            <th className="p-3 text-center">{t("inventory.in")}</th>
                                            <th className="p-3 text-center">{t("inventory.out")}</th>
                                            <th className="p-3 text-center">{t("dashboard.activity")}</th>
                                            <th className="p-3 text-right rounded-r-lg">{t("common.last")}</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100">
                                        {topMovingItems.slice(0, 20).map((item, idx) => (
                                            <tr key={item.itemId} className="hover:bg-slate-50 transition-colors">
                                                <td className="p-3">
                                                    <div className="flex items-center gap-2">
                                                        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-400 to-blue-500 text-white flex items-center justify-center text-xs font-bold">
                                                            #{idx + 1}
                                                        </div>
                                                        <span className="font-semibold text-slate-800">{item.itemName}</span>
                                                    </div>
                                                </td>
                                                <td className="p-3 text-center">
                                                    <span className="px-2 py-1 bg-emerald-100 text-emerald-700 rounded font-bold text-xs">
                                                        +{item.totalIn}
                                                    </span>
                                                </td>
                                                <td className="p-3 text-center">
                                                    <span className="px-2 py-1 bg-rose-100 text-rose-700 rounded font-bold text-xs">
                                                        -{item.totalOut}
                                                    </span>
                                                </td>
                                                <td className="p-3 text-center">
                                                    <span className="px-2.5 py-1 bg-purple-100 text-purple-700 rounded-full font-bold text-xs">
                                                        {item.count}x
                                                    </span>
                                                </td>
                                                <td className="p-3 text-right text-xs text-slate-500 font-medium">
                                                    {new Date(item.lastActivity).toLocaleDateString('en-MY', { day: 'numeric', month: 'short' })}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <div className="text-center py-10 text-slate-400">
                                <TrendingUp size={32} className="mx-auto mb-2 opacity-30" />
                                <p className="text-sm">{t("dashboard.noActivityRecorded")}</p>
                            </div>
                        )}
                    </Card>
                    
                    {/* Recent Transactions with Tabs */}
                    <Card>
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                                <History size={20} className="text-indigo-600" />
                                {t("dashboard.recentActivity")}
                            </h3>
                            <button onClick={() => setActiveTab('transactions')} className="text-sm font-semibold text-[#10056E] hover:text-[#0d0459]">
                                {t("dashboard.viewAll")}
                            </button>
                        </div>
                        {recentTransactions.length > 0 ? (
                            <div className="space-y-2">
                                {recentTransactions.slice(0, 8).map((tx) => (
                                    <div key={tx.id} className="flex items-center justify-between p-3 hover:bg-slate-50 rounded-xl transition-colors">
                                        <div className="flex items-center gap-3">
                                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${tx.type === 'IN' ? 'bg-emerald-100 text-emerald-600' : 'bg-rose-100 text-rose-600'}`}>
                                                {tx.type === 'IN' ? <ArrowDownLeft size={20}/> : <ArrowUpRight size={20}/>}
                                            </div>
                                            <div>
                                                <h4 className="font-bold text-slate-700 text-sm">{tx.itemName}</h4>
                                                <p className="text-xs text-slate-400 font-medium">
                                                    {new Date(tx.timestamp).toLocaleString('en-MY', { hour: '2-digit', minute:'2-digit', day:'numeric', month:'short' })} • {tx.userName || tx.user.split('@')[0]}
                                                </p>
                                            </div>
                                        </div>
                                        <div className={`font-bold text-lg ${tx.type === 'IN' ? 'text-emerald-600' : 'text-rose-600'}`}>
                                            {tx.type === 'IN' ? '+' : '-'}{formatNumber(tx.quantity)}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-10 text-slate-400">
                                <History size={32} className="mx-auto mb-2 opacity-30" />
                                <p className="text-sm">{t("dashboard.noActivityRecordedShort")}</p>
                            </div>
                        )}
                    </Card>
                </div>

                {/* Right Column - Critical Info & Widgets */}
                <div className="space-y-6">
                    {/* System Stats */}
                    <Card className="bg-gradient-to-br from-slate-50 to-slate-100 border-slate-200">
                        <h3 className="font-bold text-slate-800 mb-3 flex items-center gap-2">
                            <LayoutGrid size={18} className="text-emerald-600" />
                            {t('dashboard.systemStats')}
                        </h3>
                        <div className="space-y-2">
                            <div className="flex justify-between items-center">
                                <span className="text-xs text-slate-600">{t('dashboard.totalSKU')}</span>
                                <span className="font-bold text-slate-800">{projectInventory.length}</span>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="text-xs text-slate-600">{t('dashboard.totalTransactions')}</span>
                                <span className="font-bold text-slate-800">{projectTransactions.length}</span>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="text-xs text-slate-600">{t('dashboard.productCategories')}</span>
                                <span className="font-bold text-slate-800">{Object.keys(categoryDistribution).length}</span>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="text-xs text-slate-600">{t('dashboard.activeSuppliers')}</span>
                                <span className="font-bold text-slate-800">{projectSuppliers.length}</span>
                            </div>
                            <div className="flex justify-between items-center pt-2 border-t border-slate-300">
                                <span className="text-xs text-emerald-600 font-semibold">{t('dashboard.todayTransactions')}</span>
                                <span className="font-bold text-emerald-600">{todayTransactions.length}</span>
                            </div>
                        </div>
                    </Card>

                    {/* Critical Stock Card - More Compact Without Thumbnails */}
                    <Card>
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                                <AlertTriangle size={20} className="text-rose-600" />
                                {t('dashboard.criticalStock')}
                            </h3>
                            <span className="text-xs font-bold bg-rose-100 text-rose-600 px-2 py-1 rounded-lg">
                                {lowStockItems.length}
                            </span>
                        </div>
                        {lowStockItems.length > 0 ? (
                            <div className="space-y-2">
                                {lowStockItems.slice(0, 8).map(item => (
                                    <div key={item.id} className="flex items-center justify-between p-2.5 bg-rose-50/50 hover:bg-rose-50 rounded-lg transition-colors cursor-pointer text-sm" onClick={() => { setSelectedItem(item); setStockAction('IN'); setIsStockModalOpen(true); }}>
                                        <div className="flex-1 min-w-0">
                                            <div className="font-bold text-slate-800 truncate">{item.name}</div>
                                            <div className="text-xs text-slate-500">Min: {item.minLevel} {item.unit}</div>
                                        </div>
                                        <div className="text-right ml-3">
                                            <div className="font-bold text-rose-600 text-lg">{item.quantity}</div>
                                            <div className="text-[10px] uppercase font-bold text-rose-400">{language === 'en' ? 'Remaining' : 'Baki'}</div>
                                        </div>
                                    </div>
                                ))}
                                <button onClick={() => { setShowLowStockOnly(true); setActiveTab('inventory'); }} className="w-full py-2.5 mt-2 rounded-xl bg-rose-500 text-white text-sm font-bold hover:bg-rose-600 transition-all shadow-lg shadow-rose-500/30">
                                    {t('dashboard.manageAllLowStock')}
                                </button>
                            </div>
                        ) : (
                            <div className="text-center py-6">
                                <div className="w-12 h-12 bg-emerald-50 rounded-full flex items-center justify-center mx-auto mb-2 text-emerald-500">
                                    <CheckCircle size={24}/>
                                </div>
                                <p className="text-slate-700 font-bold text-sm">{language === 'en' ? 'All Good!' : 'Semua Baik!'}</p>
                                <p className="text-xs text-slate-500 mt-1">{t('dashboard.noProductsBelowMin')}</p>
                            </div>
                        )}
                    </Card>

                    {/* Price Log Widget - Taller to Fit All 10 Without Scroll */}
                    {can.viewFinancials && (
                        <Card>
                            <div className="flex items-center gap-2 mb-4">
                                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-500 to-purple-600 text-white flex items-center justify-center">
                                    <DollarSign size={16} />
                                </div>
                                <div>
                                    <h3 className="text-base font-bold text-slate-800">{t("dashboard.recentPriceLog")}</h3>
                                    <p className="text-xs text-slate-500">{language === "en" ? "10 most recent changes" : "10 perubahan terbaru"}</p>
                                </div>
                            </div>
                        
                        {projectPriceLogs.length > 0 ? (
                            <div className="space-y-2">
                                {projectPriceLogs.slice(0, 10).map((log, idx) => {
                                    const meta = log.metadata;
                                    const priceChange = meta ? (meta.newPrice - meta.oldPrice) : 0;
                                    const isIncrease = priceChange > 0;
                                    
                                    return (
                                        <div key={log.id || idx} className="flex items-start gap-2 p-2 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors">
                                            <div className={`w-6 h-6 rounded-md flex items-center justify-center text-white shrink-0 ${isIncrease ? 'bg-emerald-500' : 'bg-rose-500'}`}>
                                                {isIncrease ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-start justify-between gap-2">
                                                    <div className="flex-1 min-w-0">
                                                        <h4 className="text-xs font-bold text-slate-800 truncate">{log.itemName || meta?.itemName}</h4>
                                                        <p className="text-[10px] text-slate-400">{meta?.sku || ''}</p>
                                                    </div>
                                                    <div className="text-right shrink-0">
                                                        <div className="text-[10px] text-slate-400 line-through">
                                                            RM {formatNumber(meta?.oldPrice || 0, 2)}
                                                        </div>
                                                        <div className={`text-xs font-bold ${isIncrease ? 'text-emerald-600' : 'text-rose-600'}`}>
                                                            RM {formatNumber(meta?.newPrice || 0, 2)}
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="text-[10px] text-slate-400 mt-0.5">
                                                    {new Date(log.timestamp).toLocaleDateString('en-MY', { 
                                                        day: 'numeric', 
                                                        month: 'short'
                                                    })}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <div className="text-center py-6 text-slate-400">
                                <DollarSign size={24} className="mx-auto mb-2 opacity-30" />
                                <p className="text-xs">{t("dashboard.noPriceChanges")}</p>
                            </div>
                        )}
                    </Card>
                    )}
                </div>
            </div>
        </div>
    );

    if (loading) return <div className="flex h-screen items-center justify-center bg-slate-50"><Loader2 className="animate-spin text-[#10056E]" size={48} /></div>;

    // Public Product Page - accessible via QR code without login
    const urlParams = new URLSearchParams(window.location.search);
    const publicProductId = urlParams.get('product');
    if (publicProductId) {
        return <PublicProductPage productId={publicProductId} />;
    }

    if (!isLoggedIn) return <LoginScreen onLogin={handleLogin} />;

    return (
        <div className="min-h-screen bg-[#F8FAFC] text-slate-900 font-sans selection:bg-emerald-100 selection:text-emerald-900">
            {dataLoading && (
                <div className="fixed inset-0 z-[9999] flex flex-col items-center justify-center bg-slate-50/90 backdrop-blur-sm" data-testid="data-loading-overlay">
                    <Loader2 className="animate-spin text-[#10056E] mb-4" size={48} />
                    <p className="text-sm font-semibold text-slate-600">{t('common.loading')}</p>
                </div>
            )}
            {activeProject ? (
                <div className="flex h-[100dvh] overflow-hidden">
                    {/* Sidebar - Desktop only */}
                    <aside className="hidden md:flex md:flex-col w-72 bg-white border-r border-slate-100 shadow-xl shadow-slate-200/50">
                         <div className="p-8">
                             <div className="flex items-center mb-10">
                                 <div className="h-12 flex items-center justify-center">
                                     <img src="/logo-main.png" alt="ENHIVE by POMEN" className="h-full object-contain" />
                                 </div>
                             </div>
                             
                             <div className="mb-8">
                                 <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-3 pl-2">{t('nav.workspace')}</div>
                                 <div className={`bg-slate-50 p-4 rounded-2xl border border-slate-100 ${canSwitchProject ? 'group hover:border-[#10056E]/20 cursor-pointer' : ''} transition-colors`} onClick={() => canSwitchProject && setActiveProject(null)}>
                                     <div className="flex justify-between items-start mb-1">
                                         <div className="font-bold text-slate-800 truncate pr-2">{activeProject.name}</div>
                                         {canSwitchProject && <div className="text-emerald-500 opacity-0 group-hover:opacity-100 transition-opacity"><ArrowLeft size={16}/></div>}
                                     </div>
                                     <div className="text-xs text-slate-500 font-medium">{activeProject.warehouseName || activeProject.warehouse}</div>
                                 </div>
                             </div>

                             <nav className="space-y-2">
                                {[
                                    { id: 'dashboard', label: t('nav.dashboard'), icon: LayoutDashboard, permission: can.accessDashboard },
                                    { id: 'inventory', label: t('nav.stockOps'), icon: Package, permission: true },
                                    { id: 'purchase_orders', label: t('nav.purchaseOrders'), icon: ShoppingBag, permission: can.accessPO },
                                    { id: 'stock_checks', label: t('nav.stockAudit'), icon: ClipboardCheck, permission: can.accessStockCheck },
                                    { id: 'transactions', label: t('nav.history'), icon: History, permission: can.accessHistory },
                                    { id: 'settings', label: t('nav.settings'), icon: Settings, permission: can.accessSettings }
                                ].filter(tab => tab.permission).map(tab => (
                                    <button 
                                        key={tab.id} 
                                        onClick={() => { setActiveTab(tab.id); setIsSidebarOpen(false); }} 
                                        className={`w-full flex items-center gap-3 px-4 py-3.5 rounded-2xl text-sm font-semibold transition-all duration-200 group ${activeTab === tab.id ? 'bg-[#10056E]/10 text-[#10056E] shadow-sm ring-1 ring-[#10056E]/10' : 'text-slate-500 hover:bg-slate-50 hover:text-slate-900'}`}
                                    >
                                        <tab.icon size={20} className={activeTab === tab.id ? 'text-[#10056E]' : 'text-slate-400 group-hover:text-slate-600'} strokeWidth={activeTab === tab.id ? 2.5 : 2} />
                                        {tab.label}
                                    </button>
                                ))}
                             </nav>
                         </div>
                    </aside>
                    <main className="flex-1 overflow-y-auto pb-20 md:pb-0 flex flex-col">
                        <div className="flex-1 p-4 md:p-8 lg:p-10">
                        {/* Mobile Header */}
                        <header className="flex justify-between items-center mb-6 md:mb-10">
                             <div className="flex items-center gap-2 md:gap-4 min-w-0">
                                 {canSwitchProject && (
                                     <button onClick={() => setActiveProject(null)} className="md:hidden p-2 rounded-xl text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors flex-shrink-0" data-testid="mobile-back-btn">
                                         <ArrowLeft size={18}/>
                                     </button>
                                 )}
                                 <div className="min-w-0">
                                     <h1 className="text-xl md:text-3xl font-extrabold text-slate-900 tracking-tight capitalize truncate">
                                         {t(`pageTitles.${activeTab}`)}
                                     </h1>
                                     <p className="hidden md:block text-slate-500 font-medium mt-1">
                                         {t(`pageSubtitles.${activeTab}`)}
                                     </p>
                                 </div>
                             </div>
                             <div className="flex items-center gap-2 md:gap-4 flex-shrink-0">
                                 <div className="hidden md:flex items-center gap-2 px-4 py-2 bg-white rounded-full border border-slate-100 shadow-sm text-xs font-bold text-slate-600">
                                     <span className="w-2 h-2 rounded-full bg-enhive-yellow animate-pulse"></span>
                                     {t('dashboard.systemOnline')}
                                 </div>
                                 {/* Mobile: Settings gear icon */}
                                 {can.accessSettings && (
                                     <button 
                                         onClick={() => setActiveTab('settings')} 
                                         className={`md:hidden w-9 h-9 rounded-full flex items-center justify-center transition-colors ${activeTab === 'settings' ? 'bg-[#10056E]/10 text-[#10056E]' : 'bg-white text-slate-500 shadow-sm'}`}
                                         data-testid="mobile-settings-btn"
                                     >
                                         <Settings size={18}/>
                                     </button>
                                 )}
                                 <ProfileDropdown currentUser={currentAppUser} onOpenProfile={() => setIsProfileModalOpen(true)} onLogout={handleLogout} />
                             </div>
                        </header>
                        
                        {activeTab === 'dashboard' && renderDashboard()}
                        {activeTab === 'inventory' && renderInventoryOps()}
                        {activeTab === 'purchase_orders' && renderPurchaseOrders()}
                        {activeTab === 'stock_checks' && renderStockCheck()}
                        {activeTab === 'transactions' && renderHistory()}
                        {activeTab === 'settings' && (
                            <SettingsModule
                                activeSettingsTab={activeSettingsTab}
                                setActiveSettingsTab={setActiveSettingsTab}
                                searchTerm={searchTerm}
                                setSearchTerm={setSearchTerm}
                                filteredInventory={filteredInventory}
                                priceLogs={priceLogs}
                                can={can}
                                setIsCategoryModalOpen={setIsCategoryModalOpen}
                                setSelectedItem={setSelectedItem}
                                setIsItemModalOpen={setIsItemModalOpen}
                                setViewingImage={setViewingImage}
                                setSelectedItemForPriceLog={setSelectedItemForPriceLog}
                                setShowPriceLogModal={setShowPriceLogModal}
                                requestConfirm={requestConfirm}
                                db={db}
                                fetchData={fetchData}
                                suppliers={projectSuppliers}
                                setEditingSupplier={setEditingSupplier}
                                setIsSupplierModalOpen={setIsSupplierModalOpen}
                                activeProject={activeProject}
                                selectedProjectIcon={selectedProjectIcon}
                                setSelectedProjectIcon={setSelectedProjectIcon}
                                setConfirmModal={setConfirmModal}
                                showToast={showToast}
                                setIsDeleteProjectModalOpen={setIsDeleteProjectModalOpen}
                                setDeleteProjectPassword={setDeleteProjectPassword}
                                setDeleteProjectError={setDeleteProjectError}
                            />
                        )}
                        </div>
                        <div className="hidden md:block"><Footer /></div>
                    </main>
                    
                    {/* Mobile Bottom Navigation */}
                    <nav className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-slate-200 safe-area-bottom" data-testid="mobile-bottom-nav">
                        <div className="flex items-stretch justify-around h-16">
                            {[
                                { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, permission: can.accessDashboard },
                                { id: 'inventory', label: t('dashboard.stockLabel'), icon: Package, permission: true },
                                { id: 'purchase_orders', label: 'PO', icon: ShoppingBag, permission: can.accessPO },
                                { id: 'stock_checks', label: language === 'en' ? 'Audit' : 'Audit', icon: ClipboardCheck, permission: can.accessStockCheck },
                                { id: 'transactions', label: t('nav.history'), icon: History, permission: can.accessHistory },
                            ].filter(tab => tab.permission).map(tab => (
                                <button
                                    key={tab.id}
                                    onClick={() => setActiveTab(tab.id)}
                                    className={`flex flex-col items-center justify-center flex-1 gap-0.5 transition-colors ${
                                        activeTab === tab.id 
                                            ? 'text-[#10056E]' 
                                            : 'text-slate-400'
                                    }`}
                                    data-testid={`mobile-nav-${tab.id}`}
                                >
                                    <tab.icon size={20} strokeWidth={activeTab === tab.id ? 2.5 : 1.8} />
                                    <span className="text-[10px] font-semibold leading-tight">{tab.label}</span>
                                    {activeTab === tab.id && <span className="absolute bottom-1 w-5 h-0.5 rounded-full bg-enhive-yellow"></span>}
                                </button>
                            ))}
                        </div>
                    </nav>
                </div>
            ) : (
                <div className="max-w-7xl mx-auto px-4 py-6 sm:p-6 md:p-12 min-h-[100dvh] flex flex-col">
                    <header className="flex justify-between items-center mb-8 md:mb-16">
                        <div className="flex items-center gap-2 sm:gap-3">
                            <div className="h-8 sm:h-12 flex items-center justify-center flex-shrink-0">
                                <img src="/logo-main.png" alt="ENHIVE by POMEN" className="h-full object-contain" />
                            </div>
                        </div>
                        <div className="flex items-center gap-2 sm:gap-4">
                            {currentAppUser?.isSuperAdmin && (
                                <button 
                                    onClick={() => setActiveTab('user_management')}
                                    className="hidden sm:flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-slate-200 hover:border-[#10056E] hover:bg-[#10056E]/5 transition-all shadow-sm"
                                >
                                    <Users size={18} className="text-[#10056E]" />
                                    <span className="text-sm font-semibold text-slate-700">{t('projects.manageUsers')}</span>
                                </button>
                            )}
                            {currentAppUser?.isSuperAdmin && (
                                <button 
                                    onClick={() => setActiveTab('user_management')}
                                    className="sm:hidden w-9 h-9 rounded-full bg-white border border-slate-200 flex items-center justify-center shadow-sm"
                                    data-testid="mobile-user-mgmt-btn"
                                >
                                    <Users size={16} className="text-[#10056E]" />
                                </button>
                            )}
                            <ProfileDropdown currentUser={currentAppUser} onOpenProfile={() => setIsProfileModalOpen(true)} onLogout={handleLogout} />
                        </div>
                    </header>
                    
                    {activeTab === 'user_management' ? (
                        /* User Management Page */
                        <div className="space-y-6">
                            <div className="flex items-center gap-4 mb-8">
                                <button 
                                    onClick={() => setActiveTab('dashboard')}
                                    className="w-10 h-10 rounded-full hover:bg-slate-100 flex items-center justify-center transition-colors"
                                >
                                    <ArrowLeft size={20} className="text-slate-600" />
                                </button>
                                <div>
                                    <h2 className="text-4xl font-extrabold text-slate-900 tracking-tight">{t('projects.userManagement')}</h2>
                                    <p className="text-lg text-slate-500 font-medium mt-1">{t('projects.manageAccess')}</p>
                                </div>
                            </div>

                            <div className="flex justify-end mb-6">
                                <Button onClick={() => { setEditingUser(null); setIsUserModalOpen(true); }} icon={Plus}>{t("userMgmt.createNewUserBtn")}</Button>
                            </div>

                            <div className="bg-white rounded-3xl border border-slate-100 overflow-hidden shadow-sm">
                                <div className="overflow-x-auto">
                                    <table className="w-full">
                                        <thead className="bg-slate-50 border-b border-slate-100">
                                            <tr>
                                                <th className="px-6 py-4 text-left text-xs font-bold text-slate-500 uppercase tracking-wider">{language === 'en' ? 'USER' : 'Pengguna'}</th>
                                                <th className="px-6 py-4 text-left text-xs font-bold text-slate-500 uppercase tracking-wider">{language === 'en' ? 'EMAIL' : 'Emel'}</th>
                                                <th className="px-6 py-4 text-left text-xs font-bold text-slate-500 uppercase tracking-wider">{language === 'en' ? 'ROLE' : 'Peranan'}</th>
                                                <th className="px-6 py-4 text-left text-xs font-bold text-slate-500 uppercase tracking-wider">{t("userMgmt.projectAccessCol")}</th>
                                                <th className="px-6 py-4 text-left text-xs font-bold text-slate-500 uppercase tracking-wider">{language === 'en' ? 'STATUS' : 'Status'}</th>
                                                <th className="px-6 py-4 text-center text-xs font-bold text-slate-500 uppercase tracking-wider">{language === 'en' ? 'ACTIONS' : 'Tindakan'}</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-100">
                                            {projectUsers.map(u => (
                                                <tr key={u.id} className="hover:bg-slate-50 transition-colors">
                                                    <td className="px-6 py-4">
                                                        <div className="flex items-center gap-3">
                                                            <div className="w-10 h-10 rounded-full bg-[#10056E]/10 text-[#10056E] flex items-center justify-center font-bold text-sm">
                                                                {u.name?.charAt(0).toUpperCase()}
                                                            </div>
                                                            <div>
                                                                <div className="font-semibold text-slate-800">{u.name}</div>
                                                                {u.hideFinancials && (
                                                                    <div className="text-xs text-amber-600 flex items-center gap-1 mt-1">
                                                                        <EyeOff size={12} />
                                                                        <span>Kewangan Disembunyikan</span>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 text-sm text-slate-600">{u.email}</td>
                                                    <td className="px-6 py-4">
                                                        {u.isSuperAdmin ? (
                                                            <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-purple-100 text-purple-700 text-xs font-bold">
                                                                <Crown size={12} />
                                                                Super Admin
                                                            </span>
                                                        ) : (
                                                            <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-blue-100 text-blue-700 text-xs font-bold">
                                                                <Shield size={12} />
                                                                {t("userMgmt.projectLevel")}
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        {u.isSuperAdmin ? (
                                                            <span className="text-xs text-slate-500 font-medium">{t("userMgmt.allProjectsGlobal")}</span>
                                                        ) : (
                                                            <div className="flex flex-col gap-1">
                                                                {u.access && u.access.length > 0 ? (
                                                                    u.access.map((acc, idx) => {
                                                                        const proj = projects.find(p => p.id === acc.projectId);
                                                                        return proj ? (
                                                                            <div key={idx} className="text-xs">
                                                                                <span className="font-semibold text-slate-700">{proj.name}</span>
                                                                                <span className="text-slate-400"> - {acc.role}</span>
                                                                            </div>
                                                                        ) : null;
                                                                    })
                                                                ) : (
                                                                    <span className="text-xs text-slate-400">{t("userMgmt.noAccess")}</span>
                                                                )}
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        {u.status === 'Aktif' || u.status === 'Active' ? (
                                                            <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-emerald-100 text-emerald-700 text-xs font-bold">
                                                                <CheckCircle size={12} />
                                                                {t('userMgmt.activeStatus')}
                                                            </span>
                                                        ) : (
                                                            <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-slate-100 text-slate-500 text-xs font-bold">
                                                                <Ban size={12} />
                                                                {t('userMgmt.inactiveStatus')}
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        <div className="flex items-center justify-center gap-2">
                                                            <button
                                                                onClick={() => { setEditingUser(u); setIsUserModalOpen(true); }}
                                                                className="w-8 h-8 rounded-lg hover:bg-blue-50 flex items-center justify-center text-blue-600 transition-colors"
                                                                title="Edit"
                                                            >
                                                                <Edit2 size={16} />
                                                            </button>
                                                            {!u.isSuperAdmin && (
                                                                <button
                                                                    onClick={() => handleDeleteUser(u.id)}
                                                                    className="w-8 h-8 rounded-lg hover:bg-rose-50 flex items-center justify-center text-rose-600 transition-colors"
                                                                    title={t("userMgmt.deleteTooltip")}
                                                                >
                                                                    <Trash2 size={16} />
                                                                </button>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                    {projectUsers.length === 0 && (
                                        <div className="text-center py-12 text-slate-400">
                                            <Users size={48} className="mx-auto mb-4 opacity-50" />
                                            <p className="font-medium">{t("userMgmt.noUsersFound")}</p>
                                            <p className="text-sm mt-1">{t("userMgmt.clickCreateNewUser")}</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                            <Footer />
                        </div>
                    ) : (
                        /* Project Listing Page */
                        <>
                    <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 sm:gap-6 mb-6 sm:mb-10">
                        <div>
                            <h2 className="text-2xl sm:text-4xl font-extrabold text-slate-900 tracking-tight mb-1 sm:mb-2">{t('projects.welcome')}, {currentAppUser.name} 👋</h2>
                            <p className="text-sm sm:text-lg text-slate-500 font-medium">{t('projects.selectProject')}</p>
                        </div>
                        <div className="flex gap-3">
                            {/* Export BSON - hidden for now
                            {currentAppUser?.isSuperAdmin && (
                                <button
                                    onClick={async () => {
                                        try {
                                            const response = await api.get('/database/export-bson', { responseType: 'blob' });
                                            const url = window.URL.createObjectURL(new Blob([response.data]));
                                            const a = document.createElement('a');
                                            a.href = url;
                                            a.download = `enhive_backup_${new Date().toISOString().slice(0,10)}.bson`;
                                            a.click();
                                            window.URL.revokeObjectURL(url);
                                        } catch (err) {
                                            console.error('Export failed:', err);
                                        }
                                    }}
                                    className="px-4 py-2.5 rounded-xl bg-slate-200 text-slate-700 hover:bg-slate-300 transition-all font-bold text-sm flex items-center gap-2"
                                    data-testid="export-bson-btn"
                                >
                                    <Download size={16}/> {language === 'en' ? 'Export BSON' : 'Eksport BSON'}
                                </button>
                            )}
                            */}
                            {can.createProject && (
                                <Button onClick={() => setIsProjectModalOpen(true)} icon={Plus}>{t('projects.newProject')}</Button>
                            )}
                        </div>
                    </div>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                        {accessibleProjects.map(p => {
                            const ProjectIcon = getIconComponent(p.icon || 'Package');
                            return (
                            <div key={p.id} onClick={() => setActiveProject(p)} className="group bg-white p-6 sm:p-8 rounded-2xl sm:rounded-[2rem] border border-slate-100 hover:border-[#10056E]/30 hover:shadow-xl hover:shadow-[#10056E]/5 cursor-pointer transition-all duration-300 relative overflow-hidden active:scale-[0.98]" data-testid={`project-card-${p.id}`}>
                                <div className="absolute top-0 right-0 w-24 sm:w-32 h-24 sm:h-32 bg-gradient-to-br from-slate-50 to-white rounded-bl-[3rem] sm:rounded-bl-[4rem] -mr-6 sm:-mr-8 -mt-6 sm:-mt-8 z-0"></div>
                                <div className="relative z-10">
                                    <div className="flex justify-between items-start mb-4 sm:mb-6">
                                        <div className="w-12 h-12 sm:w-14 sm:h-14 bg-[#10056E]/10 text-[#10056E] rounded-xl sm:rounded-2xl flex items-center justify-center shadow-sm group-hover:scale-110 transition-transform duration-300">
                                            <ProjectIcon size={24} />
                                        </div>
                                    </div>
                                    <h3 className="text-lg sm:text-xl font-bold text-slate-800 mb-1.5 sm:mb-2 group-hover:text-[#10056E] transition-colors">{p.name}</h3>
                                    <p className="text-slate-500 text-sm leading-relaxed mb-4 sm:mb-6 line-clamp-2">{p.description || t("common.noDescription")}</p>
                                    <div className="flex items-center gap-2 text-xs font-bold text-slate-400 uppercase tracking-wider">
                                        <Store size={14} /> {p.warehouseName || p.warehouse || t("common.mainWarehouse")}
                                    </div>
                                </div>
                            </div>
                            );
                        })}
                        {accessibleProjects.length === 0 && (
                            <div onClick={() => currentAppUser?.isSuperAdmin && setIsProjectModalOpen(true)} className={`flex flex-col items-center justify-center p-10 border-2 border-dashed border-slate-200 rounded-[2rem] ${currentAppUser?.isSuperAdmin ? 'cursor-pointer hover:border-[#10056E]/40 hover:bg-[#10056E]/5' : ''} transition-all group`}>
                                <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center shadow-sm mb-4 text-slate-300 group-hover:text-[#10056E] transition-colors">{currentAppUser?.isSuperAdmin ? <Plus size={32}/> : <Package size={32}/>}</div>
                                <h3 className="font-bold text-lg text-slate-600 group-hover:text-[#10056E]">{currentAppUser?.isSuperAdmin ? t('projects.createFirstProject') : t('projects.noProjectsAssigned')}</h3>
                                <p className="text-sm text-slate-400 mt-1">{currentAppUser?.isSuperAdmin ? t('projects.startManaging') : t('projects.contactSuperAdmin')}</p>
                            </div>
                        )}
                    </div>
                        <Footer />
                        </>
                    )}
                </div>
            )}

            {/* Global Modals - Kept same logic, updated styling via Modal component */}
            <Modal isOpen={isProjectModalOpen} onClose={() => setIsProjectModalOpen(false)} title={t('projects.createNewProject')}><form onSubmit={handleCreateProject} className="space-y-6"><Input label={t('settings.projectName')} name="name" required placeholder={t("projects.exampleWarehouse")} /><Input label={t('projects.description')} name="description" placeholder={t('projects.briefDescription')} /><div className="flex justify-end gap-3 pt-4"><Button variant="secondary" type="button" onClick={() => setIsProjectModalOpen(false)}>{t('common.cancel')}</Button><Button type="submit" disabled={isSubmitting}>{isSubmitting ? <><Loader2 className="animate-spin" size={18} /> {t('common.processing')}</> : t('projects.createProject')}</Button></div></form></Modal>
            <Modal isOpen={isItemModalOpen} onClose={() => setIsItemModalOpen(false)} title={selectedItem ? t('itemModal.updateProduct') : t('itemModal.addNewProduct')}>
                <form onSubmit={handleAddItem} className="space-y-6">
                    <Input label={t('itemModal.productName')} name="name" defaultValue={selectedItem?.name} required placeholder={language === "en" ? "E.g.: Running Shoes" : "Cth: Kasut Sukan"} />
                    
                    {/* Product Image - After Nama Produk */}
                    <div className="border-2 border-dashed border-slate-200 p-6 rounded-2xl bg-slate-50 hover:bg-white transition-colors">
                        <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">{t('itemModal.productImage')}</p>
                        
                        {/* Show current image if exists and no preview */}
                        {selectedItem?.image && !previewImage && (
                            <div className="mb-4 p-3 bg-white rounded-xl border border-slate-200">
                                <p className="text-xs font-bold text-slate-500 mb-2">{language === 'en' ? 'CURRENT IMAGE' : 'IMEJ SEMASA'}</p>
                                <img src={selectedItem.image} className="w-24 h-24 rounded-lg object-cover border-2 border-slate-200 mb-2" alt="Current" />
                                <p className="text-xs text-slate-600">{language === 'en' ? 'Upload a new image to replace' : 'Muat naik imej baru untuk menggantikan'}</p>
                            </div>
                        )}
                        
                        {/* Show preview if new image selected */}
                        {previewImage && (
                            <div className="mb-4 p-3 bg-white rounded-xl border border-emerald-200">
                                <p className="text-xs font-bold text-emerald-600 mb-2">{language === 'en' ? 'NEW IMAGE' : 'IMEJ BARU'}</p>
                                <img src={previewImage} className="w-24 h-24 rounded-lg object-cover border-2 border-emerald-300" alt="Preview" />
                            </div>
                        )}
                        
                        <input 
                            type="file" 
                            onChange={(e) => { 
                                const file = e.target.files[0]; 
                                if(file){ 
                                    const reader = new FileReader(); 
                                    reader.onloadend = () => setPreviewImage(reader.result); 
                                    reader.readAsDataURL(file); 
                                } 
                            }} 
                            className="text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-xs file:font-bold file:bg-emerald-100 file:text-emerald-700 hover:file:bg-emerald-200 cursor-pointer"
                        />
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                        <Input label={t('itemModal.sku')} name="sku" defaultValue={selectedItem?.sku} required placeholder="SH-001" />
                        <Select label={t('itemModal.category')} name="category" defaultValue={selectedItem?.category || INITIAL_CATEGORIES[0]}>
                            {projectCategories.map(c => <option key={c} value={c}>{c}</option>)}
                        </Select>
                    </div>
                    
                    <Input label={language === 'en' ? 'BIN No.' : 'No. BIN'} name="binNo" defaultValue={selectedItem?.binNo || ''} placeholder={language === 'en' ? 'E.g.: A1-01' : 'Cth: A1-01'} />

                    <div className="grid grid-cols-2 gap-4">
                        <Input label={t('itemModal.unit')} name="unit" defaultValue={selectedItem?.unit || 'pcs'} placeholder="pcs, kg, box" />
                        {can.viewFinancials && (
                            <Input label={t('itemModal.costPrice')} name="price" type="number" step="0.01" defaultValue={selectedItem?.price} />
                        )}
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                        <Input label={t('itemModal.minStock')} name="minLevel" type="number" defaultValue={selectedItem?.minLevel || 5} />
                        <Input label={language === 'en' ? 'Burst Limit (Monthly)' : 'Had Burst (Bulanan)'} name="burstLimit" type="number" defaultValue={selectedItem?.burstLimit || 0} placeholder={language === 'en' ? 'Monthly out limit' : 'Had keluar bulanan'} />
                    </div>
                    
                    {/* Instruction Box - Moved to bottom */}
                    <div className="flex items-start gap-3 p-4 rounded-xl bg-amber-50 border border-amber-100">
                        <Info size={20} className="text-amber-700 shrink-0 mt-0.5" />
                        <p className="text-sm text-amber-800">
                            {language === 'en' 
                                ? <>Stock quantities and location are managed through the <span className="font-bold">Stock Operations</span> tab to ensure proper audit trails.</>
                                : <>{t("inventory.stockQtyManagedNote")} <span className="font-bold">{t("inventory.stockOpsTab")}</span> {t("inventory.stockQtyManagedNote2")}</>
                            }
                        </p>
                    </div>
                    
                    <div className="flex justify-end gap-3 pt-6 border-t border-slate-100">
                        <Button variant="secondary" type="button" onClick={() => setIsItemModalOpen(false)}>{t('common.cancel')}</Button>
                        <Button type="submit" disabled={isSubmitting}>
                            {isSubmitting ? <><Loader2 className="animate-spin" size={18} /> {language === 'en' ? 'Saving...' : 'Menyimpan...'}</> : t('common.save')}
                        </Button>
                    </div>
                </form>
            </Modal>
            <Modal isOpen={isStockModalOpen} onClose={() => setIsStockModalOpen(false)} title={stockAction === 'IN' ? t('stockModal.stockInTitle') : t('stockModal.stockOutTitle')}>
                <form onSubmit={handleStockAdjustment} className="space-y-6">
                    {/* Product Info Card */}
                    <div className={`p-4 rounded-xl border-2 ${stockAction === 'IN' ? 'bg-emerald-50 border-emerald-200' : 'bg-rose-50 border-rose-200'}`}>
                        <div className="flex items-center gap-4">
                            <div className={`w-14 h-14 rounded-xl flex items-center justify-center shadow-sm ${stockAction === 'IN' ? 'bg-emerald-500' : 'bg-rose-500'}`}>
                                {stockAction === 'IN' ? <Plus size={28} className="text-white"/> : <Minus size={28} className="text-white"/>}
                            </div>
                            <div className="flex-1">
                                <div className="font-bold text-slate-900 text-lg">{selectedItem?.name}</div>
                                <div className="flex items-center gap-2 mt-1">
                                    <span className="text-sm text-slate-600">Baki Semasa:</span>
                                    <span className={`px-2 py-0.5 rounded-md text-sm font-bold ${
                                        selectedItem?.quantity <= selectedItem?.minLevel 
                                            ? 'bg-rose-100 text-rose-700' 
                                            : 'bg-emerald-100 text-emerald-700'
                                    }`}>
                                        {selectedItem?.quantity} {selectedItem?.unit}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Required Fields */}
                    <div className="grid grid-cols-2 gap-4">
                        <Input 
                            label="KUANTITI" 
                            name="quantity" 
                            type="number" 
                            min="1" 
                            required 
                            autoFocus 
                            className="text-lg font-bold"
                            placeholder="0" 
                        />
                        <Input 
                            label={language === "en" ? "REASON / REFERENCE" : "SEBAB / RUJUKAN"} 
                            name="reason" 
                            required 
                            placeholder={t("inventory.invoiceRef")} 
                        />
                    </div>

                    {/* File Upload - Required */}
                    <div>
                        <label className="block text-xs font-bold text-slate-500 mb-2">{language === "en" ? "PROOF DOCUMENT / IMAGE" : "BUKTI DOKUMEN / GAMBAR"} <span className="text-rose-500">*</span></label>
                        <div 
                            className={`border-2 border-dashed p-6 rounded-xl text-center transition-all cursor-pointer ${
                                stockProofImage 
                                    ? 'border-emerald-400 bg-emerald-50/50' 
                                    : 'border-slate-300 hover:border-[#10056E]/40 hover:bg-[#10056E]/5'
                            }`}
                            onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('border-emerald-500', 'bg-emerald-100'); }}
                            onDragLeave={(e) => { e.currentTarget.classList.remove('border-emerald-500', 'bg-emerald-100'); }}
                            onDrop={(e) => {
                                e.preventDefault();
                                e.currentTarget.classList.remove('border-emerald-500', 'bg-emerald-100');
                                const file = e.dataTransfer.files[0];
                                if (file && file.type.startsWith('image/')) {
                                    const reader = new FileReader();
                                    reader.onloadend = () => setStockProofImage(reader.result);
                                    reader.readAsDataURL(file);
                                }
                            }}
                            onClick={() => document.getElementById('proof-upload').click()}
                        >
                            <input 
                                type="file" 
                                accept="image/*"
                                onChange={(e) => { 
                                    const file = e.target.files[0]; 
                                    if(file){ 
                                        const reader = new FileReader(); 
                                        reader.onloadend = () => setStockProofImage(reader.result); 
                                        reader.readAsDataURL(file); 
                                    } 
                                }} 
                                className="hidden"
                                id="proof-upload"
                            />
                            {stockProofImage ? (
                                <div className="space-y-3">
                                    <div className="w-32 h-32 mx-auto rounded-lg overflow-hidden border-2 border-emerald-500 shadow-lg">
                                        <img src={stockProofImage} alt="Preview" className="w-full h-full object-cover" />
                                    </div>
                                    <div className="flex items-center justify-center gap-2 text-emerald-600">
                                        <CheckCircle size={20} />
                                        <p className="text-sm font-bold">{language === "en" ? "Image uploaded successfully" : "Imej berjaya dimuat naik"}</p>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={(e) => { e.stopPropagation(); setStockProofImage(null); document.getElementById('proof-upload').value = ''; }}
                                        className="text-xs text-slate-500 hover:text-rose-600 underline"
                                    >
                                        {language === "en" ? "Change image" : "Tukar imej"}
                                    </button>
                                </div>
                            ) : (
                                <div>
                                    <Upload size={40} className="mx-auto mb-3 text-slate-400" />
                                    <p className="text-sm font-bold text-slate-700 mb-1">{language === "en" ? "Click or drag & drop image here" : "Klik atau seret & lepas imej ke sini"}</p>
                                    <p className="text-xs text-slate-500">JPG, PNG (Max 5MB)</p>
                                    <p className="text-xs text-rose-600 font-medium mt-2">{language === "en" ? "* Proof document required" : "* Bukti dokumen diperlukan"}</p>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Optional Section */}
                    <div className="pt-4 border-t border-slate-200">
                        <div className="flex items-center gap-2 mb-4">
                            <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">{t("inventory.additionalInfoOptional")}</span>
                            <div className="flex-1 h-px bg-slate-200"></div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <Input 
                                label={language === "en" ? "PIC NAME" : "NAMA PIC"} 
                                name="picName" 
                                placeholder={language === "en" ? "Person In Charge Name" : "Nama Person In Charge"} 
                            />
                            <Input 
                                label={language === "en" ? "PIC PHONE NO." : "NO. TEL PIC"} 
                                name="picContact" 
                                placeholder="012-3456789" 
                            />
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex justify-end gap-3 pt-4 border-t border-slate-100">
                        <Button 
                            variant="secondary" 
                            type="button" 
                            onClick={() => { setIsStockModalOpen(false); setStockProofImage(null); }}
                        >
                            Batal
                        </Button>
                        <Button 
                            type="submit" 
                            disabled={isSubmitting}
                            className={stockAction === 'IN' ? 'bg-emerald-500 hover:bg-emerald-600' : 'bg-rose-500 hover:bg-rose-600'}
                        >
                            {isSubmitting ? (
                                <><Loader2 className="animate-spin" size={18} /> Memproses...</>
                            ) : stockAction === 'IN' ? (
                                <><Plus size={18} /> {t("inventory.confirmStockIn")}</>
                            ) : (
                                <><Minus size={18} /> {t("inventory.confirmStockOut")}</>
                            )}
                        </Button>
                    </div>
                </form>
            </Modal>
            <Modal isOpen={isStockCheckModalOpen} onClose={() => setIsStockCheckModalOpen(false)} title={t('stockCheck.startNewAudit')}><form onSubmit={handleStartStockCheck} className="space-y-6"><div className="bg-blue-50 text-blue-700 p-4 rounded-2xl text-sm font-medium flex gap-3"><Info size={20} className="shrink-0"/> <p>{t('stockCheck.auditInfoNote')}</p></div><Input label={t('stockCheck.auditorName')} name="auditorName" required placeholder={t('stockCheck.officerOnDuty')} /><Input label={t('stockCheck.auditDate')} name="auditDate" type="date" defaultValue={new Date().toISOString().slice(0, 10)} required /><Input label={t('stockCheck.auditNotes')} name="notes" placeholder={t('stockCheck.additionalNotes')} /><div className="flex justify-end gap-3 pt-4"><Button variant="secondary" type="button" onClick={() => setIsStockCheckModalOpen(false)}>{t('common.cancel')}</Button><Button type="submit" disabled={isSubmitting}>{isSubmitting ? <><Loader2 className="animate-spin" size={18} /> {t('stockCheck.starting')}</> : t('stockCheck.startAuditBtn')}</Button></div></form></Modal>
            <Modal isOpen={isCategoryModalOpen} onClose={() => setIsCategoryModalOpen(false)} title={t("settings.categoryManagement")} maxWidth="max-w-2xl">
                <div className="space-y-6">
                    <form onSubmit={handleAddCategory} className="flex gap-2">
                        <Input name="newCategory" placeholder={t("settings.newCategoryPlaceholder")} required />
                        <Button type="submit" icon={Plus}>{t("settings.addBtn")}</Button>
                    </form>
                    
                    <div className="space-y-2 max-h-[500px] overflow-y-auto">
                        {projectCategories.map((cat) => (
                            <div 
                                key={cat}
                                className="flex items-center justify-between p-3 bg-slate-50 rounded-xl border border-slate-100 hover:bg-white hover:shadow-sm transition-all"
                            >
                                <span className="font-medium text-slate-700">{cat}</span>
                                <button 
                                    type="button"
                                    onClick={() => handleDeleteCategory(cat)} 
                                    className="p-2 text-slate-400 hover:text-rose-500 hover:bg-rose-50 rounded-lg transition-colors"
                                >
                                    <Trash2 size={16}/>
                                </button>
                            </div>
                        ))}
                        {projectCategories.length === 0 && <div className="text-center text-slate-400 py-8 text-sm">{t("settings.noCategoriesYet")}</div>}
                    </div>
                </div>
            </Modal>
            <Modal isOpen={isSupplierModalOpen} onClose={() => setIsSupplierModalOpen(false)} title={editingSupplier ? t("settings.editSupplier") : t("settings.addSupplier")}>
                <form onSubmit={handleAddSupplier} className="space-y-6">
                    <Input label={t("settings.supplierName")} name="name" defaultValue={editingSupplier?.name} required placeholder="E.g.: Global Spices" />
                    
                    <div className="grid grid-cols-2 gap-4">
                        <Input label={language === "en" ? "PIC Name" : "Nama PIC"} name="picName" defaultValue={editingSupplier?.picName} required placeholder={language === "en" ? "E.g.: John" : "Cth: Raj"} />
                        <Input label={language === "en" ? "PIC Phone No." : "No. Telefon PIC"} name="picPhone" defaultValue={editingSupplier?.picPhone} required placeholder={language === "en" ? "E.g.: 011-22331122" : "Cth: 011-22331122"} />
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                        <Input label={language === "en" ? "Email" : "Emel"} name="email" type="email" defaultValue={editingSupplier?.email} required placeholder={language === "en" ? "E.g.: demo@supplier.com" : "Cth: demo@supplier.com"} />
                        <Input label={language === "en" ? "Payment Terms" : "Term Pembayaran"} name="paymentTerms" defaultValue={editingSupplier?.paymentTerms} placeholder={language === "en" ? "E.g.: 30 Days" : "Cth: 30 Hari"} />
                    </div>
                    
                    <div className="space-y-4">
                        <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider">{language === "en" ? "Address" : "Alamat"} *</label>
                        <Input name="addressLine1" defaultValue={editingSupplier?.addressLine1} required placeholder={language === "en" ? "Line 1" : "Baris 1"} />
                        <Input name="addressLine2" defaultValue={editingSupplier?.addressLine2} placeholder={t("settings.line2Optional")} />
                        <div className="grid grid-cols-2 gap-4">
                            <Input name="postcode" defaultValue={editingSupplier?.postcode} placeholder={language === "en" ? "Postcode" : "Poskod"} />
                            <Select name="state" defaultValue={editingSupplier?.state || 'W.P. Kuala Lumpur'}>
                                <option value="Johor">Johor</option>
                                <option value="Kedah">Kedah</option>
                                <option value="Kelantan">Kelantan</option>
                                <option value="Melaka">Melaka</option>
                                <option value="Negeri Sembilan">Negeri Sembilan</option>
                                <option value="Pahang">Pahang</option>
                                <option value="Penang">Penang</option>
                                <option value="Perak">Perak</option>
                                <option value="Perlis">Perlis</option>
                                <option value="Sabah">Sabah</option>
                                <option value="Sarawak">Sarawak</option>
                                <option value="Selangor">Selangor</option>
                                <option value="Terengganu">Terengganu</option>
                                <option value="W.P. Kuala Lumpur">W.P. Kuala Lumpur</option>
                                <option value="W.P. Labuan">W.P. Labuan</option>
                                <option value="W.P. Putrajaya">W.P. Putrajaya</option>
                            </Select>
                        </div>
                    </div>
                    
                    <div className="flex justify-end gap-3 pt-4 border-t border-slate-100">
                        <Button variant="secondary" type="button" onClick={() => setIsSupplierModalOpen(false)}>{t('common.cancel')}</Button>
                        <Button type="submit" disabled={isSubmitting}>
                            {isSubmitting ? (
                                <><Loader2 className="animate-spin" size={18} /> {t('common.processing')}</>
                            ) : (
                                editingSupplier ? t('settings.updateBtn') : t('settings.saveBtn')
                            )}
                        </Button>
                    </div>
                </form>
            </Modal>
            <ProfileModal isOpen={isProfileModalOpen} onClose={() => setIsProfileModalOpen(false)} userProfile={currentAppUser} onSave={handleSaveProfile} />
            {/* AI Insights Modal */}
            <AIInsightsModal
                isOpen={showAIInsightsModal}
                onClose={() => setShowAIInsightsModal(false)}
                title={aiInsightsTitle}
                content={aiInsightsContent}
                projectName={activeProject?.name || t('userMgmt.projectLevel')}
            />
            <UserManagementModal 
                isOpen={isUserModalOpen} 
                onClose={() => { setIsUserModalOpen(false); setEditingUser(null); }} 
                user={editingUser}
                projects={projects}
                allUsers={projectUsers}
                onSave={handleSaveUser}
            />
            {/* Quick Action Item Selector Modal */}
            <Modal 
                isOpen={showQuickActionSelector} 
                onClose={() => {
                    setShowQuickActionSelector(false);
                    setQuickActionSearch('');
                }} 
                title={quickActionType === 'IN' ? t('inventory.selectItemStockIn') : t('inventory.selectItemStockOut')}
                maxWidth="max-w-3xl"
            >
                <div className="space-y-4">
                    {/* Search */}
                    <div className="relative">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
                        <input 
                            className="w-full pl-12 pr-4 py-3 bg-slate-50 rounded-xl outline-none text-sm font-medium text-slate-700 placeholder:text-slate-400 border border-transparent focus:border-emerald-500 focus:bg-white transition-all" 
                            placeholder={t("inventory.searchProductSKUShort")} 
                            value={quickActionSearch}
                            onChange={(e) => setQuickActionSearch(e.target.value)}
                            autoFocus
                        />
                    </div>

                    {/* Item List */}
                    <div className="max-h-[500px] overflow-y-auto space-y-2">
                        {projectInventory
                            .filter(item => 
                                quickActionSearch === '' || 
                                item.name.toLowerCase().includes(quickActionSearch.toLowerCase()) ||
                                item.sku.toLowerCase().includes(quickActionSearch.toLowerCase())
                            )
                            .map(item => (
                                <div 
                                    key={item.id}
                                    onClick={() => {
                                        setSelectedItem(item);
                                        setStockAction(quickActionType);
                                        setShowQuickActionSelector(false);
                                        setQuickActionSearch('');
                                        setIsStockModalOpen(true);
                                    }}
                                    className="flex items-center justify-between p-4 hover:bg-slate-50 rounded-xl transition-colors cursor-pointer border border-transparent hover:border-emerald-200 group"
                                >
                                    <div className="flex items-center gap-4">
                                        <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-slate-100 to-slate-50 flex items-center justify-center overflow-hidden border border-slate-200 group-hover:border-emerald-300 transition-colors">
                                            {item.image ? (
                                                <img src={item.image} className="w-full h-full object-cover" alt={item.name}/>
                                            ) : (
                                                <Package size={24} className="text-slate-400"/>
                                            )}
                                        </div>
                                        <div>
                                            <h4 className="font-bold text-slate-800 group-hover:text-emerald-700 transition-colors">{item.name}</h4>
                                            <div className="flex items-center gap-3 mt-1">
                                                <span className="text-xs text-slate-500 font-medium">{item.sku}</span>
                                                <span className="text-xs text-slate-400">•</span>
                                                <span className={`text-xs font-semibold ${
                                                    item.quantity <= item.minLevel ? 'text-rose-600' : 'text-slate-600'
                                                }`}>
                                                    {item.quantity} {item.unit}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${
                                        quickActionType === 'IN' 
                                            ? 'bg-emerald-50 text-emerald-600 group-hover:bg-emerald-100' 
                                            : 'bg-rose-50 text-rose-600 group-hover:bg-rose-100'
                                    } transition-colors`}>
                                        {quickActionType === 'IN' ? <Plus size={18}/> : <Minus size={18}/>}
                                        <span className="font-bold text-sm">
                                            {quickActionType === 'IN' ? t('inventory.in') : t('inventory.out')}
                                        </span>
                                    </div>
                                </div>
                            ))
                        }
                        
                        {projectInventory.filter(item => 
                            quickActionSearch === '' || 
                            item.name.toLowerCase().includes(quickActionSearch.toLowerCase()) ||
                            item.sku.toLowerCase().includes(quickActionSearch.toLowerCase())
                        ).length === 0 && (
                            <div className="text-center py-12 text-slate-400">
                                <Search size={48} className="mx-auto mb-3 opacity-30" />
                                <p className="font-medium">{t("inventory.noItemFound")}</p>
                                <p className="text-sm mt-1">Cuba cari dengan nama atau SKU yang berbeza</p>
                            </div>
                        )}
                    </div>

                    <div className="flex justify-end pt-4 border-t border-slate-200">
                        <Button 
                            onClick={() => {
                                setShowQuickActionSelector(false);
                                setQuickActionSearch('');
                            }}
                            className="bg-slate-200 text-slate-700 hover:bg-slate-300"
                        >
                            Batal
                        </Button>
                    </div>
                </div>
            </Modal>

            <ConfirmationModal {...confirmModal} onClose={() => setConfirmModal({...confirmModal, isOpen: false})} />
            
            {/* Transaction Detail Modal */}
            {isTransactionDetailOpen && selectedTransaction && (
                <Modal 
                    isOpen={isTransactionDetailOpen} 
                    onClose={() => {
                        setIsTransactionDetailOpen(false);
                        setSelectedTransaction(null);
                    }} 
                    title={t("history.transactionDetails")}
                    maxWidth="max-w-2xl"
                >
                    <div className="space-y-6">
                        {/* Transaction Header */}
                        <div className={`p-4 rounded-xl ${
                            selectedTransaction.transactionType === 'STOK_MASUK' || selectedTransaction.transactionType === 'PO_DITERIMA' 
                                ? 'bg-emerald-50 border-2 border-emerald-200' 
                                : selectedTransaction.transactionType === 'STOK_KELUAR'
                                ? 'bg-rose-50 border-2 border-rose-200'
                                : 'bg-blue-50 border-2 border-blue-200'
                        }`}>
                            <div className="flex items-center justify-between">
                                <div>
                                    <div className={`text-xs font-bold uppercase mb-1 ${
                                        selectedTransaction.transactionType === 'STOK_MASUK' || selectedTransaction.transactionType === 'PO_DITERIMA' 
                                            ? 'text-emerald-700' 
                                            : selectedTransaction.transactionType === 'STOK_KELUAR'
                                            ? 'text-rose-700'
                                            : 'text-blue-700'
                                    }`}>
                                        {selectedTransaction.transactionType === 'STOK_MASUK' ? t("history.stockIn") :
                                         selectedTransaction.transactionType === 'STOK_KELUAR' ? t("history.stockOut") :
                                         selectedTransaction.transactionType === 'PO_DITERIMA' ? t("history.poReceived") :
                                         t("history.auditAdjustment")}
                                    </div>
                                    <div className="text-2xl font-bold text-slate-900">
                                        {selectedTransaction.itemName}
                                    </div>
                                </div>
                                <div className={`text-3xl font-bold ${
                                    selectedTransaction.type === 'IN' ? 'text-emerald-600' : 'text-rose-600'
                                }`}>
                                    {selectedTransaction.type === 'IN' ? '+' : '-'}{selectedTransaction.quantity}
                                </div>
                            </div>
                        </div>

                        {/* Transaction Info Grid */}
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <div className="text-xs font-bold text-slate-400 uppercase tracking-wide mb-1">{t("history.dateTime")}</div>
                                <div className="text-sm font-medium text-slate-700">
                                    {new Date(selectedTransaction.timestamp).toLocaleString('en-MY', {
                                        day: '2-digit',
                                        month: '2-digit',
                                        year: 'numeric',
                                        hour: '2-digit',
                                        minute: '2-digit'
                                    })}
                                </div>
                            </div>
                            <div>
                                <div className="text-xs font-bold text-slate-400 uppercase tracking-wide mb-1">{t("history.recordedBy")}</div>
                                <div className="text-sm font-medium text-slate-700">{selectedTransaction.userName || selectedTransaction.user}</div>
                            </div>
                        </div>

                        {/* Reason/Notes */}
                        <div>
                            <div className="text-xs font-bold text-slate-400 uppercase tracking-wide mb-2">{t("inventory.reason")}</div>
                            <div className="bg-slate-50 p-4 rounded-xl text-sm text-slate-700 font-medium">
                                {selectedTransaction.reason}
                            </div>
                        </div>

                        {/* Additional Info for Stok Masuk/Keluar */}
                        {(selectedTransaction.transactionType === 'STOK_MASUK' || selectedTransaction.transactionType === 'STOK_KELUAR') && (
                            <>
                                {(selectedTransaction.picName || selectedTransaction.picContact) && (
                                    <div className="bg-slate-50 p-4 rounded-xl">
                                        <div className="flex items-center gap-2 mb-3">
                                            <User size={18} className="text-slate-500" />
                                            <div className="text-xs font-bold text-slate-500 uppercase tracking-wide">{t("history.additionalInfoProof")}</div>
                                        </div>
                                        <div className="grid grid-cols-2 gap-4">
                                            {selectedTransaction.picName && (
                                                <div>
                                                    <div className="text-xs font-bold text-slate-400 uppercase tracking-wide mb-1">{t("history.picName")}</div>
                                                    <div className="text-sm font-medium text-slate-700">{selectedTransaction.picName}</div>
                                                </div>
                                            )}
                                            {selectedTransaction.picContact && (
                                                <div>
                                                    <div className="text-xs font-bold text-slate-400 uppercase tracking-wide mb-1">{t("history.contactNo")}</div>
                                                    <div className="text-sm font-medium text-slate-700">{selectedTransaction.picContact}</div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {/* Photo Evidence */}
                                {selectedTransaction.proofImage && (
                                    <div>
                                        <div className="text-xs font-bold text-slate-400 uppercase tracking-wide mb-2">{t("history.proof")}</div>
                                        <div 
                                            className="relative rounded-xl overflow-hidden border-2 border-slate-200 cursor-pointer hover:border-emerald-500 transition-all group"
                                            onClick={() => setViewingImage(selectedTransaction.proofImage)}
                                        >
                                            <img 
                                                src={selectedTransaction.proofImage} 
                                                alt={t("history.proof")}
                                                className="w-full h-64 object-cover"
                                            />
                                            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all flex items-center justify-center">
                                                <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-white/90 backdrop-blur-sm px-4 py-2 rounded-lg">
                                                    <div className="flex items-center gap-2 text-sm font-bold text-slate-700">
                                                        <Eye size={18} />
                                                        {t("inventory.viewFullImage")}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </>
                        )}

                        {/* PO Reference Info */}
                        {selectedTransaction.transactionType === 'PO_DITERIMA' && selectedTransaction.referenceName && (
                            <div className="bg-blue-50 p-4 rounded-xl border border-blue-200">
                                <div className="text-xs font-bold text-blue-600 uppercase tracking-wide mb-2">{t("po.orderItems")}</div>
                                <div className="space-y-1">
                                    <div className="text-sm">
                                        <span className="text-blue-700 font-semibold">{t("history.supplierLabel")}</span>
                                        <span className="text-blue-900 font-bold">{selectedTransaction.referenceName}</span>
                                    </div>
                                    {selectedTransaction.referenceId && (
                                        <div className="text-xs text-blue-600">
                                            ID: {selectedTransaction.referenceId}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Audit Reference Info */}
                        {selectedTransaction.transactionType === 'PELARASAN_AUDIT' && selectedTransaction.referenceName && (
                            <div className="bg-amber-50 p-4 rounded-xl border border-amber-200">
                                <div className="text-xs font-bold text-amber-700 uppercase tracking-wide mb-2">{t("stockCheck.auditStock")}</div>
                                <div className="space-y-1">
                                    <div className="text-sm">
                                        <span className="text-amber-700 font-semibold">{t("history.auditorLabel")} </span>
                                        <span className="text-amber-900 font-bold">{selectedTransaction.referenceName}</span>
                                    </div>
                                    {selectedTransaction.referenceId && (
                                        <div className="text-xs text-amber-600">
                                            {t("history.auditId")} {selectedTransaction.referenceId}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Close Button */}
                        <div className="flex justify-end pt-4 border-t border-slate-200">
                            <Button 
                                onClick={() => {
                                    setIsTransactionDetailOpen(false);
                                    setSelectedTransaction(null);
                                }}
                                className="bg-[#10056E] hover:bg-[#0d0459]"
                            >
                                {t("common.close")}
                            </Button>
                        </div>
                    </div>
                </Modal>
            )}
            
            {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
            
            {/* Bulk Stock Out Confirmation Modal */}
            {showBulkConfirm && bulkConfirmData && (
                <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="bulk-confirm-overlay">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden" data-testid="bulk-confirm-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="bg-gradient-to-br from-rose-500 to-rose-600 px-6 py-5 text-white">
                            <h3 className="text-lg font-bold">{t("bulkConfirm.title")}</h3>
                            <p className="text-rose-100 text-sm mt-1">Sila semak maklumat berikut sebelum meneruskan</p>
                        </div>
                        <div className="px-6 py-4 space-y-4">
                            <div>
                                <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">{t("bulkConfirm.itemList")}</p>
                                <div className="space-y-2 max-h-48 overflow-y-auto">
                                    {bulkStockOutItems.map((item, idx) => (
                                        <div key={idx} className="flex items-center justify-between py-1.5 px-3 rounded-lg bg-slate-50">
                                            <span className="text-sm font-medium text-slate-700">{item.name}</span>
                                            <span className="text-sm font-bold text-rose-600">-{item.qty} {item.unit}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-3 pt-2 border-t border-slate-100">
                                <div>
                                    <p className="text-xs text-slate-400 font-bold">JUMLAH ITEM</p>
                                    <p className="text-lg font-extrabold text-slate-800">{bulkStockOutItems.length}</p>
                                </div>
                                <div>
                                    <p className="text-xs text-slate-400 font-bold">JUMLAH UNIT</p>
                                    <p className="text-lg font-extrabold text-rose-600">{bulkStockOutItems.reduce((s, i) => s + (i.qty || 0), 0)}</p>
                                </div>
                            </div>
                            <div className="pt-2 border-t border-slate-100">
                                <p className="text-xs text-slate-400 font-bold">{language === "en" ? "REASON / REFERENCE" : "SEBAB / RUJUKAN"}</p>
                                <p className="text-sm font-medium text-slate-700 mt-0.5">{bulkConfirmData.reason}</p>
                            </div>
                            {bulkConfirmData.picName && (
                                <div>
                                    <p className="text-xs text-slate-400 font-bold">PIC</p>
                                    <p className="text-sm font-medium text-slate-700 mt-0.5">{bulkConfirmData.picName} {bulkConfirmData.picContact && `(${bulkConfirmData.picContact})`}</p>
                                </div>
                            )}
                        </div>
                        <div className="flex gap-3 px-6 pb-5">
                            <button
                                onClick={() => { setShowBulkConfirm(false); setBulkConfirmData(null); }}
                                className="flex-1 px-4 py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-sm font-bold transition-colors"
                                data-testid="bulk-confirm-cancel-btn"
                            >
                                Batal
                            </button>
                            <button
                                onClick={executeBulkStockOut}
                                className="flex-1 px-4 py-3 bg-rose-500 hover:bg-rose-600 text-white rounded-xl text-sm font-bold transition-colors shadow-lg shadow-rose-500/20"
                                data-testid="bulk-confirm-submit-btn"
                            >
                                {t("common.yesConfirm")}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Image Viewer Modal */}
            {viewingImage && (
                <div 
                    className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
                    onClick={() => setViewingImage(null)}
                >
                    <button 
                        onClick={() => setViewingImage(null)}
                        className="absolute top-4 right-4 p-2 bg-white/10 hover:bg-white/20 rounded-xl text-white transition-colors"
                    >
                        <X size={24} />
                    </button>
                    <img 
                        src={viewingImage} 
                        alt="Full size" 
                        className="max-w-full max-h-full rounded-2xl shadow-2xl"
                        onClick={(e) => e.stopPropagation()}
                    />
                </div>
            )}
            
            {/* Audit Report Modal */}
            {auditReportModal.isOpen && auditReportModal.audit && (
                <Modal 
                    isOpen={auditReportModal.isOpen} 
                    onClose={() => setAuditReportModal({ isOpen: false, audit: null })} 
                    title={t("stockCheck.auditStockReport")}
                    maxWidth="max-w-4xl"
                >
                    <div className="space-y-6">
                        {/* Audit Header Info */}
                        <div className="grid grid-cols-2 gap-4 p-6 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl border border-blue-100">
                            <div>
                                <p className="text-xs text-blue-600 font-semibold uppercase tracking-wide mb-1">{t("stockCheck.auditNo")}</p>
                                <p className="text-lg font-bold text-blue-900">AUDIT-{auditReportModal.audit.id.slice(-6).toUpperCase()}</p>
                            </div>
                            <div>
                                <p className="text-xs text-blue-600 font-semibold uppercase tracking-wide mb-1">{t("stockCheck.auditor")}</p>
                                <p className="text-lg font-bold text-blue-900">{auditReportModal.audit.auditorName}</p>
                            </div>
                            <div>
                                <p className="text-xs text-blue-600 font-semibold uppercase tracking-wide mb-1">{t("reports.dateLabel")}</p>
                                <p className="text-sm font-medium text-blue-800">
                                    {new Date(auditReportModal.audit.startDate).toLocaleDateString('en-MY', { 
                                        day: '2-digit', 
                                        month: 'long', 
                                        year: 'numeric' 
                                    })}
                                </p>
                            </div>
                            <div>
                                <p className="text-xs text-blue-600 font-semibold uppercase tracking-wide mb-1">Status</p>
                                <Badge color={auditReportModal.audit.status === 'Completed' ? 'green' : 'blue'}>
                                    {auditReportModal.audit.status === 'Completed' ? t("stockCheck.completed") : t("stockCheck.inProgress")}
                                </Badge>
                            </div>
                        </div>
                        
                        {/* Compact Summary Stats & Export Buttons */}
                        <div className="flex items-center justify-between gap-4 p-4 bg-slate-50 rounded-xl border border-slate-200">
                            <div className="flex items-center gap-6">
                                <div className="text-center">
                                    <p className="text-xs text-slate-500 font-medium mb-1">{t("po.totalLabel")}</p>
                                    <p className="text-2xl font-bold text-slate-800">{auditReportModal.audit.items.length}</p>
                                </div>
                                <div className="w-px h-8 bg-slate-300"></div>
                                <div className="text-center">
                                    <p className="text-xs text-emerald-600 font-medium mb-1">{t("stockCheck.match")}</p>
                                    <p className="text-2xl font-bold text-emerald-700">
                                        {auditReportModal.audit.items.filter(i => (i.actualQty || 0) === i.systemQty).length}
                                    </p>
                                </div>
                                <div className="w-px h-8 bg-slate-300"></div>
                                <div className="text-center">
                                    <p className="text-xs text-rose-600 font-medium mb-1">{t("stockCheck.variance")}</p>
                                    <p className="text-2xl font-bold text-rose-700">
                                        {auditReportModal.audit.items.filter(i => i.confirmed && (i.actualQty || 0) !== i.systemQty).length}
                                    </p>
                                </div>
                            </div>
                            
                            {/* Export Buttons */}
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => exportAuditToExcel(auditReportModal.audit, activeProject, t, language)}
                                    className="px-4 py-2 border-2 border-[#10056E] text-[#10056E] hover:bg-[#10056E]/5 rounded-lg text-sm font-semibold transition-all flex items-center gap-2"
                                >
                                    <FileSpreadsheet size={16} />
                                    Excel
                                </button>
                                <button
                                    onClick={() => exportAuditToPDF(auditReportModal.audit, activeProject, t, language)}
                                    className="px-4 py-2 border-2 border-slate-400 text-slate-600 hover:bg-slate-50 rounded-lg text-sm font-semibold transition-all flex items-center gap-2"
                                >
                                    <Printer size={16} />
                                    PDF
                                </button>
                            </div>
                        </div>
                        
                        {/* Items Table */}
                        <div className="border border-slate-200 rounded-2xl overflow-hidden">
                            <div className="overflow-x-auto max-h-96">
                                <table className="w-full text-sm">
                                    <thead className="bg-slate-50 sticky top-0">
                                        <tr>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">{t("inventory.product")}</th>
                                            <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 uppercase tracking-wider">{t("stockCheck.system")}</th>
                                            <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 uppercase tracking-wider">{t("stockCheck.physical")}</th>
                                            <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 uppercase tracking-wider">{t("stockCheck.variance")}</th>
                                            <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 uppercase tracking-wider">Status</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100">
                                        {auditReportModal.audit.items.map((item, idx) => {
                                            const diff = (item.actualQty || 0) - item.systemQty;
                                            const isMatch = diff === 0;
                                            return (
                                                <tr key={idx} className={isMatch ? 'bg-emerald-50/50' : 'bg-amber-50'}>
                                                    <td className="px-4 py-3">
                                                        <div className="font-semibold text-slate-800">{item.name}</div>
                                                        <div className="text-xs text-slate-500">SKU: {item.sku}</div>
                                                    </td>
                                                    <td className="px-4 py-3 text-center font-medium text-slate-700">
                                                        {item.systemQty} {item.unit}
                                                    </td>
                                                    <td className="px-4 py-3 text-center font-medium text-slate-700">
                                                        {item.actualQty || 0} {item.unit}
                                                    </td>
                                                    <td className={`px-4 py-3 text-center font-bold ${
                                                        diff > 0 ? 'text-blue-600' : diff < 0 ? 'text-amber-600' : 'text-emerald-600'
                                                    }`}>
                                                        {diff > 0 ? '+' : ''}{diff}
                                                    </td>
                                                    <td className="px-4 py-3 text-center">
                                                        <Badge color={isMatch ? 'green' : diff > 0 ? 'blue' : 'amber'}>
                                                            {isMatch ? t('stockCheck.match') : diff > 0 ? t('stockCheck.excess') : t('stockCheck.shortage')}
                                                        </Badge>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                        
                        {/* Nota */}
                        {auditReportModal.audit.notes && (
                            <Card className="bg-blue-50 border-blue-200">
                                <p className="text-xs text-blue-600 font-semibold uppercase tracking-wide mb-2">{t("reports.auditNotes")}</p>
                                <p className="text-sm text-blue-900">{auditReportModal.audit.notes}</p>
                            </Card>
                        )}
                        
                        {/* Close Button */}
                        <div className="pt-4 border-t border-slate-200">
                            <Button 
                                variant="secondary" 
                                onClick={() => setAuditReportModal({ isOpen: false, audit: null })}
                                fullWidth
                            >
                                {t("common.close")}
                            </Button>
                        </div>
                    </div>
                </Modal>
            )}
            
            {/* Audit Finalize Confirmation Modal */}
            {auditConfirmModal.isOpen && auditConfirmModal.summary && (
                <Modal
                    isOpen={auditConfirmModal.isOpen}
                    onClose={() => setAuditConfirmModal({ isOpen: false, check: null, summary: null })}
                    title={t('stockCheck.confirmEndAudit')}
                    maxWidth="max-w-3xl"
                >
                    <div className="space-y-6">
                        {/* Warning Banner */}
                        <div className="p-4 bg-amber-50 border-l-4 border-amber-500 rounded-lg">
                            <div className="flex items-start gap-3">
                                <AlertTriangle className="text-amber-600 mt-0.5" size={20} />
                                <div>
                                    <h4 className="font-bold text-amber-900 mb-1">{t("stockCheck.adjustmentWarning")}</h4>
                                    <p className="text-sm text-amber-800 mb-2">
                                        {t("stockCheck.adjustmentDescription")}
                                    </p>
                                    <p className="text-sm text-amber-800">
                                        {t("stockCheck.endAuditWarning")}
                                    </p>
                                </div>
                            </div>
                        </div>
                        
                        {/* Summary Stats */}
                        <div className="grid grid-cols-3 gap-4">
                            <Card className="text-center bg-slate-50">
                                <p className="text-xs text-slate-500 font-medium mb-1">{t("inventory.totalItems")}</p>
                                <p className="text-2xl font-bold text-slate-800">{auditConfirmModal.summary.totalItems}</p>
                            </Card>
                            <Card className="text-center bg-emerald-50 border-emerald-200">
                                <p className="text-xs text-emerald-600 font-medium mb-1">{t("stockCheck.match")}</p>
                                <p className="text-2xl font-bold text-emerald-700">{auditConfirmModal.summary.matched}</p>
                            </Card>
                            <Card className="text-center bg-amber-50 border-amber-200">
                                <p className="text-xs text-amber-600 font-medium mb-1">{t("stockCheck.needsAdjustment")}</p>
                                <p className="text-2xl font-bold text-amber-700">{auditConfirmModal.summary.toAdjust}</p>
                            </Card>
                        </div>
                        
                        {/* Items to be adjusted */}
                        {auditConfirmModal.summary.toAdjust > 0 && (
                            <div>
                                <h4 className="font-bold text-slate-800 mb-3 flex items-center gap-2">
                                    <Package size={18} />
                                    {t("stockCheck.itemsToAdjust")} ({auditConfirmModal.summary.toAdjust})
                                </h4>
                                <div className="border border-slate-200 rounded-xl overflow-hidden">
                                    <div className="max-h-64 overflow-y-auto">
                                        <table className="w-full text-sm">
                                            <thead className="bg-slate-50 sticky top-0">
                                                <tr>
                                                    <th className="px-4 py-2 text-left text-xs font-semibold text-slate-600">{t("inventory.product")}</th>
                                                    <th className="px-4 py-2 text-center text-xs font-semibold text-slate-600">{t("stockCheck.system")}</th>
                                                    <th className="px-4 py-2 text-center text-xs font-semibold text-slate-600">{t("stockCheck.physical")}</th>
                                                    <th className="px-4 py-2 text-center text-xs font-semibold text-slate-600">{t("stockCheck.adjustment")}</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-slate-100">
                                                {auditConfirmModal.summary.adjustments.map((adj, idx) => (
                                                    <tr key={idx} className="hover:bg-slate-50">
                                                        <td className="px-4 py-3">
                                                            <div className="font-semibold text-slate-800">{adj.name}</div>
                                                            <div className="text-xs text-slate-500">SKU: {adj.sku}</div>
                                                        </td>
                                                        <td className="px-4 py-3 text-center text-slate-700">{adj.systemQty} {adj.unit}</td>
                                                        <td className="px-4 py-3 text-center font-semibold text-slate-800">{adj.actualQty} {adj.unit}</td>
                                                        <td className={`px-4 py-3 text-center font-bold ${
                                                            adj.diff > 0 ? 'text-blue-600' : 'text-amber-600'
                                                        }`}>
                                                            {adj.diff > 0 ? '+' : ''}{adj.diff} {adj.unit}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        )}
                        
                        {auditConfirmModal.summary.toAdjust === 0 && (
                            <div className="p-6 bg-emerald-50 rounded-xl border border-emerald-200 text-center">
                                <CheckCircle className="mx-auto mb-2 text-emerald-600" size={32} />
                                <p className="font-semibold text-emerald-900">{t("stockCheck.allItemsMatch")}</p>
                                <p className="text-sm text-emerald-700 mt-1">{t("stockCheck.noAdjustmentNeeded")}</p>
                            </div>
                        )}
                        
                        {/* Action Buttons */}
                        <div className="flex gap-3 pt-4 border-t border-slate-200">
                            <Button
                                variant="secondary"
                                onClick={() => setAuditConfirmModal({ isOpen: false, check: null, summary: null })}
                                fullWidth
                                disabled={isFinalizingAudit}
                            >
                                {t("common.cancel")}
                            </Button>
                            <Button
                                onClick={confirmFinalizeAudit}
                                className="bg-[#10056E] hover:bg-[#0d0459] text-white"
                                icon={isFinalizingAudit ? Loader2 : CheckCircle}
                                fullWidth
                                disabled={isFinalizingAudit}
                            >
                                {isFinalizingAudit ? t('common.processing') : t('stockCheck.confirmEndAuditBtn')}
                            </Button>
                        </div>
                    </div>
                </Modal>
            )}

            {/* Price Log Modal */}
            {showPriceLogModal && selectedItemForPriceLog && (
                <Modal 
                    isOpen={showPriceLogModal} 
                    onClose={() => {
                        setShowPriceLogModal(false);
                        setSelectedItemForPriceLog(null);
                    }} 
                    title={`${t('priceLog.title')} - ${selectedItemForPriceLog.name}`}
                >
                    <div className="space-y-4">
                        {/* Item Details */}
                        <div className="p-4 bg-slate-50 rounded-xl">
                            <div className="flex items-center gap-3">
                                {selectedItemForPriceLog.image ? (
                                    <img src={selectedItemForPriceLog.image} className="w-16 h-16 rounded-lg object-cover border border-slate-200" alt=""/>
                                ) : (
                                    <div className="w-16 h-16 rounded-lg bg-slate-200 flex items-center justify-center">
                                        <Package size={24} className="text-slate-400"/>
                                    </div>
                                )}
                                <div>
                                    <h3 className="font-bold text-slate-800 text-lg">{selectedItemForPriceLog.name}</h3>
                                    <p className="text-sm text-slate-500">SKU: {selectedItemForPriceLog.sku}</p>
                                    <p className="text-sm font-bold text-emerald-600 mt-1">
                                        {t('priceLog.currentPrice')} {formatNumber(selectedItemForPriceLog.price, 2)}
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Price History */}
                        <div>
                            <h4 className="text-sm font-bold text-slate-600 mb-3 uppercase">{t('priceLog.priceChangeHistory')}</h4>
                            {(() => {
                                const itemLogs = priceLogs.filter(log => log.itemId === selectedItemForPriceLog.id);
                                
                                if (itemLogs.length === 0) {
                                    return (
                                        <div className="text-center py-10 text-slate-400">
                                            <DollarSign size={32} className="mx-auto mb-2 opacity-30" />
                                            <p className="text-sm">{t('priceLog.noPriceChanges')}</p>
                                        </div>
                                    );
                                }
                                
                                return (
                                    <div className="space-y-2 max-h-96 overflow-y-auto">
                                        {itemLogs.map((log, idx) => {
                                            const meta = log.metadata;
                                            const priceChange = meta ? (meta.newPrice - meta.oldPrice) : 0;
                                            const isIncrease = priceChange > 0;
                                            const changePercent = meta && meta.oldPrice > 0 
                                                ? ((priceChange / meta.oldPrice) * 100).toFixed(1)
                                                : '0.0';
                                            
                                            return (
                                                <div key={log.id || idx} className="p-2.5 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors">
                                                    <div className="flex items-center justify-between gap-3">
                                                        {/* Price Change */}
                                                        <div className="flex items-center gap-2 flex-1">
                                                            <div className={`w-6 h-6 rounded-md flex items-center justify-center text-white flex-shrink-0 ${isIncrease ? 'bg-emerald-500' : 'bg-rose-500'}`}>
                                                                {isIncrease ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
                                                            </div>
                                                            <div className="flex items-center gap-1.5 flex-1 min-w-0">
                                                                <span className="text-xs text-slate-400 line-through">
                                                                    RM {formatNumber(meta?.oldPrice || 0, 2)}
                                                                </span>
                                                                <span className="text-slate-300">→</span>
                                                                <span className={`text-sm font-bold ${isIncrease ? 'text-emerald-600' : 'text-rose-600'}`}>
                                                                    RM {formatNumber(meta?.newPrice || 0, 2)}
                                                                </span>
                                                                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${
                                                                    isIncrease ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'
                                                                }`}>
                                                                    {isIncrease ? '+' : ''}{changePercent}%
                                                                </span>
                                                            </div>
                                                        </div>
                                                        
                                                        {/* Date */}
                                                        <span className="text-[10px] text-slate-400 whitespace-nowrap">
                                                            {new Date(log.timestamp).toLocaleDateString('en-MY', { 
                                                                day: 'numeric', 
                                                                month: 'short',
                                                                hour: '2-digit',
                                                                minute: '2-digit'
                                                            })}
                                                        </span>
                                                    </div>
                                                    
                                                    {/* Source & User */}
                                                    <div className="mt-1.5 flex items-center gap-2 text-[10px]">
                                                        <span className={`px-1.5 py-0.5 rounded ${meta?.changeType === 'SKU_EDIT' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}`}>
                                                            {meta?.changeType === 'SKU_EDIT' ? 'SKU' : 'PO'}
                                                        </span>
                                                        <span className="text-slate-500">{log.userName || meta?.changedByName}</span>
                                                        {meta?.changeType !== 'SKU_EDIT' && meta?.poSupplier && (
                                                            <span className="text-slate-400">• {meta.poSupplier}</span>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                );
                            })()}
                        </div>
                    </div>
                </Modal>
            )}

            {/* Delete Project Password Modal */}
            {isDeleteProjectModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
                    <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full">
                        <div className="p-8">
                            <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 rounded-full bg-rose-100">
                                <AlertCircle size={32} className="text-rose-600" />
                            </div>
                            <h2 className="text-2xl font-bold text-center text-slate-900 mb-2">{t("deleteProjectModal.title")}</h2>
                            <p className="text-center text-slate-600 mb-6">
                                This action is <span className="font-bold text-rose-600">CRITICAL</span> and cannot be undone. 
                                {t('deleteProjectModal.warningMsg')}
                            </p>
                            
                            <div className="bg-rose-50 border border-rose-200 rounded-xl p-4 mb-6">
                                <div className="flex items-start gap-2">
                                    <AlertCircle size={20} className="text-rose-600 flex-shrink-0 mt-0.5" />
                                    <div className="text-sm text-rose-700">
                                        <p className="font-bold mb-1">Data that will be deleted:</p>
                                        <ul className="list-disc list-inside space-y-0.5 text-rose-600">
                                            <li>{t('deleteProjectModal.allProducts')}</li>
                                            <li>{t("deleteProjectModal.purchaseOrders")}</li>
                                            <li>{t("deleteProjectModal.transactionHistory")}</li>
                                            <li>Audit reports</li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                            
                            <div className="mb-6">
                                <label className="block text-sm font-bold text-slate-700 mb-2">
                                    {t("deleteProjectModal.confirmWithPassword")}
                                </label>
                                <input
                                    type="password"
                                    value={deleteProjectPassword}
                                    onChange={(e) => setDeleteProjectPassword(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' && deleteProjectPassword) {
                                            handleDeleteProject();
                                        }
                                    }}
                                    placeholder="Your password"
                                    className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:border-rose-500 focus:ring-2 focus:ring-rose-200 outline-none transition-all"
                                    autoFocus
                                />
                                {deleteProjectError && (
                                    <p className="text-rose-600 text-sm mt-2 flex items-center gap-1">
                                        <AlertCircle size={14} />
                                        {deleteProjectError}
                                    </p>
                                )}
                            </div>
                            
                            <div className="flex gap-3">
                                <button
                                    onClick={() => {
                                        setIsDeleteProjectModalOpen(false);
                                        setDeleteProjectPassword('');
                                        setDeleteProjectError('');
                                        setIsDeletingProject(false);
                                    }}
                                    disabled={isDeletingProject}
                                    className="flex-1 px-4 py-3 rounded-xl border border-slate-200 text-slate-700 font-semibold hover:bg-slate-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleDeleteProject}
                                    disabled={!deleteProjectPassword || isDeletingProject}
                                    className="flex-1 px-4 py-3 rounded-xl bg-rose-600 text-white font-semibold hover:bg-rose-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                >
                                    {isDeletingProject ? (
                                        <>
                                            <Loader2 size={18} className="animate-spin" />
                                            Deleting...
                                        </>
                                    ) : (
                                        <>
                                            <Trash2 size={18} />
                                            {t("deleteProjectModal.deleteProjectBtn")}
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
